import type {
  AlertKey,
  CancellationBlock,
  CancellationFeeRule,
  CargosProblemCode,
  CloseBlock,
  CounterPaymentMethod,
  DepositMethod,
  DepositState,
  DocumentKind,
  DocumentSide,
  EligibilityReasonCode,
  EligibilityStatus,
  FiscalDocumentKind,
  FiscalSource,
  FuelType,
  KnownDisputeStatus,
  Locale,
  MaintenanceKind,
  MediaStatus,
  PaperFieldKey,
  PaperSheet,
  PaymentMethod,
  QuoteLineKind,
  ReentryRevenueDay,
  RefundMethod,
  RefundState,
  RefusalCode,
  RentalChargeKind,
  ReservationSource,
  ReservationStatus,
  StripeDifferenceKind,
  Transmission,
  VehicleDocumentKind,
  VehicleState,
} from '@rental/domain'
import type { StaffErrorCode } from '../errors'

/**
 * The staff app's copy (Italian source). Staff read it at the counter and on
 * the road, so it is short, third-person and never addresses the customer.
 * Public error codes fall back to the booking flow's wording (`staffProblem`).
 */
export const it = {
  meta: {
    language: 'Italiano',
    htmlLang: 'it',
    title: 'Mazzella Rent · Staff',
  },

  app: {
    name: 'Mazzella Rent',
    product: 'Staff',
    skipToContent: 'Vai al contenuto',
    loading: 'Caricamento…',
    retry: 'Riprova',
    notFound: 'Questa pagina non esiste.',
    home: 'Torna a Oggi',
    technicalDetails: 'Dettagli tecnici',
    notActiveYet: 'Non ancora attivo',
    stripeCode: 'Codice di Stripe: {code}',
  },

  /** UI-2b: what every list shares (Prenotazioni, Clienti, Veicoli). */
  lists: {
    pages: 'Pagine dei risultati',
    range: '{first}–{last}',
    change: 'Cambia la ricerca, o azzerala.',
    pastEnd: 'Nessun risultato su questa pagina.',
  },

  nav: {
    label: 'Menu principale',
    today: 'Oggi',
    bookings: 'Prenotazioni',
    devices: 'Dispositivi',
    // S9 (ADM-1): the staff accounts, and one's own password.
    people: 'Persone',
    lock: 'Blocca',
    changePin: 'Cambia PIN',
    changePassword: 'Cambia password',
    signedInAs: '{name}, {role}',
    otherLanguage: 'English',
    otherLanguageLabel: 'Use the app in English',
    newBooking: 'Nuova prenotazione',
    timeline: 'Planning',
    prices: 'Prezzi',
    // S10: the printed fallback kit, for the counter's paper stock.
    kit: 'Kit di emergenza',
    vehicles: 'Veicoli',
    customers: 'Clienti',
    maintenance: 'Manutenzione',
    // Q-157: the models of each category (models.manage).
    models: 'Categorie e modelli',
    cash: 'Cassa',
    fiscal: 'Fiscale',
    fines: 'Multe',
    reports: 'Report',
    reconcile: 'Riconciliazione',
    system: 'Sistema',
    terms: 'Regole del contratto',
    groupDesk: 'Banco',
    groupFleet: 'Flotta e clienti',
    groupMoney: 'Soldi',
    groupAdmin: 'Gestione',
    menu: 'Menu',
    closeMenu: 'Chiudi il menu',
    search: 'Trova una prenotazione',
    searchSubmit: 'Trova',
    device: 'Dispositivo: {name}',
    // UI-2f: the shell's word for it, the lock screen's too.
    offline: 'Senza connessione',
    /** UI-2g: said for a few seconds when the connection comes back. */
    backOnline: 'Connessione tornata.',
  },

  roles: {
    owner: 'Titolare',
    agent: 'Banco',
    runner: 'Runner',
  },

  enroll: {
    title: 'Registra questo dispositivo',
    intro:
      'Si accede una volta con e-mail e password: il dispositivo resta registrato e da lì in poi si sblocca con il PIN personale.',
    revoked:
      'Questo dispositivo è stato disconnesso. Per usarlo di nuovo va registrato con e-mail e password.',
    email: 'E-mail',
    password: 'Password',
    deviceName: 'Nome del dispositivo',
    deviceNameHint:
      'Come comparirà nell’elenco dei dispositivi, per esempio «iPad banco aliscafi».',
    totp: 'Codice dell’app di autenticazione',
    totpHint: 'Le 6 cifre che l’app mostra in questo momento.',
    submit: 'Registra e accedi',
  },

  signIn: {
    title: 'Accedi con la password',
    intro: 'Per chi non ha ancora un PIN, o lo ha bloccato su questo dispositivo.',
    submit: 'Accedi',
    backToLock: 'Torna al blocco schermo',
  },

  lock: {
    title: 'Schermo bloccato',
    device: 'Dispositivo: {name}',
    pickUser: 'Scegli il tuo nome, poi inserisci il PIN.',
    noPin: 'Senza PIN',
    lockedHere: 'Bloccato qui',
    pinFor: 'PIN di {name}',
    pinLabel: 'PIN, 6 cifre',
    unlock: 'Sblocca',
    /** UI-2g: Sblocca pressed before the sixth digit, which unlocks by itself. */
    needSix: 'Scrivi le 6 cifre del PIN.',
    /** UI-2a: in place of a person's PIN, while nobody is chosen; the keypad's reason too. */
    chooseFirst: 'Scegli il tuo nome',
    /** UI-2a: the three who last unlocked this iPad are on its lock screen; everyone, from here. */
    everyone: 'Altre persone',
    back: 'Indietro',
    usePassword: 'Accedi con la password',
    wrongPin: {
      one: 'PIN errato: resta {count} tentativo su questo dispositivo.',
      other: 'PIN errato: restano {count} tentativi su questo dispositivo.',
    },
    autoLocked: 'Schermo bloccato dopo {minutes} minuti senza attività.',
    handback: 'Il dispositivo è tornato a te: sblocca con il tuo PIN per continuare.',
    expired: 'La sessione è terminata: sblocca con il tuo PIN per continuare.',
    keypad: 'Tastierino del PIN',
    deleteDigit: 'Cancella l’ultima cifra',
    /** UI-1e: the dots are drawn, not read: how many digits are in, said as it changes. */
    digits: { one: '{count} cifra su 6', other: '{count} cifre su 6' },
  },

  /** UI-1d (C7): the customer's screen when the contract does not open; the staff's words. */
  signingScreen: {
    unavailable: 'Il contratto non si apre',
    takeBack: 'Riprendi il dispositivo',
  },

  pin: {
    title: 'Scegli il tuo PIN',
    intro:
      'Sei cifre, solo tue: sbloccano ogni dispositivo registrato. Niente sequenze né ripetizioni.',
    pin: 'Nuovo PIN',
    confirm: 'Ripeti il PIN',
    mismatch: 'I due PIN non coincidono.',
    format: 'Il PIN è di 6 cifre.',
    submit: 'Salva il PIN',
    saved: 'PIN salvato.',
    later: 'Più tardi',
    needsPassword:
      'Il PIN si sceglie entro 10 minuti da un accesso con la password: blocca lo schermo e accedi con la password.',
  },

  /** S9 (Q-51): a password of one's own, at the first sign-in and whenever wanted. */
  password: {
    title: 'Cambia password',
    intro:
      'La password serve per registrare un dispositivo e per entrare senza PIN: almeno 12 caratteri, e non la tua e-mail.',
    forcedTitle: 'Scegli la tua password',
    forcedIntro:
      'La password che ti è stata consegnata vale solo per il primo accesso: per continuare scegline una tua, di almeno 12 caratteri, che non sia la tua e-mail.',
    current: 'Password attuale',
    received: 'Password consegnata',
    next: 'Nuova password',
    nextHint: 'Una frase di più parole si ricorda bene ed è difficile da indovinare.',
    confirm: 'Ripeti la nuova password',
    missingCurrent: 'Scrivi la password attuale.',
    wrongCurrent: 'Questa password non è quella giusta: riscrivila.',
    short: 'La nuova password ha almeno 12 caratteri.',
    mismatch: 'Le due password non coincidono.',
    submit: 'Salva la password',
    done: 'Password cambiata: dalla prossima volta si entra con quella nuova.',
  },

  /** A1: the API no longer accepts this version of the app (426). */
  update: {
    title: 'Aggiornamento necessario',
    app: 'Questa versione dell’app non è più accettata. Aggiornala dall’App Store per continuare.',
    web: 'È disponibile una nuova versione. Ricarica la pagina per continuare.',
    reload: 'Ricarica la pagina',
  },

  today: {
    title: 'Oggi',
    day: 'Giorno',
    previousDay: 'Giorno prima',
    nextDay: 'Giorno dopo',
    backToToday: 'Torna a oggi',
    location: 'Sede',
    allLocations: 'Tutte le sedi',
    locationHint: 'Le consegne si vedono sempre.',
    pickups: 'Ritiri',
    returns: 'Riconsegne',
    overdueReturns: 'Riconsegne in ritardo',
    missedPickups: 'Ritiri non avvenuti',
    overbooked: 'Confermate senza un’auto per loro',
    expiringDocuments: 'Documenti dei veicoli in scadenza',
    depositAlerts: 'Depositi in scadenza',
    moneyToReturn: 'Da restituire',
    cargos: 'CaRGOS da sistemare',
    fines: 'Multe in scadenza',
    cargosStatus: {
      refused: 'Comunicazione rifiutata',
      unknown: 'Esito sconosciuto',
      queued: 'Comunicazione ferma',
    },
    moneyToReturnAmount: 'Da restituire {amount}',
    refundFailed: 'Il rimborso con Stripe non è riuscito',
    refundAction: 'Stripe chiede un’azione sul rimborso',
    refundDisputed: 'Contestato: decide la banca del cliente',
    depositLapses: 'La preautorizzazione scade il {date}',
    depositLapsed: 'La preautorizzazione è scaduta',
    nothing: 'Niente in programma.',
    noDocuments: 'Nessun documento in scadenza nei prossimi 30 giorni.',
    late: 'In ritardo',
    unassigned: 'Veicolo da assegnare',
    checkAtCounter: 'Verifica al banco',
    ferry: 'Nave {name}',
    ferryArrival: 'arrivo {time}',
    expiresIn: {
      one: 'scade tra {count} giorno',
      other: 'scade tra {count} giorni',
    },
    expiresToday: 'scade oggi',
    expiredAgo: {
      one: 'scaduto da {count} giorno',
      other: 'scaduto da {count} giorni',
    },
    refresh: 'Aggiorna',
    updatedAt: 'Aggiornato alle {time}',
    attention: 'Da controllare',
    attentionClear: 'Niente da controllare.',
    dayLine: {
      title: 'La giornata',
      summary: 'Dalle {from} alle {to}: {items}.',
      nowAt: 'Adesso sono le {time}.',
      pickups: { one: '{count} ritiro', other: '{count} ritiri' },
      returns: { one: '{count} riconsegna', other: '{count} riconsegne' },
      ferries: {
        one: '{count} arrivo in nave',
        other: '{count} arrivi in nave',
      },
      legendPickups: 'Ritiri',
      legendReturns: 'Riconsegne',
      legendFerries: 'Arrivi in nave',
      legendNow: 'Adesso',
    },
  },

  /**
   * S9 (LOC-5, J8): the trips to and from the delivery zones, on Today and on
   * a booking's page. "Viaggio", never "ritiro" alone: on Today the Ritiri
   * are the customers' pick-ups. Each trip is named as its price line names it.
   */
  trips: {
    title: 'Viaggi',
    toAssign: 'Viaggi da assegnare',
    none: 'Nessun viaggio.',
    state: {
      to_assign: 'Da assegnare',
      assigned: 'Affidato a {name}',
      done: 'Fatto',
    },
    mine: 'Tuo',
    meetingPoint: 'Ritrovo: {place}',
    note: 'Nota: {note}',
    assign: 'Assegna',
    edit: 'Modifica',
    form: {
      about: 'Prenotazione {code}, {customer}: {when}.',
      driver: 'Chi lo fa',
      nobody: 'Nessuno',
      driverHint: 'Tra le persone attive che consegnano un veicolo.',
      meetingPoint: 'Punto di ritrovo',
      meetingPointHint: 'Dove si incontra il cliente: una banchina, l’ingresso di un hotel.',
      note: 'Nota',
      ferryName: 'Nave del cliente',
      ferryNameHint: 'Solo se arriva con una nave diversa da quella della prenotazione.',
      bookingFerry: 'Sulla prenotazione: {ferry}.',
      ferryTime: 'Arrivo della nave',
      ferryTimeHint: 'L’ora, nel giorno del viaggio.',
      ferryTimeInvalid: 'Scrivi un’ora valida, per esempio 09:30.',
      save: 'Salva',
      cancel: 'Annulla',
      saved: 'Viaggio salvato.',
    },
  },

  bookings: {
    title: 'Prenotazioni',
    search: 'Cerca',
    searchHint: 'Codice, nome, e-mail, telefono, targa o riferimento del vecchio sito.',
    status: 'Stato',
    anyStatus: 'Tutti gli stati',
    from: 'Ritiro dal',
    to: 'Ritiro fino al',
    submit: 'Cerca',
    clear: 'Azzera',
    upcoming: 'Dai ritiri di oggi in poi, i più vicini prima.',
    found: {
      one: '{count} prenotazione',
      other: '{count} prenotazioni',
    },
    range: '{first}–{last} di {total}',
    previous: 'Precedenti',
    next: 'Successive',
    none: 'Nessuna prenotazione trovata.',
    noneUpcoming: 'Nessun ritiro da oggi in poi.',
    fills: 'Le prenotazioni del sito e del banco compaiono qui.',
  },

  booking: {
    title: 'Prenotazione {code}',
    back: 'Prenotazioni',
    notFound: 'Nessuna prenotazione con questo codice.',
    summary: 'Riepilogo',
    pickup: 'Ritiro',
    return: 'Riconsegna',
    category: 'Categoria',
    vehicle: 'Veicolo',
    source: 'Canale',
    // S9 (ADM-4): a booking carried over from the old site, by the reference its customer knows.
    oldSite: 'Vecchio sito {reference}',
    oldSiteNote:
      'Prenotazione del vecchio sito: al cliente non parte nessuna e-mail automatica, scrivigli tu citando il riferimento {reference}. Valgono le condizioni del vecchio sito: una penale di annullamento si scrive a mano.',
    rentalDays: {
      one: '{count} giorno di noleggio',
      other: '{count} giorni di noleggio',
    },
    total: 'Totale',
    // S9: a manual price's lines add up to the list price it replaced.
    listPrice: 'Listino',
    customer: 'Cliente',
    name: 'Nome',
    email: 'E-mail',
    phone: 'Telefono',
    nationality: 'Nazionalità',
    birthDate: 'Data di nascita',
    notGiven: 'Non indicato',
    driver: 'Conducente',
    toCheck: 'Da controllare al banco:',
    documents: 'Documenti',
    price: 'Prezzo',
    quoteUnreadable: 'Il dettaglio del prezzo non è leggibile: vale il totale.',
    terms: 'Condizioni accettate: versione {version}.',
    noTerms: 'Condizioni non ancora accettate.',
    oldSiteTerms: 'Valgono le condizioni del vecchio sito.',
    termsSignedAtCheckout: 'Contratto firmato alla consegna (condizioni versione {version}).',
    contract: {
      title: 'Contratto',
      badge: 'Firmato sul dispositivo',
      none: 'Non ancora firmato: si firma sul dispositivo alla consegna.',
      prepared: 'In attesa della firma del cliente sul dispositivo.',
      signed: 'Firmato dal cliente sul dispositivo il {date}.',
      stale: 'La prenotazione è cambiata dopo la firma: il contratto va firmato di nuovo.',
      pdf: 'Apri il contratto firmato (PDF)',
      pdfPending: 'Il PDF del contratto è in preparazione.',
      pdfNotInApp:
        'Il PDF del contratto ora non si apre in quest’app: chiudila e riaprila, oppure apri il PDF da un computer.',
      // S8: a booking naming someone anonymised.
      ownerOnly:
        'Questa prenotazione nomina una persona anonimizzata: il contratto lo apre solo il titolare.',
      viewLogged:
        'Questa prenotazione nomina una persona anonimizzata: ogni apertura del contratto viene registrata.',
    },
    notes: 'Note',
    confirmedAt: 'Confermata: {date}',
    payment: 'Pagamento',
    payAtCounter: 'Il cliente paga al banco, al ritiro.',
    prepayOnline:
      'Si paga online sul sito: al banco non si incassa finché il pagamento online non la conferma.',
    paidInFull: 'Già pagato per intero: non c’è nulla da incassare.',
    feeAtCounter: 'Resta da pagare la penale: si incassa al banco.',
    nothingDue: 'Non c’è nulla da incassare.',
    owedAgain:
      'La banca del cliente ha restituito un pagamento: quanto è di nuovo dovuto si incassa solo al banco.',
    fineFeeLink:
      'Restano da pagare i diritti di gestione multe: si incassano al banco o con un link di pagamento.',
    fineFeeLinkPending:
      'Link di pagamento: restano da pagare i diritti di gestione multe, si incassano al banco.',
    fineFeeAtCounter:
      'Restano da pagare i diritti di gestione multe: per questa prenotazione si incassano solo al banco.',
    paymentLinkPending: 'Link di pagamento: il cliente paga al banco, al ritiro.',
    paymentLink: 'Link di pagamento',
    paymentLinkIntro:
      'Il cliente paga online il saldo, {amount}, da un link che vale 7 giorni. Mostra solo l’importo, non i dati della prenotazione.',
    paymentLinkEmail: 'Invia per e-mail',
    paymentLinkMake: 'Crea link da copiare',
    paymentLinkNoEmail:
      'La prenotazione non ha un indirizzo e-mail: crea il link e copialo nel messaggio.',
    paymentLinkEmailed: 'Link inviato per e-mail. Vale fino al {date}.',
    paymentLinkMade: 'Link pronto: copialo nel messaggio WhatsApp. Vale fino al {date}.',
    paymentLinkField: 'Link di pagamento da copiare',
    paymentLinkCopy: 'Copia',
    paymentLinkCopied: 'Copiato.',
    paymentLinkLastEmailed: 'Ultimo link inviato per e-mail: {date}.',
    paymentLinkLastMade: 'Ultimo link creato: {date}.',
    priceOverridden: 'Prezzo manuale: il listino era {quoted}. Motivo: {reason}',
    created: 'Prenotazione {code} confermata.',
    forCustomer: 'Nuova prenotazione per questo cliente',
    /** OPS-1: the move that is not a drag: the vehicle and the dates, from the booking page. */
    move: {
      action: 'Sposta…',
      title: 'Sposta la prenotazione {code}',
      hint: 'Un veicolo della stessa categoria e le date di ritiro e riconsegna, insieme o da soli. La disponibilità viene controllata prima di salvare.',
      keep: 'Com’è ora: {plate}',
      keepNone: 'Com’è ora: senza veicolo',
      unchanged: 'Nulla è cambiato.',
      invalidDates: 'Le date e gli orari devono essere completi.',
      returnBeforePickup: 'La riconsegna deve venire dopo il ritiro.',
    },
  },

  devices: {
    title: 'Dispositivi',
    intro: 'I dispositivi registrati, chi li sta usando e quando sono stati visti l’ultima volta.',
    current: 'Questo dispositivo',
    enrolled: 'Registrato da {name}, {date}',
    lastSeen: 'Ultima attività: {date}',
    inUse: 'In uso: {name}',
    locked: 'Bloccato',
    revoked: 'Disconnesso: {date}',
    revoke: 'Disconnetti',
    revokeTitle: 'Disconnettere «{name}»?',
    revokeBody:
      'Esce subito: la sua prossima richiesta viene rifiutata e per riusarlo va registrato di nuovo con e-mail e password.',
    revokeSelf: 'È il dispositivo che stai usando: uscirai anche tu.',
    confirm: 'Disconnetti',
    cancel: 'Indietro',
    done: '«{name}» è stato disconnesso.',
    empty: 'Nessun dispositivo registrato.',
    emptyHint: 'Un dispositivo compare qui quando qualcuno lo registra con e-mail e password.',
  },

  /**
   * S9 (ADM-1, Q-51): Persone, the staff accounts the Owner keeps. Giving
   * access asks for the Owner's code; taking it away does not, so a
   * deactivation is at once. Words about a person avoid a gender: «account»,
   * «la persona».
   */
  people: {
    title: 'Persone',
    intro:
      'Chi usa l’app e con quale ruolo. Un account nuovo parte con una password da usare una volta, consegnata a mano.',
    add: 'Nuova persona',
    activeTitle: 'Attive',
    disabledTitle: 'Disattivate',
    you: 'Sei tu',
    disabled: 'Account disattivato',
    disabledOn: 'Disattivato il {date}',
    passwordToChoose: 'Password da scegliere',
    passwordExpired: 'Password scaduta',
    temporaryUntil: 'Password consegnata, valida fino al {date}',
    expiredOn: 'La password consegnata è scaduta il {date}',
    lastUnlock: 'Ultimo accesso: {date}',
    neverUnlocked: 'Nessun accesso finora',
  },

  /** S9: a new account, with its one-time password (and an Owner's key) shown once. */
  newPerson: {
    title: 'Nuova persona',
    back: 'Persone',
    intro:
      'L’account parte con una password valida 72 ore, da consegnare a mano e mai per e-mail: al primo accesso la persona ne sceglie una sua.',
    name: 'Nome',
    nameHint: 'Come compare sul blocco schermo, per esempio «Giulia R.».',
    email: 'E-mail',
    emailHint: 'Serve per accedere: una per persona.',
    role: 'Ruolo',
    codeRole:
      'Questo ruolo accede anche con il codice: riceverà la chiave da inserire nell’app di autenticazione.',
    locale: 'Lingua delle e-mail',
    localeHint:
      'Avvisi e riepiloghi di lavoro arrivano in questa lingua; l’app cambia lingua dal menu.',
    code: 'Il tuo codice dell’app di autenticazione',
    submit: 'Crea l’account',
    why: {
      name: 'Scrivi il nome.',
      email: 'Scrivi un’e-mail valida.',
      role: 'Scegli il ruolo.',
      code: 'Scrivi le 6 cifre del tuo codice.',
    },
    done: 'Account creato per {name}.',
  },

  /** S9: one person's page: who they are, their access, the devices they used. */
  person: {
    title: 'Persona',
    back: 'Persone',
    notFound: 'Questa persona non esiste.',
    details: 'Dati',
    edit: 'Modifica',
    name: 'Nome',
    nameHint: 'Come compare sul blocco schermo.',
    email: 'E-mail',
    role: 'Ruolo',
    locale: 'Lingua delle e-mail',
    localeHint:
      'Avvisi e riepiloghi di lavoro arrivano in questa lingua; l’app cambia lingua dal menu.',
    authenticator: 'App di autenticazione',
    authenticatorSet: 'Configurata',
    authenticatorNone: 'Non serve con questo ruolo',
    lastUnlock: 'Ultimo accesso',
    never: 'Nessuno finora',
    save: 'Salva',
    cancel: 'Annulla',
    saved: 'Modifiche salvate.',
    conflict:
      'Nel frattempo questa persona è stata cambiata da un altro titolare: non è stato salvato niente. Qui sopra i dati di adesso; se serve, modifica di nuovo.',
    toCodeRole:
      'Con questo ruolo si accede anche con il codice: riceverà una chiave per l’app di autenticazione, mostrata una volta sola.',
    fromCodeRole: 'Con questo ruolo si accede senza codice: la sua chiave smette di funzionare.',
    roleSelf: 'Il tuo ruolo lo cambia un altro titolare.',
    roleDisabled: 'Il ruolo si cambia dopo la riattivazione.',
    code: 'Il tuo codice dell’app di autenticazione',
    why: {
      name: 'Scrivi il nome.',
      code: 'Scrivi le 6 cifre del tuo codice.',
      reason: 'Scrivi un motivo di almeno {min} caratteri.',
      nothing: 'Non hai cambiato niente.',
    },
    access: 'Accesso',
    accessIntro:
      'Dare accesso chiede il tuo codice; toglierlo no, così una disattivazione vale subito.',
    selfAccess:
      'Sul tuo account: la password la cambi da «Cambia password», ruolo e accesso li cambia un altro titolare.',
    newPassword: 'Nuova password',
    newKey: 'Nuova chiave di autenticazione',
    deactivate: 'Disattiva',
    reactivate: 'Riattiva',
    passwordTitle: 'Nuova password per {name}?',
    passwordBody:
      'La password attuale smette subito di funzionare, e se sta lavorando l’app si ferma finché non ne sceglie una sua. La nuova vale una volta sola, per 72 ore: consegnala a mano.',
    passwordConfirm: 'Crea la password',
    passwordDone: 'Nuova password creata per {name}.',
    keyTitle: 'Nuova chiave di autenticazione per {name}?',
    keyBody:
      'Per un telefono perso o cambiato: la chiave attuale smette subito di funzionare, e la nuova va inserita nell’app di autenticazione.',
    keyConfirm: 'Crea la chiave',
    keyDone: 'Nuova chiave creata per {name}.',
    deactivateTitle: 'Disattivare l’account di {name}?',
    deactivateBody:
      'Esce subito da ogni dispositivo: le sue sessioni finiscono ora. Torna ad accedere solo con una riattivazione, fatta da un titolare con il suo codice.',
    reason: 'Motivo',
    reasonHint: 'Resta nel registro, per esempio «fine stagione».',
    signOut: 'Disconnetti anche questi dispositivi',
    signOutHint:
      'Già spuntati quelli che ha usato solo questa persona, come il suo telefono. Uno disconnesso va registrato di nuovo.',
    deactivateConfirm: 'Disattiva',
    keep: 'Indietro',
    deactivateDone: 'Account di {name} disattivato: le sue sessioni sono finite.',
    reactivateTitle: 'Riattivare l’account di {name}?',
    reactivateBody:
      'Torna ad accedere con la sua password e il suo PIN, come prima. I dispositivi disconnessi vanno registrati di nuovo.',
    reactivateExpired: 'La password consegnata è scaduta: dopo, creane una nuova.',
    reactivateConfirm: 'Riattiva',
    reactivateDone: 'Account di {name} riattivato.',
    devices: 'Dispositivi usati',
    devicesNone: 'Non ha ancora usato nessun dispositivo.',
    deviceCurrent: 'Questo dispositivo',
    deviceOnly: 'Solo questa persona',
    deviceRevoked: 'Disconnesso il {date}',
    deviceLastUsed: 'Ultimo uso: {date}',
    deviceNeverUnlocked: 'Registrato, mai sbloccato',
  },

  /** S9: a password or a key someone else chose, shown once and kept nowhere. */
  shownOnce: {
    title: 'Da consegnare a mano',
    intro:
      'Si vede solo adesso: lasciata questa pagina non si potrà più rileggere. Se si perde, se ne crea una nuova dalla scheda della persona.',
    password: 'Password da usare una volta',
    passwordUntil: 'Vale fino al {date}. Al primo accesso {name} ne sceglie una sua.',
    key: 'Chiave per l’app di autenticazione',
    keyHint:
      'Nell’app (Google Authenticator, Microsoft Authenticator…) si aggiunge come «chiave di configurazione», con o senza spazi.',
    link: 'Apri nell’app di autenticazione',
    linkHint: 'Sul telefono dove c’è l’app: il collegamento la aggiunge da solo.',
    copy: 'Copia',
    copied: 'Copiato.',
    copyFailed: 'Copia non riuscita: seleziona il testo e copialo a mano.',
    done: 'Fatto',
  },

  newBooking: {
    title: 'Nuova prenotazione',
    cancel: 'Annulla',
    discardTitle: 'Scartare la prenotazione non salvata?',
    discardBody: 'Quello che hai scritto andrà perso.',
    discardConfirm: 'Scarta',
    discardKeep: 'Continua a compilare',
    source: 'Canale',
    when: 'Quando',
    pickupDate: 'Data di ritiro',
    pickupTime: 'Ora di ritiro',
    returnDate: 'Data di riconsegna',
    returnTime: 'Ora di riconsegna',
    pickupPlace: 'Luogo di ritiro',
    returnPlace: 'Luogo di riconsegna',
    // S9 (RES-6): a delivery to a port meets the customer's ferry.
    ferryName: 'Nave del cliente',
    ferryNameHint: 'Quella con cui arriva al porto, per chi gli porta il veicolo.',
    ferryTime: 'Arrivo della nave',
    ferryTimeHint: 'L’ora, nel giorno del ritiro.',
    needFerry: 'Scrivi la nave e la sua ora d’arrivo, o lasciale vuote entrambe.',
    locations: 'Sedi',
    zones: 'Consegne',
    vehicleSection: 'Veicolo',
    category: 'Categoria',
    chooseCategory: 'Scegli una categoria',
    vehicle: 'Veicolo',
    anyVehicle: 'Qualsiasi veicolo libero (da assegnare)',
    taken: 'occupato',
    noRoom: 'Nessun veicolo libero in questa categoria per queste date.',
    extras: 'Extra',
    cover: 'Coperture',
    promoCode: 'Codice sconto',
    priceSection: 'Prezzo',
    quoting: 'Calcolo del prezzo…',
    notBookable: 'Non prenotabile:',
    // S2: the counter books what the website refuses (stop sale), and says so.
    counterOnlyTitle: 'Solo dall’ufficio, il sito non la accetta:',
    customerSection: 'Cliente',
    existingCustomer: 'Cliente già registrato',
    newCustomer: 'Nuovo cliente',
    searchCustomer: 'Cerca cliente',
    searchCustomerHint: 'Nome, e-mail o telefono.',
    noCustomerFound: 'Nessun cliente trovato.',
    // S8: a record whose owner asked to be erased takes no new booking.
    erasureRequested:
      'Ha chiesto la cancellazione dei suoi dati il {date}: registralo come nuovo cliente.',
    choose: 'Scegli',
    chosen: 'Cliente: {name}',
    change: 'Cambia',
    firstName: 'Nome',
    lastName: 'Cognome',
    email: 'E-mail',
    phone: 'Telefono',
    phoneHint: 'Con il prefisso, per esempio +39 333 1234567.',
    birthDate: 'Data di nascita',
    nationality: 'Nazionalità (codice, es. IT)',
    language: 'Lingua delle e-mail al cliente',
    optional: 'facoltativo',
    contactNeeded: 'Al telefono, su WhatsApp o per e-mail serve almeno un telefono o un’e-mail.',
    licenceSection: 'Patente (facoltativa ora, obbligatoria al ritiro)',
    licenceNumber: 'Numero di patente',
    licenceCountry: 'Paese di rilascio (codice, es. IT)',
    licenceCategory: 'Categoria (es. B)',
    licenceFirstIssued: 'Prima emissione della categoria',
    licenceExpires: 'Scadenza',
    hasIdp: 'Ha il permesso internazionale di guida',
    overrideSection: 'Prezzo manuale',
    override: 'Applica un prezzo diverso dal listino',
    overrideTotal: 'Totale (€)',
    overrideReason: 'Motivo',
    overrideReasonHint: 'Obbligatorio: resta nel registro delle modifiche.',
    notes: 'Note',
    submit: 'Prenota',
    priceChanged: 'Il prezzo è cambiato: controlla il nuovo totale e conferma di nuovo.',
    incomplete: 'Compila le date, i luoghi e la categoria per vedere il prezzo.',
    priceDetails: 'Dettaglio del prezzo',
    needWhen: 'Scegli data e ora di ritiro e di riconsegna.',
    needPlace: 'Scegli il luogo di ritiro.',
    needCategory: 'Scegli la categoria.',
    noQuote: 'Il prezzo non è arrivato.',
    needCustomer: 'Scegli il cliente.',
    needName: 'Scrivi nome e cognome del cliente.',
    needOverrideTotal: 'Scrivi il totale del prezzo manuale.',
    needOverrideReason: 'Scrivi il motivo del prezzo manuale, in almeno 3 caratteri.',
    vehiclesWithPrice: 'I veicoli liberi arrivano con il prezzo.',
    placesFailed: 'I luoghi non si sono caricati.',
    optionsFailed: 'Gli extra e le coperture non si sono caricati.',
    searchTooShort: 'Scrivi almeno 2 lettere.',
    notesLabel: 'Note sulla prenotazione',
    promoRefused: 'Codice sconto {code}: {why}',
    promoReasons: {
      unknown: 'non esiste.',
      inactive: 'non è attivo.',
      not_yet_valid: 'non è ancora valido.',
      expired: 'è scaduto.',
      exhausted: 'è esaurito.',
      blackout: 'non vale in queste date.',
      location: 'vale solo per alcuni luoghi di ritiro e riconsegna.',
    },
    // S9 (ADM-4): a future booking made on the old site, carried over by hand.
    oldSite: {
      section: 'Prenotazione del vecchio sito',
      reference: 'Riferimento sul vecchio sito',
      referenceHint:
        'Il codice che il cliente conosce, come nell’e-mail di conferma del vecchio sito.',
      agreedTotal: 'Prezzo concordato (€)',
      agreedTotalHint: 'Il totale accettato sul vecchio sito: la prenotazione costa questo.',
      paid: 'Pagato sul vecchio sito (€)',
      paidHint: 'Vuoto se il cliente non ha pagato niente.',
      stripeId: 'Codice del pagamento in Stripe',
      stripeIdHint:
        'Comincia con pi_. Con il codice l’importo viene controllato con Stripe; senza, resta come scritto e non si può correggere.',
      noEmail:
        'Al cliente non parte nessuna e-mail: scrivigli tu, citando il riferimento del vecchio sito.',
      language: 'Lingua del cliente (per il contratto)',
      needReference: 'Scrivi il riferimento del vecchio sito.',
      needAgreedTotal: 'Scrivi il prezzo concordato.',
      needPaid: 'Scrivi quanto è stato pagato sul vecchio sito, oppure lascia vuoto.',
      needStripeId: 'Il codice del pagamento in Stripe comincia con pi_.',
      needPaidForStripe: 'Scrivi anche quanto è stato pagato con Stripe.',
      contactNeeded: 'Serve un telefono o un’e-mail: il cliente va avvisato a mano.',
      listPrice: 'Listino oggi',
      agreed: 'Prezzo concordato',
      paidThere: 'Pagato sul vecchio sito',
      due: 'Da pagare al ritiro',
      credit: 'Da restituire al cliente',
      waivedTitle: 'Regole del vecchio sito, non controllate qui:',
      submit: 'Registra',
      confirmTitle: 'Registrare la prenotazione {reference} del vecchio sito?',
      confirmLabel: 'Registra la prenotazione',
      correct: 'Correggi',
      paidWith: '{amount}, con Stripe ({id})',
      paidWithout: '{amount}, senza codice Stripe',
      nothingPaid: 'Niente',
      overpaid: 'Il pagato supera il prezzo concordato: controlla gli importi prima di registrare.',
      noCorrection: 'Senza codice Stripe l’importo pagato non si potrà correggere.',
      openTaken: 'Apri la prenotazione {code}',
    },
  },

  legs: {
    pickup: 'Ritiro',
    return: 'Riconsegna',
  },

  refusals: {
    invalid_period: 'La riconsegna deve essere dopo il ritiro.',
    in_the_past: 'Il ritiro è passato da più di 30 minuti.',
    category_unavailable: 'Categoria non prenotabile.',
    season_not_configured: 'Mancano i prezzi dal {from} al {to}.',
    rate_not_configured: 'Manca un prezzo per questa durata.',
    day_price_not_configured: 'Manca il prezzo nel calendario dal {from} al {to}.',
    stop_sale: 'Vendita online chiusa dal {from} al {to}.',
    min_stay: 'Noleggio minimo in queste date: {minDays} giorni.',
    place_unknown: 'Luogo non più disponibile.',
    outside_opening_hours: '{leg} alle {time}: {location} è chiuso.',
    delivery_blackout: '{leg} · {zone}: consegne sospese in questo periodo.',
    delivery_lead_time: '{leg} · {zone}: preavviso minimo di {minutes} minuti.',
    delivery_cutoff: '{leg} · {zone}: da prenotare entro le {cutoff} del giorno prima.',
    delivery_fee_not_configured: '{leg} · {zone}: tariffa di consegna non configurata.',
    one_way_fee_not_configured: 'Tariffa per la riconsegna in un altro luogo non configurata.',
    extra_unknown: 'Un extra non è più disponibile.',
    extra_not_for_category: '{extra}: non previsto per questa categoria.',
    extra_quantity: '{extra}: al massimo {max}.',
    extra_out_of_stock: '{extra}: ne restano {left} per queste date.',
    coverage_unknown: 'Una copertura non è più disponibile.',
    driver_too_young: 'Età minima del conducente: {minAge} anni.',
    young_driver_surcharge_not_configured:
      'Supplemento giovane conducente non configurato per questa categoria.',
  } satisfies Record<RefusalCode, string>,

  /** S2: a refusal whose span is one day ({from} = {to}) names it once, as {day}. */
  refusalsOneDay: {
    season_not_configured: 'Mancano i prezzi per {day}.',
    day_price_not_configured: 'Manca il prezzo nel calendario per {day}.',
    stop_sale: 'Vendita online chiusa per {day}.',
  },

  timeline: {
    title: 'Planning',
    from: 'Dal',
    days: 'Giorni',
    earlier: 'Prima',
    later: 'Dopo',
    today: 'Oggi',
    category: 'Categoria',
    allCategories: 'Tutte le categorie',
    unassigned: 'Da assegnare',
    noVehicles: 'Nessun veicolo attivo.',
    noCategories: 'Nessuna categoria.',
    categoryGone: 'Categoria non trovata',
    categoryGoneHint:
      'Il collegamento porta a una categoria che non c’è più: scegline un’altra qui sopra.',
    legend: 'Legenda',
    legendConfirmed: 'Prenotazione',
    legendOut: 'In noleggio',
    legendUnassigned: 'Senza veicolo',
    legendTurnaround: 'Pulizia',
    legendOutOfService: 'Manutenzione o blocco',
    untilFurtherNotice: 'fino a nuovo avviso',
    bar: '{code}, {name}: {status}, {from} – {to}',
    blockBar: '{kind}: {reason}, {from} – {to}',
    turnaroundBar: 'Pulizia: {from} – {to}',
    now: 'Ora',
    dragMoveHint: 'Rilascia per spostare, Esc per annullare',
    createHint: 'Rilascia per creare una prenotazione in queste date, Esc per annullare',
    resizeHint: 'Rilascia per impostare il nuovo orario, Esc per annullare',
    confirmMoveTitle: 'Spostare questa prenotazione?',
    confirmMoveBody: 'Prenotazione {code}. Veicolo: {vehicle}. Date: {from} → {to}.',
    reasonVehicleTaken: 'Quel veicolo è già occupato in quelle date.',
    reasonCategory: 'Nessun veicolo di questa categoria è libero in quelle date.',
    reasonNotReschedulable: 'Questa prenotazione non può più essere spostata.',
    /** S9: a trip to or from a delivery zone moved where the zone does not take it. */
    reasonDeliveryZone:
      'La zona di consegna non accetta queste date: consegne sospese, preavviso troppo breve o prenotazioni chiuse per quel giorno.',
    /** Q-151: a hand-over or a return moved outside its pick-up point's hours. */
    reasonOpeningHours: 'Il punto è chiuso a quell’ora: scegli un orario di apertura.',
    confirmMove: 'Sposta',
    cancelMove: 'Lascia com’è',
    moved: 'Prenotazione spostata.',
    moveFailed: 'Non è stato possibile spostare la prenotazione. Nulla è stato modificato.',
    /** OPS-1: any number of days, a quarter at most, typed beside the presets. */
    custom: 'Personalizzato…',
    customDays: 'Numero di giorni',
    customRange: 'Da 1 a {max} giorni.',
  },

  /**
   * W2-S S1: Prezzi, the rate calendar (prices.manage): a price, two minimums
   * and stop sale per category and day, a day at a time or many at once.
   */
  prices: {
    title: 'Prezzi',
    intro:
      'Prezzo, giorni minimi e vendita online per categoria e giorno. Tocca un giorno per cambiarlo.',
    from: 'Dal',
    category: 'Categoria',
    allCategories: 'Tutte le categorie',
    noCategories: 'Nessuna categoria.',
    categoryGone: 'Categoria non trovata',
    categoryGoneHint:
      'Il collegamento porta a una categoria che non c’è più: scegline un’altra qui sopra.',
    activeUnits: { one: '{count} veicolo attivo', other: '{count} veicoli attivi' },
    noPrice: '—',
    noPriceSaid: 'nessun prezzo',
    minPickup: 'min {count} (dal giorno)',
    minThrough: 'min {count} (tutto il noleggio)',
    minPickupSaid: {
      one: 'minimo {count} giorno per un noleggio che inizia qui',
      other: 'minimo {count} giorni per un noleggio che inizia qui',
    },
    minThroughSaid: {
      one: 'minimo {count} giorno per ogni noleggio che comprende questo giorno',
      other: 'minimo {count} giorni per ogni noleggio che comprende questo giorno',
    },
    stopSale: 'Vendita online chiusa',
    stopSaleOpen: 'Vendita online aperta',
    free: '{free}/{total} liberi',
    freeSaid: '{free} liberi su {total}',
    freeUnknown: '?/{total}',
    noBoundary: 'senza orario',
    noBoundarySaid:
      'liberi non noti su {total}: l’ora d’inizio del giorno di noleggio non è impostata',
    notCounted: 'non calcolato',
    notCountedSaid: 'liberi non noti su {total}: calcolo non concluso',
    cell: '{category}, {date}: {details}',
    pastCell: '{category}, {date}, giorno passato: {details}',
    earlier: 'Prima',
    later: 'Dopo',
    today: 'Oggi',
    /** One day's dialog. */
    dayTitle: '{category}, {date}',
    price: 'Prezzo del giorno (€)',
    priceHint: 'Per esempio 45,50. Vuoto: nessun prezzo.',
    priceInvalid: 'Scrivi un importo da 0,01 a {max}, per esempio 45,50.',
    minPickupLabel: 'Giorni minimi (dal giorno)',
    minPickupHint: 'Per un noleggio che inizia questo giorno.',
    minThroughLabel: 'Giorni minimi (tutto il noleggio)',
    minThroughHint: 'Per ogni noleggio che comprende questo giorno.',
    minInvalid: 'Scrivi un numero intero da 1 a {max}.',
    optional: 'facoltativo',
    stopSaleLabel: 'Chiudi la vendita online',
    save: 'Salva',
    cancel: 'Annulla',
    close: 'Chiudi',
    saved: 'Salvato: {category}, {date}.',
    conflict:
      'Qualcuno ha cambiato questo giorno mentre lo modificavi: nulla è stato salvato. Ecco com’è ora e cosa hai scritto.',
    conflictTable: 'Il giorno com’è ora e cosa hai scritto',
    conflictWhat: 'Voce',
    conflictNow: 'Ora',
    conflictTyped: 'Hai scritto',
    saveOver: 'Salva sopra',
    none: 'nessuno',
    open: 'aperta',
    closed: 'chiusa',
    /** Many days at once: preview, then apply with the Owner's code. */
    bulk: 'Modifica più giorni',
    categories: 'Categorie',
    bulkFrom: 'Dal',
    bulkTo: 'Al',
    fromEarlier: 'Sposta il primo giorno indietro',
    fromLater: 'Sposta il primo giorno avanti',
    fromToday: 'Da oggi',
    toEarlier: 'Sposta l’ultimo giorno indietro',
    toLater: 'Sposta l’ultimo giorno avanti',
    toToday: 'Fino a oggi',
    weekdays: 'Giorni della settimana',
    allWeek: 'Tutti',
    weekend: 'Weekend',
    whatToSet: 'Cosa cambiare',
    priceMode: 'Prezzo',
    leave: 'Lascia com’è',
    setTo: 'Imposta a',
    upBy: 'Aumenta di',
    downBy: 'Riduci di',
    amount: 'Importo (€)',
    clear: 'Togli',
    minDays: 'Giorni',
    stopSaleMode: 'Vendita online',
    stopOn: 'Chiudi',
    stopOff: 'Apri',
    preview: 'Anteprima',
    why: {
      categories: 'Scegli almeno una categoria.',
      weekdays: 'Scegli almeno un giorno della settimana.',
      range: 'L’ultimo giorno viene prima del primo.',
      past: 'Il primo giorno è già passato.',
      nothing: 'Scegli almeno una cosa da cambiare.',
      amount: 'Scrivi un importo da 0,01 a {max}.',
      min: 'Scrivi i giorni minimi, da 1 a {max}.',
      code: 'Scrivi il codice del Titolare.',
    },
    changesCount: { one: '{count} giorno cambia.', other: '{count} giorni cambiano.' },
    unchangedCount: {
      one: '{count} giorno è già così.',
      other: '{count} giorni sono già così.',
    },
    skippedCount: {
      one: '{count} giorno saltato: non ha un prezzo da aumentare o ridurre.',
      other: '{count} giorni saltati: non hanno un prezzo da aumentare o ridurre.',
    },
    changesTable: 'Giorni che cambiano',
    skippedTable: 'Giorni saltati',
    colCategory: 'Categoria',
    colDay: 'Giorno',
    colBefore: 'Prima',
    colAfter: 'Dopo',
    colNote: 'Nota',
    colReason: 'Perché',
    noPriceToMove: 'Nessun prezzo da aumentare o ridurre',
    bigMove: 'Cambia più della metà',
    bigMoveCount: {
      one: '{count} prezzo cambia di più della metà: controllalo.',
      other: '{count} prezzi cambiano di più della metà: controllali.',
    },
    noChanges: 'Nessun giorno cambia.',
    code: 'Codice del Titolare',
    apply: 'Applica',
    confirmBig: 'Alcuni prezzi cambiano di più della metà. Applichi lo stesso?',
    confirmApply: 'Sì, applica',
    backToPreview: 'Torna all’anteprima',
    stale: 'Qualcuno ha cambiato uno di questi giorni dopo l’anteprima: nulla è stato cambiato.',
    previewAgain: 'Rifai l’anteprima',
    applied: 'Giorni cambiati: {changed}. Già così: {unchanged}. Saltati: {skipped}.',
  },

  vehicles: {
    title: 'Veicoli',
    search: 'Cerca',
    searchHint: 'Targa, marca o modello.',
    category: 'Categoria',
    allCategories: 'Tutte le categorie',
    retired: 'Mostra i veicoli dismessi',
    none: 'Nessun veicolo.',
    fills: 'I veicoli arrivano con la configurazione dell’azienda.',
    found: {
      one: '{count} veicolo',
      other: '{count} veicoli',
    },
    categoriesFailed: 'Le categorie non si sono caricate.',
    next: 'Prossima: {code}, {date}',
    noNext: 'Nessuna prenotazione in arrivo',
    expiry: '{kind} scade il {date}',
    /** UI-2g: a day already gone, one sentence per kind so the word agrees with it. */
    expired: {
      insurance: 'Assicurazione scaduta il {date}',
      revisione: 'Revisione scaduta il {date}',
      bollo: 'Bollo scaduto il {date}',
      other: 'Altro documento scaduto il {date}',
    } satisfies Record<VehicleDocumentKind, string>,
  },

  vehicle: {
    back: 'Veicoli',
    title: 'Veicolo',
    notFound: 'Nessun veicolo a questo indirizzo.',
    details: 'Scheda del veicolo',
    category: 'Categoria',
    year: 'Anno',
    fuel: 'Alimentazione',
    transmission: 'Cambio',
    seats: 'Posti',
    odometer: 'Chilometri',
    vin: 'Telaio',
    notes: 'Note',
    notGiven: 'Non indicato',
    blocks: 'Manutenzioni e blocchi',
    noBlocks: 'Nessuna manutenzione o blocco.',
    newBlock: 'Togli il veicolo dalla disponibilità',
    kind: 'Tipo',
    startDate: 'Dal giorno',
    startTime: 'Dalle ore',
    endDate: 'Al giorno',
    endTime: 'Alle ore',
    openEnded: 'Lascia vuota la fine: fino a nuovo avviso.',
    reason: 'Motivo',
    addBlock: 'Blocca il veicolo',
    blocked: 'Veicolo bloccato.',
    endBlock: 'Termina ora',
    cancelBlock: 'Annulla il blocco',
    blockEnded: 'Blocco terminato.',
    blockCancelled: 'Blocco annullato.',
    endBlockLabel: 'Termina ora: {kind} dal {date}',
    cancelBlockLabel: 'Annulla il blocco: {kind} dal {date}',
    cancelBlockTitle: 'Annullare il blocco?',
    cancelBlockBody: '{kind} dal {date}: il veicolo torna disponibile in quel periodo.',
    keep: 'Mantieni',
    needReason: 'Scrivi il motivo, almeno 3 caratteri.',
    needStart: 'Scegli il giorno e l’ora d’inizio.',
    needEndTime: 'Scegli l’ora di fine, o lascia vuoto il giorno di fine.',
    busy: 'Il veicolo è prenotato in quel periodo: {codes}.',
    cancelled: 'Annullato',
    ended: 'Concluso',
    documents: 'Documenti',
    noDocuments: 'Nessun documento registrato.',
    newDocument: 'Registra un documento o un rinnovo',
    documentKind: 'Documento',
    expiresOn: 'Scadenza',
    reference: 'Riferimento (facoltativo)',
    addDocument: 'Registra',
    documentAdded: 'Documento registrato.',
    superseded: 'Sostituito',
    expired: 'Scaduto',
    expiresOnDate: 'Scade il {date}',
    expiredOn: 'Scaduto il {date}',
    needExpiry: 'Scegli la data di scadenza.',
    bookings: 'Prenotazioni',
    noBookings: 'Nessuna prenotazione dagli ultimi 30 giorni.',
    damages: 'Danni da riparare',
    noDamages: 'Nessun danno da riparare.',
    damagesHint: 'Restano sullo schema di ogni consegna finché non sono segnati come riparati.',
    damageFound: {
      checkout: 'Segnato alla consegna di {code}, il {date}.',
      return: 'Segnato al rientro di {code}, il {date}.',
    },
    repair: 'Segna come riparato',
    repairLabel: 'Segna come riparato: {description}',
    repaired: 'Riparato: {description}.',
    correctDocument: 'Correggi',
    correctDocumentLabel: 'Correggi: {kind} con scadenza {date}',
    correctDocumentTitle: 'Annullare questo documento?',
    correctDocumentBody:
      '{kind} con scadenza {date}: resta nell’elenco, barrato, e non conta più per le scadenze. Registra poi quello giusto.',
    correctDocumentConfirm: 'Annulla il documento',
    correctReason: 'Motivo',
    correctWhy: 'Scrivi il motivo, almeno {min} caratteri.',
    voided: 'Annullato',
    voidedWhy: 'Annullato: {reason}',
    documentVoided: 'Documento annullato.',
  },

  vehicleStates: {
    available: 'Libero',
    reserved: 'Prenotato ora',
    on_rent: 'In noleggio',
    turnaround: 'In pulizia',
    maintenance: 'In manutenzione',
    blocked: 'Bloccato',
    retired: 'Dismesso',
  } satisfies Record<VehicleState, string>,

  maintenanceKinds: {
    service: 'Tagliando',
    repair: 'Riparazione',
    block: 'Blocco',
  } satisfies Record<MaintenanceKind, string>,

  /** Manutenzione: the register of service and repair interventions (with their cost). */
  maintenance: {
    title: 'Manutenzione',
    nav: 'Manutenzione',
    empty: 'Nessun intervento registrato.',
    columns: {
      date: 'Data',
      vehicle: 'Veicolo',
      kind: 'Tipo',
      description: 'Descrizione',
      cost: 'Costo',
      supplier: 'Fornitore',
      rental: 'Noleggio collegato',
    },
    kinds: {
      service: 'Tagliando',
      repair: 'Riparazione',
    },
    filters: {
      vehicle: 'Veicolo',
      allVehicles: 'Tutti i veicoli',
      from: 'Dal',
      to: 'Al',
      kind: 'Tipo',
      allKinds: 'Tutti i tipi',
    },
    form: {
      title: 'Nuovo intervento',
      open: 'Nuovo intervento',
      date: 'Data dell’intervento',
      vehicle: 'Veicolo',
      kind: 'Tipo',
      description: 'Descrizione',
      cost: 'Costo',
      supplier: 'Fornitore',
      rental: 'Noleggio collegato (facoltativo)',
      save: 'Salva intervento',
      cancel: 'Annulla',
    },
    totals: {
      filtered: 'Totale costi (filtri applicati)',
      vehicle: 'Costi totali del veicolo',
    },
    void: {
      action: 'Annulla intervento',
      label: 'Annulla intervento: {description}',
      title: 'Annullare l’intervento del {date}?',
      details: '{vehicle}: {description}',
      confirm: 'Annullare questo intervento? Non verrà più conteggiato nei costi.',
      reason: 'Motivo',
      why: 'Scrivi il motivo, almeno {min} caratteri.',
      confirmAction: 'Sì, annulla',
      keep: 'Mantieni',
      done: 'Intervento annullato.',
    },
  },

  fuels: {
    petrol: 'Benzina',
    diesel: 'Diesel',
    electric: 'Elettrico',
    hybrid: 'Ibrido',
    lpg: 'GPL',
    methane: 'Metano',
  } satisfies Record<FuelType, string>,

  transmissions: {
    manual: 'Manuale',
    automatic: 'Automatico',
  } satisfies Record<Transmission, string>,

  customers: {
    title: 'Clienti',
    search: 'Cerca',
    searchHint: 'Nome, e-mail o telefono.',
    none: 'Nessun cliente trovato.',
    noneYet: 'Ancora nessun cliente.',
    fills: 'Ogni prenotazione porta il suo cliente.',
    found: {
      one: '{count} cliente',
      other: '{count} clienti',
    },
    bookings: {
      one: '{count} prenotazione',
      other: '{count} prenotazioni',
    },
    last: 'Ultima: {code}, {date}',
  },

  customer: {
    back: 'Clienti',
    title: 'Cliente',
    notFound: 'Nessun cliente a questo indirizzo.',
    contacts: 'Dati',
    address: 'Indirizzo',
    birthPlace: 'Luogo di nascita',
    language: 'Lingua',
    since: 'Cliente dal {date}',
    licences: 'Patenti',
    noLicences: 'Nessuna patente registrata.',
    licence: '{number}, {country}',
    categoriesHeld: 'Categorie: {categories}',
    expires: 'Scade il {date}',
    expired: 'Scaduta',
    expiredOn: 'Scaduta il {date}',
    idp: 'Permesso internazionale di guida',
    history: 'Noleggi',
    noBookings: 'Nessuna prenotazione.',
    // S8: a record whose owner asked to be erased, a contact erased, a customer anonymised.
    erasedOn: 'Dato cancellato il {date}',
    contactErased: 'Recapiti cancellati',
    erasureRequested: 'Cancellazione richiesta',
    requestNotice:
      'Ha chiesto la cancellazione dei suoi dati il {date}: questa scheda non si usa più per nuove prenotazioni. Se torna a noleggiare, registralo come nuovo cliente.',
    anonymisedTitle: 'Cliente anonimizzato',
    anonymisedMeta: 'Cliente dal {since} · anonimizzato il {date}',
    remains: {
      title: 'Cosa resta',
      erased:
        'Nome, recapiti, nascita, indirizzo, patenti e foto dei documenti sono stati cancellati il {date}.',
      keptByLaw:
        'Restano le prenotazioni con i loro pagamenti, i contratti firmati, le lettere delle multe e i documenti fiscali, che la legge chiede di conservare: per questi non è fissata una data di cancellazione.',
      ownerOnly:
        'Un contratto o una lettera che lo nomina si apre solo per il titolare, e ogni apertura viene registrata.',
      vehiclePhotos: {
        one: 'Le foto del veicolo restano {count} giorno dalla fine di ciascun noleggio.',
        other: 'Le foto del veicolo restano {count} giorni dalla fine di ciascun noleggio.',
      },
      vehiclePhotosKept:
        'Le foto del veicolo restano finché non viene impostato per quanto tenerle.',
    },
    correction: {
      edit: 'Modifica dati',
      title: 'Correggi i dati',
      firstName: 'Nome',
      lastName: 'Cognome',
      birthDate: 'Data di nascita',
      birthPlace: 'Luogo di nascita',
      nationality: 'Nazionalità (codice, es. IT)',
      email: 'E-mail',
      phone: 'Telefono',
      phoneHint: 'Con il prefisso internazionale, per esempio +39 333 1234567.',
      addressLine: 'Indirizzo',
      postalCode: 'CAP',
      city: 'Città',
      province: 'Provincia (es. NA)',
      country: 'Paese (codice, es. IT)',
      preferredLocale: 'Lingua',
      notes: 'Note',
      fiscal: 'Dati di fatturazione',
      partyType: 'Fattura come',
      partyTypeNone: 'Non indicato',
      partyTypes: { person: 'Privato', company: 'Azienda' },
      vatNumber: 'Partita IVA',
      taxCode: 'Codice fiscale',
      sdiCode: 'Codice destinatario SdI',
      pec: 'PEC',
      optional: 'facoltativo',
      invalid: 'Non è valido: controlla.',
      contactErased:
        'E-mail, telefono e note sono stati cancellati su sua richiesta il {date}: non si possono reinserire.',
      keepsRecords:
        'I contratti già firmati e i dati già inviati a CaRGOS restano come erano: la correzione vale da qui in avanti.',
      save: 'Salva le correzioni',
      cancel: 'Annulla',
      saved: 'Dati corretti: {fields}.',
      unchanged: 'Nessuna correzione: i dati erano già così.',
      why: {
        nothing: 'Non hai ancora cambiato nulla.',
        invalid: 'Controlla i campi segnati.',
        conflict: 'Rileggi la scheda prima di salvare.',
      },
      conflict:
        'La scheda è cambiata da quando l’hai aperta, e nulla è stato salvato. Rileggila: quello che hai scritto resta.',
      reread: 'Rileggi la scheda',
      rereadKept: 'Scheda riletta. Restano le tue correzioni a: {fields}.',
      rereadNone: 'Scheda riletta: non avevi ancora cambiato nulla.',
      clashed: 'Anche qualcun altro aveva cambiato {fields}: controlla prima di salvare.',
      fields: {
        firstName: 'nome',
        lastName: 'cognome',
        birthDate: 'data di nascita',
        birthPlace: 'luogo di nascita',
        nationality: 'nazionalità',
        email: 'e-mail',
        phone: 'telefono',
        addressLine: 'indirizzo',
        postalCode: 'CAP',
        city: 'città',
        province: 'provincia',
        country: 'paese',
        preferredLocale: 'lingua',
        notes: 'note',
        partyType: 'tipo di fatturazione',
        vatNumber: 'partita IVA',
        taxCode: 'codice fiscale',
        sdiCode: 'codice destinatario SdI',
        pec: 'PEC',
      },
    },
    licenceCorrection: {
      edit: 'Modifica',
      editLabel: 'Modifica la patente {number}',
      title: 'Correggi la patente',
      number: 'Numero di patente',
      issuingCountry: 'Paese di rilascio (codice, es. IT)',
      expiresOn: 'Scadenza',
      categories: 'Categorie',
      category: 'Categoria (es. B)',
      firstIssuedOn: 'Prima emissione della categoria',
      addCategory: 'Aggiungi una categoria',
      removeCategory: 'Togli',
      removeCategoryLabel: 'Togli la categoria {code}',
      hasIdp: 'Ha il permesso internazionale di guida',
      saved: 'Patente corretta: {fields}.',
      gone: 'Questa patente non è più in archivio: è stata cancellata con i dati del cliente.',
      why: {
        categories: 'Indica almeno una categoria, con la data di prima emissione se la conosci.',
      },
      fields: {
        number: 'numero',
        issuingCountry: 'paese di rilascio',
        expiresOn: 'scadenza',
        categories: 'categorie',
        hasIdp: 'permesso internazionale',
      },
    },
  },

  /** S8 (GDPR): the Owner's privacy panel on the Cliente page, and the holds Sistema names. */
  privacy: {
    title: 'Privacy',
    intro:
      'Cosa succede ai dati di questo cliente, cosa li trattiene e fino a quando. Le richieste arrivano a mano, e si rispondono entro un mese dal giorno in cui sono arrivate.',
    anonymised: 'Anonimizzato il {date}: la scheda non nomina più nessuno.',
    requested:
      'Richiesta di cancellazione arrivata il {date} {channel}, registrata da {by} il {at}.',
    withdrawable: 'Si può ritirare fino al {until}.',
    hold: 'Blocco legale dal {date}, messo da {by}: {reason}.',
    parts: {
      contact: 'Recapiti (e-mail, telefono, note)',
      identity: 'Identità (nome, nascita, indirizzo, patenti, documenti)',
    },
    verdict: {
      erased: 'Cancellati il {date}.',
      held: 'Trattenuti {holds}.',
      erasable: 'Vanno via al prossimo passaggio, entro un’ora.',
      kept: 'Conservati fino al {date}.',
      notConfigured: 'Conservati: per questi dati non è ancora impostato un periodo.',
    },
    until: '{hold} (fino al {date})',
    holds: {
      not_ended: 'una prenotazione non ancora chiusa',
      balance: 'un saldo da regolare',
      deposit: 'una cauzione in corso',
      refund: 'un rimborso in corso',
      dispute: 'una contestazione della carta',
      payment_page: 'una pagina di pagamento ancora aperta',
      stripe_difference: 'una differenza con Stripe da chiarire',
      fine: 'una multa aperta',
      cargos: 'un invio a CaRGOS senza risposta',
      legal_hold: 'il blocco legale',
      request_grace: 'le 24 ore per ritirare la richiesta',
      fine_window: 'il periodo in cui può ancora arrivare una multa',
      damage: 'un danno non ancora riparato',
    },
    /** The same, after "Trattenuti": each with its own preposition (dal blocco, dalle 24 ore). */
    heldBy: {
      not_ended: 'da una prenotazione non ancora chiusa',
      balance: 'da un saldo da regolare',
      deposit: 'da una cauzione in corso',
      refund: 'da un rimborso in corso',
      dispute: 'da una contestazione della carta',
      payment_page: 'da una pagina di pagamento ancora aperta',
      stripe_difference: 'da una differenza con Stripe da chiarire',
      fine: 'da una multa aperta',
      cargos: 'da un invio a CaRGOS senza risposta',
      legal_hold: 'dal blocco legale',
      request_grace: 'dalle 24 ore per ritirare la richiesta',
      fine_window: 'dal periodo in cui può ancora arrivare una multa',
      damage: 'da un danno non ancora riparato',
    },
    bookings: {
      title: 'Prenotazioni',
      renter: 'intestatario',
      second_driver: 'secondo conducente',
      held: 'Trattenuta {holds}.',
      free: 'Nulla la trattiene.',
    },
    keptByLaw:
      'Conservati per legge, qualunque cosa venga chiesta: {contracts}, {letters}, {fiscal}.',
    contracts: {
      one: '{count} contratto firmato',
      other: '{count} contratti firmati',
    },
    letters: {
      one: '{count} lettera di una multa',
      other: '{count} lettere di multe',
    },
    fiscal: {
      one: '{count} documento fiscale',
      other: '{count} documenti fiscali',
    },
    photos: {
      one: '{count} foto di un documento ancora conservata: va via con l’identità.',
      other: '{count} foto di documenti ancora conservate: vanno via con l’identità.',
    },
    duplicates: {
      title: 'La stessa persona su altre schede?',
      intro:
        'Queste schede hanno la stessa e-mail o lo stesso telefono. Una richiesta va registrata su ciascuna scheda della persona.',
      email: 'stessa e-mail',
      phone: 'stesso telefono',
    },
    channels: {
      email: 'per e-mail',
      letter: 'per lettera',
      in_person: 'di persona',
      phone: 'per telefono',
    },
    channelNames: {
      email: 'E-mail',
      letter: 'Lettera',
      in_person: 'Di persona',
      phone: 'Telefono',
    },
    reasons: {
      litigation: 'una causa in corso',
      authority_request: 'una richiesta di un’autorità',
      insurance_claim: 'un sinistro con l’assicurazione',
      other: 'un altro motivo',
    },
    reasonNames: {
      litigation: 'Causa in corso',
      authority_request: 'Richiesta di un’autorità',
      insurance_claim: 'Sinistro con l’assicurazione',
      other: 'Altro motivo',
    },
    code: 'Codice dell’app di autenticazione del titolare',
    cancel: 'Annulla',
    request: {
      action: 'Registra una richiesta di cancellazione',
      title: 'Registrare una richiesta di cancellazione?',
      preview: 'Cosa succederebbe ai suoi dati:',
      grace:
        'Per 24 ore la richiesta si può ritirare; poi i dati vanno via appena nulla li trattiene. I contratti firmati, le lettere delle multe e i documenti fiscali restano comunque, per legge.',
      duplicates: {
        one: 'Un’altra scheda ha la stessa e-mail o lo stesso telefono: se è la stessa persona, registra la richiesta anche lì.',
        other:
          'Altre {count} schede hanno la stessa e-mail o lo stesso telefono: se sono della stessa persona, registra la richiesta anche lì.',
      },
      requestedOn: 'Giorno in cui è arrivata',
      requestedOnHint: 'Il mese per rispondere parte da questo giorno.',
      channel: 'Come è arrivata',
      confirm: 'Registra la richiesta',
      why: {
        day: 'Indica il giorno in cui è arrivata: oggi o prima.',
        channel: 'Indica come è arrivata.',
        code: 'Scrivi le sei cifre del codice.',
      },
      done: 'Richiesta registrata. Nulla va via prima del {until}: fino ad allora si può ritirare.',
    },
    withdraw: {
      action: 'Ritira la richiesta',
      title: 'Ritirare la richiesta di cancellazione?',
      body: 'La richiesta arrivata il {date} viene ritirata, come se non fosse mai stata registrata. Si può fare fino al {until}.',
      confirm: 'Ritira la richiesta',
      keep: 'Lascia la richiesta',
      done: 'Richiesta ritirata: i dati tornano a seguire le regole di conservazione.',
    },
    legalHold: {
      action: 'Metti un blocco legale',
      title: 'Mettere un blocco legale?',
      body: 'Finché resta, nulla dei dati di questo cliente viene cancellato, nemmeno su sua richiesta. Per toglierlo serve il codice del titolare.',
      reason: 'Motivo',
      confirm: 'Metti il blocco',
      why: 'Scegli il motivo.',
      done: 'Blocco legale messo: nulla dei suoi dati viene cancellato finché resta.',
    },
    lift: {
      action: 'Togli il blocco legale',
      title: 'Togliere il blocco legale?',
      body: 'Il blocco messo il {date} per {reason} viene tolto: i dati tornano a seguire le regole di conservazione.',
      confirm: 'Togli il blocco',
      why: 'Scrivi le sei cifre del codice.',
      done: 'Blocco legale tolto.',
    },
    export: {
      action: 'Esporta i dati',
      title: 'Esportare i dati del cliente?',
      body: 'Un archivio ZIP con i dati che conserviamo sul cliente: scheda, patenti, prenotazioni, pagamenti, contratti e lettere delle multe, con un file che spiega cosa contiene, nella sua lingua. Non contiene i tratti della firma, le foto dei veicoli né i dati del personale.',
      purpose: 'Che cosa chiede',
      purposes: {
        access: 'Una copia dei suoi dati (art. 15)',
        portability: 'I suoi dati, per portarli altrove (art. 20)',
      },
      documents: 'Foto dei documenti d’identità',
      documentsChoices: { include: 'Includile', leave: 'Lasciale fuori' },
      documentsHint: 'Sono la parte più delicata del file: includile se le ha chieste.',
      care: 'Il file contiene dati personali: consegnalo come dice la procedura, dopo aver controllato chi lo riceve, e poi eliminalo da questo dispositivo.',
      confirm: 'Prepara il file',
      why: {
        purpose: 'Indica che cosa chiede.',
        documents: 'Scegli se includere le foto dei documenti.',
      },
      done: 'Il file è pronto e il browser lo sta salvando. Contiene dati personali: consegnalo come dice la procedura e poi eliminalo da questo dispositivo.',
      notInApp: 'Esportazione dei dati nell’app per iPad: si fa dall’app web.',
    },
  },

  languages: {
    it: 'Italiano',
    en: 'Inglese',
    de: 'Tedesco',
  } satisfies Record<Locale, string>,

  /** W2-S S4: a website booking waiting for its online share. */
  hold: {
    held: 'In attesa del pagamento online',
    ended: 'Pagamento online non completato',
    heldNote:
      'Il cliente paga online {amount}, la sua parte; l’auto è tenuta fino alle {time}. Solo quel pagamento conferma la prenotazione.',
    endedNote:
      'Il pagamento online non è stato completato in tempo: l’auto non è più tenuta. Se il cliente paga ancora, la prenotazione si conferma da sola e, se non c’è più un’auto, Oggi lo segnala.',
  },

  status: {
    draft: 'Bozza',
    confirmed: 'Confermata',
    checked_out: 'In noleggio',
    returned: 'Rientrata',
    closed: 'Chiusa',
    cancelled: 'Annullata',
    no_show: 'Cliente non presentato',
  } satisfies Record<ReservationStatus, string>,

  rental: {
    handOver: 'Consegna il veicolo',
    takeBack: 'Registra il rientro',
    title: 'Noleggio',
    checkedOut: 'Consegnato il {date} da {name}',
    returned: 'Rientrato il {date} da {name}',
    notReturned: 'Non ancora rientrato.',
    kmOut: 'Km alla consegna',
    kmIn: 'Km al rientro',
    kmDriven: 'Km percorsi',
    fuelOut: 'Carburante alla consegna',
    fuelIn: 'Carburante al rientro',
    contract: 'Contratto cartaceo',
    cargos: 'Ricevuta CaRGOS',
    override: 'Consegnato con l’autorizzazione del titolare: {reason}',
    notes: 'Note al rientro',
    handedOver: 'Veicolo consegnato.',
    takenBack: 'Veicolo rientrato.',
    fuelFull: 'Pieno (8/8)',
    fuelEmpty: 'Vuoto (0/8)',
    fuelEighths: '{level}/8',
  },

  checkout: {
    title: 'Consegna {code}',
    back: 'Prenotazione',
    vehicle: 'Veicolo che parte',
    chooseVehicle: 'Scegli il veicolo',
    taken: 'occupato',
    km: 'Chilometri',
    kmHint: 'Ultima lettura registrata: {km} km.',
    fuel: 'Carburante',
    readings: 'Chilometri e carburante',
    checks: 'Controlli al banco',
    documents: 'Ho visto il documento d’identità e la patente originali',
    contractTitle: 'Contratto',
    contractChooseVehicle: 'Scegli prima il veicolo: il contratto ne riporta la targa.',
    contractSigned: 'Firmato dal cliente sul dispositivo il {date}, per questo veicolo.',
    contractOtherVehicle:
      'Il contratto firmato riporta un altro veicolo: per questo va firmato di nuovo.',
    contractStale: 'La prenotazione è cambiata dopo la firma: il contratto va firmato di nuovo.',
    contractWaiting: 'Il cliente non ha ancora firmato.',
    turn: 'Gira il dispositivo verso il cliente per la firma',
    turnHint:
      'Il tuo accesso si chiude e il cliente vede solo il contratto. Dopo la firma sblocca di nuovo con il tuo PIN.',
    paper: 'Contratto cartaceo, se il dispositivo non si può usare',
    contract: 'Il cliente ha firmato il contratto cartaceo',
    contractHint: 'Modulo stampato con le condizioni versione {version}.',
    termsMissing:
      'Non ci sono condizioni in vigore: nessun veicolo può partire. Il titolare deve caricarle.',
    termsDraft:
      'Le condizioni in vigore sono una bozza: il contratto non si può firmare e nessun veicolo può partire. Il titolare deve caricare le condizioni definitive.',
    companyIncomplete:
      'Mancano i dati dell’azienda per il contratto (ragione sociale, sede, partita IVA): il contratto sul dispositivo non si può preparare.',
    paperNeedsOwner:
      'Il contratto firmato sul dispositivo è obbligatorio: con quello cartaceo la consegna richiede il titolare.',
    contractReference: 'Numero del contratto',
    deposit: 'Deposito cauzionale',
    moneyHint: 'Registra il pagamento dalla prenotazione, dopo la consegna.',
    blocked: 'La consegna è bloccata:',
    /** S6: what no one overrides, the Owner included (decisions 1 and 5). */
    mustDo: 'Prima della consegna:',
    mustDoHint: 'Nessuno può consegnare senza, nemmeno il titolare.',
    blocks: {
      identity: 'registra i dati del conducente, con la residenza',
      cargos: 'manca la ricevuta CaRGOS per questo veicolo',
      eligibility: 'il conducente non ha i requisiti',
      birth_date: 'la data di nascita sul documento non è quella della prenotazione',
      contract: 'manca il contratto firmato sul dispositivo per questo veicolo',
      deposit: 'manca il deposito preautorizzato',
      second_identity: 'registra i dati del secondo conducente',
      second_driver:
        'il secondo conducente non ha i requisiti, o il suo documento riporta un’altra data di nascita',
    },
    ownerOnly: 'Solo il titolare può consegnare lo stesso, con un motivo.',
    override: 'Autorizzo la consegna come titolare',
    overrideReason: 'Motivo',
    overrideReasonHint: 'Obbligatorio: resta nel registro delle modifiche.',
    opensAt: 'Il veicolo può partire dalle {time}.',
    submit: 'Consegna',
    /** UI-1d: the pane with the button, what it waits for and what is owed. */
    railTitle: 'Per consegnare',
    railDetails: 'Cosa blocca la consegna, e il conto',
    notConfirmed: 'Questa prenotazione non si consegna: parte solo una prenotazione confermata.',
    /** What the form still lacks, beside the button; the blocks have their own notices. */
    why: {
      terms: 'Le condizioni in vigore non permettono la consegna: vedi in alto.',
      late: 'L’orario di riconsegna prenotato è passato: questa prenotazione non può più partire.',
      vehicle: 'Scegli il veicolo che parte.',
      km: 'Scrivi i chilometri.',
      fuel: 'Scegli il livello del carburante.',
      reading: 'Un momento: si leggono le foto salvate su questo iPad.',
      unsent: 'Foto dell’ispezione non ancora inviate da questo iPad.',
      unsentReason: 'Scrivi perché si procede senza le foto, almeno 3 caratteri.',
      documents: 'Conferma di aver visto i documenti originali.',
      overrideReason: 'Scrivi il motivo dell’autorizzazione, almeno 3 caratteri.',
    },
  },

  /**
   * Q-162 (RES-3): the second driver, added at the counter before the
   * hand-over, at the fee the configuration sets, once per rental.
   */
  secondDriver: {
    title: 'Secondo conducente',
    none: 'Nessun secondo conducente: guida solo chi ha prenotato.',
    add: 'Aggiungi un secondo conducente',
    replace: 'Cambia secondo conducente',
    remove: 'Togli il secondo conducente',
    unavailable: {
      not_before_handover:
        'Si aggiunge o si toglie solo prima della consegna, su una prenotazione confermata.',
      terms_forbid:
        'Le condizioni in vigore riservano la guida a chi firma: non si può aggiungere un secondo conducente.',
      fee_not_configured:
        'Il supplemento per il secondo conducente non è configurato: non si può aggiungere.',
    },
    who: 'Chi guida',
    whoOptions: { existing: 'Cliente già registrato', new: 'Persona nuova' },
    licence: 'Patente',
    licenceHint:
      'Serve per verificare i requisiti; senza, restano da verificare. Al banco si registrano comunque i documenti originali.',
    licenceHintExisting:
      'Se ne ha già una registrata non serve riscriverla; una nuova si aggiunge alla sua scheda.',
    noLicence: 'Nessuna patente registrata',
    expires: 'scade il {date}',
    fee: 'Supplemento',
    feeRow: 'Secondo conducente',
    feeLine: 'Supplemento {amount}, una volta per noleggio: il totale diventa {total}.',
    feeFree: 'Nessun supplemento: il totale resta {total}.',
    voids:
      'Un contratto o una comunicazione a CaRGOS fatti prima non valgono più: si rifanno con il secondo conducente.',
    identity: 'Dati per la consegna',
    identityRecorded: 'registrati',
    identityMissing: 'da registrare alla consegna',
    ineligible: 'Non ha i requisiti per questa categoria:',
    ownerMay: 'Come titolare puoi lasciarlo guidare lo stesso, con un motivo.',
    ownerOnly: 'Solo il titolare può lasciarlo guidare lo stesso.',
    overrideReason: 'Motivo del titolare',
    overrideReasonHint: 'Obbligatorio: resta sulla prenotazione e nel registro delle modifiche.',
    overridden: 'Autorizzato dal titolare: {reason}',
    addedBy: 'Aggiunto da {name}, {date}.',
    save: 'Aggiungi',
    saveReplace: 'Cambia',
    cancel: 'Annulla',
    added: 'Secondo conducente aggiunto.',
    replaced: 'Secondo conducente cambiato.',
    unchanged: 'Era già il secondo conducente: nulla è cambiato.',
    removed: 'Secondo conducente tolto: il totale è tornato {total}.',
    removeTitle: 'Togliere il secondo conducente?',
    removeBody:
      '{name} non guiderà più: il supplemento di {amount} esce dal totale. Un contratto o una comunicazione a CaRGOS fatti prima si rifanno.',
    removeConfirm: 'Togli',
    /** What the form still lacks, beside its button. */
    why: {
      customer: 'Scegli il cliente.',
      firstName: 'Scrivi il nome.',
      lastName: 'Scrivi il cognome.',
      nationality: 'Scrivi la nazionalità con due lettere, o lasciala vuota.',
      phone: 'Correggi il telefono, o lascialo vuoto.',
      licenceNumber: 'Scrivi il numero di patente intero, almeno 3 caratteri.',
      licenceCountry: 'Scrivi il paese di rilascio della patente con due lettere.',
      overrideReason: 'Scrivi il motivo, almeno 3 caratteri.',
    },
  },

  /** S6 (decision 5): who takes the vehicle, read off the original documents. */
  identity: {
    title: 'Dati del conducente',
    /** Q-162: the second driver's, recorded the same way. */
    secondTitle: 'Dati del secondo conducente: {name}',
    secondIntro:
      'Come sui documenti originali del secondo conducente. Servono prima della consegna.',
    secondIntroCargos:
      'Come sui documenti originali del secondo conducente. Servono prima della consegna e, per questo veicolo, a CaRGOS: anche il telefono.',
    secondBirthDateDiffers:
      'Quando è stato aggiunto la data di nascita era {date}: se il documento dice altro, la consegna richiede il titolare.',
    secondPhoneHint:
      'Obbligatorio: CaRGOS lo chiede per il secondo conducente. Con il prefisso internazionale.',
    intro:
      'Come sui documenti originali. Servono a ogni consegna; la residenza va anche nel contratto.',
    introCargos:
      'Come sui documenti originali. Servono a ogni consegna e, per questo veicolo, a CaRGOS: la residenza va anche nel contratto.',
    recordedBy: 'Registrati da {name}, {date}.',
    edit: 'Correggi i dati',
    editAfterReceipt:
      'CaRGOS ha già la ricevuta per questi dati: correggendoli serve un nuovo invio, con il numero del documento e il luogo di rilascio scritti di nuovo.',
    cancel: 'Annulla',
    surname: 'Cognome',
    givenNames: 'Nome',
    namesHint: 'Come stampati sul documento.',
    birthDate: 'Data di nascita',
    birthDateDiffers:
      'Sulla prenotazione la data di nascita è {date}: se il documento dice altro, la consegna richiede il titolare.',
    birthPlace: 'Luogo di nascita',
    citizenship: 'Cittadinanza',
    residence: 'Residenza',
    address: 'Via e numero',
    postalCode: 'CAP',
    noPostalCode: 'Il paese non usa il CAP',
    city: 'Città',
    country: 'Paese',
    residencePlace: 'Comune di residenza, o lo stato se all’estero',
    addressSource: 'L’indirizzo è',
    sources: {
      document: 'scritto sul documento',
      declared: 'dichiarato dal cliente (il documento non lo riporta)',
    },
    document: 'Documento d’identità',
    documentType: 'Tipo',
    documentNumber: 'Numero',
    documentNumberHint:
      'Non viene mai precompilato: si cancella da solo quando CaRGOS ha la ricevuta.',
    documentPlace: 'Rilasciato a',
    licence: 'Patente',
    licenceNumber: 'Numero',
    licencePlace: 'Rilasciata a',
    phone: 'Telefono',
    phoneHint: 'Con il prefisso internazionale, per esempio +39 333 1234567.',
    save: 'Registra i dati',
    /** UI-1d: what the form still lacks, beside its button, in the page's order. */
    why: {
      surname: 'Scrivi il cognome.',
      givenNames: 'Scrivi il nome.',
      birthDate: 'Scrivi la data di nascita.',
      birthPlace: 'Scegli il luogo di nascita.',
      citizenship: 'Scegli la cittadinanza.',
      address: 'Scrivi la via e il numero, almeno 3 caratteri.',
      postalCode: 'Scrivi il CAP, o spunta che il paese non lo usa.',
      city: 'Scrivi la città.',
      country: 'Scegli il paese.',
      residencePlace: 'Scegli il comune di residenza, o lo stato se all’estero.',
      addressSource: 'Indica se l’indirizzo è scritto sul documento o dichiarato dal cliente.',
      documentType: 'Scegli il tipo di documento d’identità.',
      documentNumber: 'Scrivi il numero del documento d’identità.',
      documentPlace: 'Scegli dove è stato rilasciato il documento d’identità.',
      licenceNumber: 'Scrivi il numero della patente.',
      licencePlace: 'Scegli dove è stata rilasciata la patente.',
      phone: 'Correggi il telefono, o lascialo vuoto.',
      phoneRequired: 'Scrivi il telefono del secondo conducente: CaRGOS lo chiede.',
    },
    saved: 'Dati registrati.',
    unchanged: 'Questi dati erano già registrati.',
    erased: 'numero e luogo di rilascio cancellati il {date}',
    problems: 'CaRGOS rifiuterebbe questi dati: correggili.',
    problem: {
      missing: 'manca',
      too_long: 'troppo lungo',
      too_short: 'troppo corto',
      charset: 'contiene caratteri non ammessi',
      unknown_code: 'non è nelle tabelle di CaRGOS',
      place_kind: 'serve un comune italiano, o uno stato',
      dissolved_code: 'non esiste più',
      pair_incomplete: 'incompleto',
      driver2_incomplete: 'incompleto',
    } satisfies Record<CargosProblemCode, string>,
    phoneInvalid: 'Scrivi il numero con il prefisso internazionale, solo cifre dopo il +.',
  },

  /** S6: a place from CaRGOS's tables, for the identity form. */
  places: {
    search: 'Cerca un luogo',
    searchHint: 'Almeno 2 lettere del nome.',
    none: 'Nessun luogo con questo nome.',
    searching: 'Ricerca…',
    found: { one: '{count} luogo trovato', other: '{count} luoghi trovati' },
    change: 'Cambia',
    gone: 'non esiste più dal {date}',
    state: 'stato',
  },

  /** S6 (CMP-1, decision 1): the report to CaRGOS, and its receipt. */
  cargos: {
    title: 'CaRGOS',
    notRequired: 'Questa categoria non si comunica a CaRGOS.',
    pending:
      'L’invio da quest’app attende le credenziali: per ora invia il contratto dal portale CaRGOS, o via PEC, e registra qui la ricevuta.',
    covered: 'Ricevuta {receipt} per {plate}: il veicolo può partire.',
    cover: {
      none: 'Nessuna ricevuta per questa consegna.',
      other_vehicle: 'La ricevuta è per un altro veicolo: questo va comunicato di nuovo.',
      booking_changed: 'La prenotazione è cambiata dopo la ricevuta: va comunicata di nuovo.',
      data_changed:
        'I dati del conducente sono cambiati dopo la ricevuta: vanno comunicati di nuovo.',
    },
    needIdentity: 'Registra prima i dati del conducente.',
    needIdentityCargos: 'Registra di nuovo i dati del conducente, con quelli per CaRGOS.',
    needVehicle: 'Scegli prima il veicolo.',
    sendTitle: 'Comunicazione dall’app',
    paymentType: 'Pagamento (tabella CaRGOS)',
    choosePaymentType: 'Scegli come paga il cliente',
    /** UI-1d: what the receipt and the report wait for, beside their buttons. */
    why: {
      receipt: 'Scrivi il numero di ricevuta.',
      paymentType: 'Scegli come paga il cliente.',
      contract:
        'Prima il contratto: firmato sul dispositivo, oppure il cartaceo firmato e spuntato qui sopra.',
      portal: 'Spunta di aver controllato sul portale CaRGOS che la comunicazione non c’è.',
    },
    checkMode:
      'Modalità di prova: CaRGOS controlla la comunicazione ma non la registra. Per far partire il veicolo invia dal portale e registra qui la ricevuta.',
    send: 'Invia a CaRGOS',
    sendAgain: 'Invia di nuovo',
    portalChecked: 'Ho controllato sul portale CaRGOS: questa comunicazione non c’è.',
    check: 'Verifica con CaRGOS (prova)',
    queued: 'Invio a CaRGOS in corso…',
    retrying: 'Tentativo {tries} non riuscito: CaRGOS non raggiunto. Nuovo tentativo alle {time}.',
    lastReason: 'Motivo: {reason}',
    stuck:
      'L’invio si è fermato: avvisa il titolare (Sistema). Intanto invia dal portale CaRGOS e registra qui la ricevuta.',
    checked:
      'Prova riuscita: CaRGOS accetterebbe questa comunicazione. La prova non vale come comunicazione.',
    unknown:
      'Esito sconosciuto: la comunicazione potrebbe essere arrivata a CaRGOS. Controlla sul portale. Se c’è, registra qui sotto la sua ricevuta; se non c’è, invia di nuovo (stesso numero di contratto).',
    refused: 'CaRGOS ha rifiutato la comunicazione. Correggi i dati e invia di nuovo.',
    /** UI-1d (C5): CaRGOS's own description, quoted; its code is for Dettagli tecnici. */
    refusedSaid: 'Il motivo, nelle parole di CaRGOS: «{text}»',
    refusedUnexplained:
      'CaRGOS ha rifiutato la comunicazione senza dire perché. Controlla i dati e invia di nuovo; se la rifiuta ancora, avvisa il titolare.',
    refusedRecord:
      'CaRGOS non accetterebbe più questa comunicazione: uno dei codici che usa non è più in vigore. Registra di nuovo i dati del conducente, poi invia; se la rifiuta ancora, avvisa il titolare.',
    technicalCode: 'Codice: {code}',
    cancelled: {
      default: 'Comunicazione annullata: è stata registrata una ricevuta dal portale o dalla PEC.',
      booking_status: 'Comunicazione annullata: la prenotazione non è più confermata.',
      booking_changed: 'Comunicazione annullata: la prenotazione è cambiata. Invia di nuovo.',
      cargos_not_required:
        'Comunicazione annullata: CaRGOS non si applica più a questa prenotazione.',
      identity_changed:
        'Comunicazione annullata: i dati del conducente sono stati registrati di nuovo. Invia di nuovo.',
      vehicle_not_in_category: 'Comunicazione annullata: il veicolo non è più di questa categoria.',
      vehicle_still_out: 'Comunicazione annullata: il veicolo è fuori con un’altra prenotazione.',
      not_reached:
        'Comunicazione annullata: CaRGOS non è stato raggiunto per ore. Invia di nuovo, o dal portale.',
    },
    receiptTitle: 'Registra una ricevuta (portale o PEC)',
    // S9: on a trip, the portal's or the PEC's receipt is the office's to type.
    officeReceipt:
      'La ricevuta del portale o della PEC la registra l’ufficio: chiedila all’ufficio. Compare qui appena è registrata.',
    channel: 'Ricevuta da',
    channels: { api: 'app', portal: 'portale CaRGOS', pec: 'PEC' },
    receipt: 'Numero di ricevuta',
    record: 'Registra la ricevuta',
    recorded:
      'Ricevuta registrata: il numero del documento e il luogo di rilascio sono stati cancellati.',
    history: 'Comunicazioni',
    entry: '{contract} · {plate} · {status}',
    status: {
      queued: 'In coda',
      checked: 'Verificato (prova)',
      sent: 'Inviato',
      refused: 'Rifiutato',
      unknown: 'Esito sconosciuto',
      cancelled: 'Annullato',
      recorded: 'Registrato',
    },
  },

  documents: {
    none: 'Nessun documento registrato.',
    fromCustomer: 'inviato dal cliente',
    atCounter: 'fotografato al banco',
    open: 'Apri',
    openLabel: 'Apri {document}',
    openHint: 'Ogni apertura di un documento viene registrata.',
    capture: 'Fotografa un documento',
    kind: 'Documento',
    side: 'Lato',
    photo: 'Scatta la foto',
    photoHint:
      'Sul dispositivo si apre la fotocamera. La foto viene ridotta e salvata senza la posizione.',
    preview: 'Anteprima: {document}',
    // S8: a document's photo erased (retention, or with its customer's identity).
    erased: 'Foto cancellata il {date}',
    save: 'Salva la foto',
    saving: 'Salvataggio…',
    saved: '{document}: foto salvata.',
    unreadable: 'Questa foto non si legge: scattala di nuovo.',
    choosePhoto: 'Scatta o scegli prima una foto.',
    pdfNotInApp:
      'È un PDF, e ora i PDF non si aprono in quest’app: chiudila e riaprila, oppure apri il PDF da un computer.',
  },

  /** A1: photos and documents shown by the iPad app from its own memory. */
  files: {
    close: 'Chiudi',
    photoUnavailable: 'Foto non disponibile',
  },

  inspection: {
    title: 'Ispezione del veicolo',
    introCheckout:
      'Prima che parta: fotografa il veicolo zona per zona e segna i danni che ci sono già. Non si addebitano al cliente.',
    introReturn:
      'Al rientro: le foto della consegna sono accanto a quelle di ora. Un danno nuovo diventa un addebito, con l’importo che scrivi tu.',
    introClosed: 'Le foto della consegna e del rientro, e i danni registrati su questo veicolo.',
    chooseVehicle: 'Scegli prima il veicolo che parte.',
    diagram: 'Schema di {plate}',
    zones: {
      front: 'Anteriore',
      left: 'Lato sinistro',
      right: 'Lato destro',
      rear: 'Posteriore',
      roof: 'Tetto',
      interior: 'Interni',
      seat: 'Sella',
    },
    photoCount: { one: '{count} foto', other: '{count} foto' },
    damageCount: { one: '{count} danno', other: '{count} danni' },
    zoneLabel: '{zone}: {photos}, {damages}',
    atCheckout: 'Alla consegna',
    atReturn: 'Al rientro',
    noPhotos: 'Nessuna foto.',
    // S8: photos erased since, where they were, and when.
    erasedPhotos: {
      one: '{count} foto cancellata {when}.',
      other: '{count} foto cancellate {when}.',
    },
    erasedSummary: {
      one: '{count} foto di questa prenotazione è stata cancellata {when}.',
      other: '{count} foto di questa prenotazione sono state cancellate {when}.',
    },
    erasedOnDay: 'il {date}',
    erasedBetween: 'tra il {from} e il {to}',
    photoAlt: '{zone}, {stage}, foto {number}',
    damagePhotoAlt: 'Danno, {zone}: {description}',
    takePhoto: 'Aggiungi una foto',
    addPhoto: 'Aggiungi una foto: {zone}',
    unreadable: 'Questa foto non si legge: scattala di nuovo.',
    damagesTitle: 'Danni su questo veicolo',
    noDamages: 'Nessun danno registrato.',
    existing: 'già presente',
    newHere: 'nuovo a questo rientro',
    foundOn: 'segnato il {date}',
    charged: 'addebitato {amount}',
    markLabel: 'Nuovo danno: {zone}',
    markHintCheckout: 'Descrivi il danno che c’è già, poi fotografalo.',
    markHintReturn: 'Descrivi il danno nuovo, poi fotografalo: si aggiunge agli addebiti.',
    mark: 'Segna il danno',
    describe: 'Scrivi che danno è.',
    damagePhoto: 'Aggiungi una foto del danno: {description}',
    charge: 'Aggiungi agli addebiti',
    chargeLabel: 'Aggiungi agli addebiti: {description}',
    chargesFull: 'Gli addebiti sono già 20: togline uno per aggiungere questo danno.',
    inCharges: 'Negli addebiti',
    chargeDescription: '{zone}: {description}',
  },

  /** S4 (OPS-5): the inspection's photos and damages, kept on the iPad until the server has them. */
  capture: {
    photoStates: {
      queued: 'In attesa',
      sending: 'Invio…',
      sent: 'Inviata',
      refused: 'Non inviata',
    },
    damageStates: {
      queued: 'In attesa',
      sending: 'Invio…',
      sent: 'Inviato',
      refused: 'Non inviato',
    },
    noConnection: 'Senza connessione: parte appena torna.',
    serverBusy: 'Il server non risponde: si riprova da solo.',
    waitsFor: 'Parte quando {name} sblocca il dispositivo.',
    waitsForDamage: 'Parte dopo il danno che mostra.',
    retry: 'Riprova',
    discard: 'Scarta',
    discardTitle: {
      one: 'Scartare {count} elemento non inviato?',
      other: 'Scartare {count} elementi non inviati?',
    },
    discardBody:
      'Non arriva al server e non si recupera. Un danno scartato porta con sé le sue foto.',
    keep: 'Tieni',
    full: 'Sul dispositivo non c’è più spazio: invia o scarta quello che aspetta, poi riprova.',
    notDurable: 'Questo browser non conserva le foto: restano solo finché la pagina resta aperta.',
    limitPhotos: 'Questa ispezione ha già il numero massimo di foto.',
    limitDamages: 'Questa ispezione ha già il numero massimo di danni.',
    saved: 'Foto salvata sul dispositivo.',
    damageSaved: 'Danno salvato sul dispositivo: {description}.',
    unsent: {
      one: '{count} elemento dell’ispezione non è ancora arrivato al server da questo iPad.',
      other: '{count} elementi dell’ispezione non sono ancora arrivati al server da questo iPad.',
    },
    waitCheckout: 'Il veicolo parte quando sono arrivati.',
    waitReturn: 'Il rientro si registra quando sono arrivati.',
    theirs: {
      one: '{count} è di {name}: parte quando {name} sblocca il dispositivo.',
      other: '{count} sono di {name}: partono quando {name} sblocca il dispositivo.',
    },
    discardMine: 'Scarta i miei non inviati',
    ownerProceed: 'Procedi senza (solo il titolare)',
    ownerReason: 'Perché si procede senza',
    ownerReasonHint: 'Resta nel registro, con il numero di elementi lasciati sul dispositivo.',
    elsewhere: {
      one: '{count} elemento di un altro veicolo di questa prenotazione non è inviato: non blocca la consegna.',
      other:
        '{count} elementi di altri veicoli di questa prenotazione non sono inviati: non bloccano la consegna.',
    },
    reading: 'Lettura di quello che è salvato sul dispositivo…',
    indicator: 'In attesa: {count}',
    /** UI-1e: said with the count when something was refused, never by colour alone. */
    indicatorRefused: { one: '{count} rifiutato', other: '{count} rifiutati' },
    /** What the button does, said after its words (its name is what it shows). */
    indicatorHint: 'Non ancora inviati da questo iPad: apri l’elenco.',
    listTitle: 'In attesa su questo iPad',
    listEmpty: 'Niente in attesa.',
    photoOf: 'Foto · {zone}',
    damageOf: 'Danno · {zone}: {description}',
    where: '{plate} · {code} · {stage}',
    takenBy: '{name}, {time}',
    stale:
      'Alcuni elementi dell’ispezione aspettano da più di 15 minuti. Controlla la connessione, o chi deve sbloccare il dispositivo.',
    staleLink: 'Vedi l’elenco',
    close: 'Chiudi',
    kept: {
      one: '{count} elemento dell’ispezione è salvato su questo iPad: parte dopo lo sblocco.',
      other:
        '{count} elementi dell’ispezione sono salvati su questo iPad: partono dopo lo sblocco.',
    },
    offline: 'Senza connessione: per sbloccare serve la rete.',
  },

  /** S3: a booking cancelled, marked a no-show (RES-2, RES-5), or closed (Q-13). */
  lifecycle: {
    cancel: 'Annulla la prenotazione',
    noShow: 'Cliente non presentato',
    close: 'Chiudi la prenotazione',
    cancelTitle: 'Annullare la prenotazione {code}?',
    noShowTitle: 'Il cliente di {code} non si è presentato?',
    closeTitle: 'Chiudere la prenotazione {code}?',
    closeBody:
      'Il veicolo è rientrato e il conto è saldato. Una prenotazione chiusa non registra più pagamenti.',
    back: 'Indietro',
    confirmCancel: 'Annulla la prenotazione',
    confirmNoShow: 'Conferma: non presentato',
    confirmClose: 'Chiudi la prenotazione',
    pickup: 'Ritiro prenotato: {date}.',
    hoursLeft: {
      one: 'Manca {count} ora al ritiro.',
      other: 'Mancano {count} ore al ritiro.',
    },
    underAnHour: 'Manca meno di un’ora al ritiro.',
    pickupPassed: 'L’orario di ritiro è già passato.',
    carFreed: 'Il veicolo torna subito disponibile per altre prenotazioni.',
    policyFee: 'Penale prevista dalle condizioni {version}: {amount}.',
    unconfirmedNoFee:
      'La prenotazione non è ancora confermata (il cliente non ha pagato la sua parte online): si annulla senza penale.',
    tier: {
      one: 'Vale per gli annullamenti fatti almeno {count} ora prima del ritiro.',
      other: 'Vale per gli annullamenti fatti almeno {count} ore prima del ritiro.',
    },
    lastTier: 'Vale per gli annullamenti più vicini al ritiro, e dopo.',
    notAccepted:
      'Il cliente non ha accettato queste condizioni online: la prenotazione è stata fatta al banco o al telefono.',
    feeChoice: 'Penale',
    feePolicy: 'Trattieni la penale prevista: {amount}',
    feeOverride: 'Riduci la penale',
    overrideAmount: 'Penale ridotta (€)',
    overrideAmountHint: 'Meno di {amount}.',
    overrideTooHigh: 'Scrivi un importo più basso di {amount}, per esempio 20 o 20,50.',
    overrideReason: 'Motivo della riduzione',
    overrideReasonHint: 'Obbligatorio: resta nel registro delle modifiche.',
    noPolicy:
      'Le condizioni di questa prenotazione non dicono nulla sull’annullamento: scrivi tu quanto si trattiene.',
    typedAmount: 'Penale trattenuta (€)',
    typedAmountHint: 'Da 0 a {amount}. Scrivi 0 se non si trattiene nulla.',
    amountInvalid: 'Scrivi un importo da 0 a {amount}, per esempio 20 o 20,50.',
    reason: 'Motivo dell’annullamento',
    reasonHint: 'Obbligatorio: resta sulla prenotazione.',
    reasonShort: 'Scrivi almeno 3 caratteri.',
    note: 'Note',
    paidSoFar: 'Pagato finora: {amount}.',
    afterOwed: 'Dopo: resta da pagare {amount}, al banco.',
    afterCredit: 'Dopo: da restituire al cliente {amount}.',
    afterSettled: 'Dopo: il conto è saldato.',
    blocks: {
      status: 'La prenotazione non è più confermata: controlla com’è ora.',
      too_early: 'Il cliente si segna come non presentato dall’orario di ritiro, {time}.',
      deposit: 'C’è un deposito in corso: prima rilascialo, dal riquadro Deposito.',
    } satisfies Record<CancellationBlock, string>,
    closeBlocked: 'Per chiudere la prenotazione:',
    closeBlocks: {
      status: 'il veicolo deve essere rientrato',
      balance: 'il conto va saldato',
      deposit: 'il deposito va rilasciato o trattenuto',
      refund: 'il rimborso in corso deve concludersi',
      dispute: 'la contestazione della banca del cliente deve concludersi',
    } satisfies Record<CloseBlock, string>,
    done: {
      cancelled: 'Prenotazione annullata.',
      no_show: 'Segnata come cliente non presentato.',
      closed: 'Prenotazione chiusa.',
    },
    paidOnlineMeanwhile:
      'Il cliente aveva appena pagato online: il pagamento è registrato e il conto mostra quanto restituire.',
    ended: {
      cancelled: 'Annullata il {date} da {name}.',
      no_show: 'Cliente non presentato: segnato il {date} da {name}.',
      closed: 'Chiusa il {date} da {name}.',
    },
    endedAt: {
      cancelled: 'Annullata il {date}.',
      no_show: 'Cliente non presentato: segnato il {date}.',
      closed: 'Chiusa il {date}.',
    },
    reasonLine: 'Motivo: {reason}',
    /** UI-1e: what the dialog still waits for, under its buttons. */
    why: {
      blocked: 'Prima quello che è elencato sopra.',
      fee: 'Scrivi la penale trattenuta, 0 se non si trattiene nulla.',
      lowerFee: 'Scrivi la penale ridotta.',
      feeReason: 'Scrivi il motivo della riduzione, almeno 3 caratteri.',
      reason: 'Scrivi il motivo dell’annullamento, almeno 3 caratteri.',
      note: 'Scrivi almeno 3 caratteri nelle note, o lasciale vuote.',
    },
  },

  deposit: {
    title: 'Deposito cauzionale',
    amount: 'Deposito previsto per questa categoria: {amount}.',
    unset: 'Importo non ancora deciso per questa categoria.',
    none: 'Questa categoria non prevede un deposito.',
    required: 'Serve un deposito preautorizzato per consegnare il veicolo.',
    readerPending: 'Lettore di carte: prendi il deposito sul POS del negozio e registralo qui.',
    readerPendingOrCash:
      'Lettore di carte: prendi il deposito sul POS del negozio o in contanti e registralo qui.',
    method: 'Come prendi il deposito',
    methods: {
      reader: 'Carta, sul lettore',
      pos: 'Carta, sul POS del negozio',
      cash: 'Contanti',
    } satisfies Record<DepositMethod, string>,
    reference: 'Numero dello scontrino del POS',
    take: 'Prendi il deposito di {amount}',
    takeTitle: 'Prendi il deposito',
    waiting: 'Il lettore aspetta la carta del cliente ({amount}).',
    simulated:
      'Lettore simulato di Stripe, in modalità di test: nessuna carta vera viene addebitata.',
    simulate: 'Simula la carta del cliente (test)',
    check: 'Controlla con Stripe',
    stopWaiting: 'Annulla sul lettore',
    held: {
      reader: 'Preautorizzati {amount} sulla carta {brand}.',
      pos: 'Preautorizzati {amount} sul POS del negozio.',
      cash: 'Deposito in contanti: {amount}.',
    } satisfies Record<DepositMethod, string>,
    posReference: 'Scontrino del POS: {reference}.',
    captureBefore: 'La preautorizzazione scade il {date}.',
    lapses:
      'Scade prima della riconsegna del {date}: rifalla prima della scadenza, oppure prendi il deposito in contanti o sul POS.',
    debit: 'Carta di debito: i soldi del cliente restano bloccati sul conto fino al rilascio.',
    takenBy: 'Preso da {name} il {date}.',
    release: 'Rilascia il deposito',
    releaseCash: 'Restituisci i contanti',
    keepTitle: 'Trattieni dal deposito',
    keepAmount: 'Importo da trattenere (€)',
    keepAmountHint: 'Al massimo {amount}: quanto resta da pagare, entro il deposito.',
    keepReason: 'Motivo',
    keepReasonHint: 'Obbligatorio: resta nel registro delle modifiche.',
    keep: 'Trattieni',
    keepNothing: 'Non resta nulla da pagare: il deposito si rilascia.',
    /** D11: what keeping part of the deposit does, asked before it is done. */
    keepConfirm: {
      title: 'Trattenere {amount} dal deposito?',
      payment: 'Si registra un pagamento di {amount} dal deposito.',
      released: 'Il resto, {amount}, si rilascia.',
      cash: 'Il resto, {amount}, lo restituisci al cliente in contanti.',
      final: 'Non si può annullare.',
      confirm: 'Trattieni {amount}',
    },
    /** UI-1c: what still stops the deposit's buttons, beside them. */
    why: {
      method: 'Scegli come prendi il deposito.',
      drawer: 'Per i contanti serve un cassetto aperto.',
      amount: 'Scrivi l’importo da trattenere.',
      overMax: 'Al massimo {amount}.',
      reason: 'Scrivi il motivo, almeno 3 caratteri.',
      drawerBack: 'Per restituire il resto in contanti serve un cassetto aperto.',
    },
    ended: {
      released: 'Deposito rilasciato il {date}.',
      captured: 'Trattenuti {kept} il {date}: {reason}. Il resto è stato rilasciato.',
      capturedCash: 'Trattenuti {kept} il {date}: {reason}. Restituisci al cliente {rest}.',
      expired: 'La preautorizzazione è scaduta il {date}: il deposito non c’è più.',
      failed: 'La carta non è stata preautorizzata: {why}. Riprova, o scegli un altro modo.',
    },
    /** Why, in words (deposit/decline.ts): a card reported lost or stolen is a plain refusal. */
    declined: {
      funds: 'non ha abbastanza disponibilità',
      expired: 'è scaduta',
      pin: 'il lettore ha chiesto il PIN, o il PIN era sbagliato',
      temporary: 'un errore temporaneo della banca o del lettore',
      cancelled: 'l’operazione è stata annullata prima di finire',
      declined: 'la banca l’ha rifiutata',
    },
    done: {
      taken: 'Deposito registrato.',
      released: 'Deposito rilasciato.',
      kept: 'Importo trattenuto dal deposito e registrato come pagamento.',
    },
  },

  giveBack: {
    title: 'Rientro {code}',
    km: 'Chilometri al rientro',
    kmHint: 'Alla consegna: {km} km.',
    fuel: 'Carburante al rientro',
    fuelHint: 'Alla consegna: {level}.',
    /** UI-1d: the server adds it only at a price fixed at the hand-over (rentals.ts). */
    fuelCharge:
      'Meno carburante della consegna: si aggiunge il rifornimento, se le condizioni ne fissano il prezzo.',
    excessKmDriven: 'Percorsi: {km} km',
    excessKmIncluded: 'Inclusi: {km} km',
    excessKmOver: 'Oltre il limite: {km} km',
    excessKmCharge: 'Addebito km eccedenti:',
    waiveExcessKm: 'Annulla l’addebito dei km eccedenti',
    notes: 'Note',
    notesHint: 'Altro da annotare. I danni si segnano nell’ispezione, con le foto.',
    charges: 'Altri addebiti',
    noCharges: 'Nessun altro addebito.',
    addCharge: 'Aggiungi un addebito',
    removeCharge: 'Togli addebito {number}',
    chargeNumber: 'Addebito {number}',
    chargesFull: 'Al massimo {max} addebiti per rientro: togline uno per aggiungerne un altro.',
    chargeKind: 'Tipo',
    chargeDescription: 'Descrizione',
    chargeAmount: 'Importo (€)',
    submit: 'Registra il rientro',
    /** UI-1d: the vehicle's section, and the rail with the button and what it waits for. */
    readings: 'Il veicolo al rientro',
    railTitle: 'Per il rientro',
    railDetails: 'Il conto',
    moneyHint:
      'Gli addebiti del rientro si aggiungono al conto quando lo registri; il conto si regola dalla prenotazione.',
    notOut: 'Questa prenotazione non ha un veicolo fuori da riprendere.',
    alreadyBack: 'Il veicolo è già rientrato il {date}.',
    /** What the form still lacks, beside the button. */
    why: {
      km: 'Scrivi i chilometri.',
      kmBelow: 'Controlla i chilometri: sono meno di quelli alla consegna.',
      fuel: 'Scegli il livello del carburante.',
      reading: 'Un momento: si leggono le foto salvate su questo iPad.',
      unsent: 'Foto dell’ispezione non ancora inviate da questo iPad.',
      unsentReason: 'Scrivi perché si procede senza le foto, almeno 3 caratteri.',
      charges: 'Completa ogni addebito: descrizione e importo.',
    },
  },

  chargeKinds: {
    fuel: 'Rifornimento',
    late: 'Ritardo',
    cleaning: 'Pulizia',
    damage: 'Danni',
    other: 'Altro',
    fine_fee: 'Gestione multa',
    excess_km: 'Chilometri eccedenti',
  } satisfies Record<RentalChargeKind, string>,

  money: {
    title: 'Conto',
    total: 'Prenotazione',
    paid: 'Pagato',
    balance: 'Da pagare',
    settled: 'Saldato',
    credit: 'Da restituire',
    fee: 'Penale trattenuta',
    feeRules: {
      policy: 'dalle condizioni {version}',
      override: 'ridotta: {note}',
      typed: 'scritta al banco',
      legacy: 'pari al totale: annullata prima che esistessero le penali',
    } satisfies Record<CancellationFeeRule, string>,
    endedTotal: 'La prenotazione valeva {amount}: ora è dovuta solo la penale.',
    refunded: 'Rimborsato',
    lostInDisputes: 'Restituito dalla banca (contestazione)',
    creditHint: 'È stato pagato più del dovuto: la differenza va restituita al cliente.',
    onlineBy: 'Stripe',
    payments: 'Pagamenti',
    noPayments: 'Nessun pagamento registrato.',
    paymentLine: '{amount}, {method}, {date}, {name}',
  },

  /** S5: disputes of a booking's payments by the customer's bank. */
  disputes: {
    title: 'Contestazioni della banca',
    status: {
      warning_needs_response: 'Richiesta della banca: va data una risposta',
      warning_under_review: 'Richiesta della banca: in esame',
      warning_closed: 'Richiesta della banca: chiusa',
      needs_response: 'Contestazione: va data una risposta',
      under_review: 'Contestazione: in esame',
      won: 'Contestazione vinta',
      lost: 'Contestazione persa',
      prevented: 'Contestazione evitata',
    } satisfies Record<KnownDisputeStatus, string>,
    /** The Owner's CSV and e-mail keep Stripe's own status, as the Owner's records (UI-2e). */
    unknownStatus: 'Stato di Stripe: {status}',
    statusUnknown: 'Stato nuovo, da leggere nella Dashboard di Stripe',
    otherReason: 'altro motivo',
    reasons: {
      fraudulent: 'il cliente dice di non aver fatto il pagamento',
      duplicate: 'pagamento doppio',
      product_not_received: 'servizio non ricevuto',
      product_unacceptable: 'servizio non conforme',
      credit_not_processed: 'rimborso non ricevuto',
      unrecognized: 'pagamento non riconosciuto',
      subscription_canceled: 'abbonamento annullato',
      general: 'motivo generico',
    } as Record<string, string>,
    line: '{amount}, {reason}, aperta il {date}',
    answerBy: 'Rispondi nella Dashboard di Stripe entro il {date}.',
    openHint:
      'Finché la banca decide non si rimborsa nulla di questo pagamento, in nessun modo, e la prenotazione non si chiude.',
    returnedHint:
      'La banca ha restituito i soldi al cliente: quanto è di nuovo dovuto si incassa solo al banco, mai con un link.',
  },

  payment: {
    title: 'Registra un pagamento',
    method: 'Metodo',
    amount: 'Importo (€)',
    amountInvalid: 'Scrivi un importo, per esempio 70 o 70,50.',
    reference: 'Riferimento',
    referenceHint: 'Lo scontrino del POS o il riferimento del bonifico.',
    submit: 'Registra il pagamento',
    /** UI-1c: what still stops the payment, beside its button. */
    why: {
      method: 'Scegli come paga il cliente.',
      amount: 'Scrivi l’importo.',
      overBalance: 'Al massimo quanto resta da pagare: {amount}.',
      drawer: 'Per i contanti serve un cassetto aperto.',
    },
    done: 'Pagamento registrato.',
    paidOnline:
      'Il cliente ha appena pagato online: il pagamento è nell’elenco qui sopra. Non incassare di nuovo.',
    pageOpen:
      'Stripe non ha risposto in tempo: la pagina di pagamento online del cliente resta aperta fino alle {time}. Se paga anche lì, il conto mostrerà quanto restituire.',
  },

  /** S3 (PAY-3): money given back, the way it came. */
  refund: {
    title: 'Restituisci al cliente',
    intro: 'Va restituito al cliente {amount}. I soldi tornano come sono arrivati.',
    payment: 'Da quale pagamento',
    paymentOption: '{amount}, {method}, {date}: restituibili {left}',
    method: 'Come',
    methods: {
      cash: 'In contanti',
      card_terminal: 'Sulla carta, con il POS del negozio',
      bank_transfer: 'Con un bonifico',
      stripe: 'Con Stripe, sulla carta usata online',
    } satisfies Record<RefundMethod, string>,
    amount: 'Importo (€)',
    amountHint: 'Al massimo {amount}.',
    amountInvalid: 'Scrivi un importo fino a {amount}, per esempio 20 o 20,50.',
    reason: 'Motivo',
    reasonHint: 'Obbligatorio: resta nel registro delle modifiche.',
    reasonShort: 'Scrivi almeno 3 caratteri.',
    reference: 'Riferimento',
    referenceHint: 'Lo scontrino del POS o il riferimento del bonifico.',
    submit: 'Registra la restituzione',
    submitStripe: 'Restituisci con Stripe',
    /** D11: the refund said back, before it is made. */
    confirm: {
      title: 'Restituire {amount} al cliente?',
      final: 'Non si può annullare.',
      confirm: 'Restituisci {amount}',
    },
    /** UI-1c: what still stops the refund, beside its button. */
    why: {
      payment: 'Scegli da quale pagamento.',
      method: 'Scegli come restituire.',
      amount: 'Scrivi l’importo.',
      overMax: 'Al massimo {amount}.',
      reason: 'Scrivi il motivo, almeno 3 caratteri.',
      drawer: 'Per restituire in contanti serve un cassetto aperto.',
    },
    done: 'Restituzione registrata.',
    sent: 'Rimborso chiesto a Stripe: parte tra pochi secondi.',
    noPayment: 'Nessun pagamento di questa prenotazione si può restituire da qui.',
    ownerOnly:
      'La restituzione la fa il titolare: la prenotazione è nell’elenco «Da restituire» di Oggi.',
    list: 'Restituzioni',
    line: '{amount}, {method}, {date}',
    by: 'da {name}',
    byStripe: 'dal pannello di Stripe',
    states: {
      pending: 'In corso con Stripe',
      requires_action: 'Stripe chiede un’azione: aprila nel pannello di Stripe',
      succeeded: 'Restituito',
      failed: 'Non riuscito: va restituito in un altro modo',
      canceled: 'Annullato',
    } satisfies Record<RefundState, string>,
    superseded: 'Non inviato: era già stato restituito in un altro modo.',
    notSent: 'Non inviato: Stripe non l’ha mai ricevuto. Chiedilo di nuovo.',
    sync: 'Controlla con Stripe',
    synced: 'Controllato con Stripe.',
  },

  /** S6 (CMP-3, decision 3): the fiscal documents the till prints, and its daily closure (Z). */
  /**
   * S6 (CMP-2): the letter naming a fine's renter to the authority, rendered
   * in Italian for every authority. The wording is to be checked with the
   * lawyer (Q-115); it says what the records show, nothing more.
   */
  fineLetter: {
    title: 'Comunicazione dei dati del conducente',
    to: 'Spett.le {authority}',
    subject:
      'Oggetto: verbale n. {number}, notificato il {notified}. Comunicazione dei dati del conducente (artt. 126-bis e 196 del Codice della strada).',
    body: 'In riferimento al verbale in oggetto, relativo alla violazione accertata il {date} alle ore {time} in {place}, con il veicolo targato {plate} ({model}), la società sotto indicata comunica che in quel momento il veicolo era concesso in locazione senza conducente alla persona sotto indicata, dal {from} al {to} (prenotazione {code}{contract}).',
    stillOut: 'noleggio in corso',
    contractSigned: ', contratto firmato il {date}',
    contractPaper: ', contratto n. {reference}',
    sections: {
      company: 'Società locatrice',
      renter: 'Conducente (locatario)',
    },
    fields: {
      name: 'Denominazione',
      address: 'Sede',
      vatNumber: 'Partita IVA',
      surname: 'Cognome',
      givenNames: 'Nome',
      birthDate: 'Data di nascita',
      birthPlace: 'Luogo di nascita',
      residence: 'Residenza',
      licence: 'Patente n.',
      licencePlace: 'Rilasciata da',
      licenceExpires: 'Scadenza',
      licenceCategories: 'Categorie',
    },
    missing: 'da completare',
    attachment: 'Si allega copia del contratto di locazione.',
    place: 'Luogo e data',
    signature: 'Timbro e firma',
    footer: 'Generata il {date} · verbale {number} · prenotazione {code}',
  },
  /** S6 (CMP-2, decision 4): the fines register, for the holders of fines.manage. */
  fines: {
    title: 'Multe',
    intro:
      'I verbali arrivati all’azienda: ognuno abbinato al noleggio che aveva l’auto, i dati del conducente inviati all’ente entro la scadenza, il diritto di gestione addebitato.',
    views: { open: 'Da gestire', all: 'Tutte' },
    search: 'Cerca',
    searchHint: 'Targa o numero del verbale.',
    register: 'Registra un verbale',
    none: 'Nessuna multa.',
    found: { one: '{count} multa', other: '{count} multe' },
    fills: 'Una multa arriva qui con «Registra un verbale».',
    /** UI-2g: «Da gestire» empty is not the register empty. */
    noneOpen: 'Nessuna multa da gestire.',
    fillsOpen: 'Quelle già chiuse sono in «Tutte».',
    shown: '{shown} di {total}: cerca per trovare le altre.',
    columns: {
      notice: 'Verbale',
      violated: 'Violazione',
      plate: 'Targa',
      booking: 'Prenotazione',
      status: 'Stato',
      deadline: 'Scadenza',
    },
    status: {
      registered: 'Registrata',
      matched: 'Abbinata',
      communicated: 'Comunicata',
      closed: 'Chiusa',
      not_ours: 'Non nostra',
      withdrawn: 'Ritirata',
    },
    deadline: {
      running: 'In corso',
      due_soon: 'In scadenza',
      overdue: 'Scaduta',
      met: 'Rispettata',
      late: 'Inviata in ritardo',
      none: 'Nessuna',
    },
    daysLeft: { one: 'manca {count} giorno', other: 'mancano {count} giorni' },
    dueToday: 'scade oggi',
    daysPast: {
      one: 'scaduta da {count} giorno',
      other: 'scaduta da {count} giorni',
    },
    /** UI-2e: under the booking it was charged on, in the register. */
    feeCharged: 'Diritto di gestione addebitato',
    channels: {
      pec: 'PEC',
      form: 'Modulo dell’ente',
      portal: 'Portale online',
      post: 'Raccomandata',
      other: 'Altro',
    },
    form: {
      titleNew: 'Registra un verbale',
      titleCorrect: 'Correggi il verbale',
      hint: 'Come stampato sul verbale. La scadenza per inviare i dati del conducente si conta dal giorno della notifica.',
      authority: 'Ente',
      authorityHint: 'Chi ha emesso il verbale: il comando o il comune.',
      noticeNumber: 'Numero del verbale',
      violatedOn: 'Data della violazione',
      violatedAt: 'Ora della violazione',
      place: 'Luogo',
      plate: 'Targa',
      amount: 'Importo della multa',
      amountHint: 'Lo paga il conducente all’ente: non è un incasso dell’azienda.',
      notifiedOn: 'Notificato il',
      submit: 'Registra',
      save: 'Salva la correzione',
      cancel: 'Annulla',
      invalid: 'Controlla i campi segnati.',
      errors: {
        authority: 'Scrivi l’ente che ha emesso il verbale.',
        noticeNumber: 'Scrivi il numero del verbale.',
        violatedOn: 'Scegli il giorno della violazione.',
        violatedAt: 'Scrivi l’ora come sul verbale, per esempio 14:30.',
        place: 'Scrivi il luogo della violazione.',
        plate: 'La targa è fatta di lettere e cifre.',
        amount: 'Scrivi l’importo in euro, per esempio 42,00.',
        notifiedOn: 'Scegli il giorno in cui il verbale è arrivato.',
      },
    },
    detail: {
      back: 'Tutte le multe',
      /** The page's title before its fine is read. */
      heading: 'Multa',
      title: 'Verbale {number}',
      notFound: 'Nessuna multa a questo indirizzo.',
      notice: 'Il verbale',
      when: '{date} alle {time}',
      authority: 'Ente',
      violated: 'Violazione',
      place: 'Luogo',
      plate: 'Targa',
      vehicle: 'Veicolo',
      notOurPlate: 'Nessun nostro veicolo ha questa targa.',
      amount: 'Importo',
      notified: 'Notificato il',
      deadline: 'Scadenza',
      registered: 'Registrato da {name} il {when}.',
      correct: 'Correggi il verbale',
    },
    rental: {
      title: 'Noleggio',
      noVehicle: 'La targa non è di un nostro veicolo: se è così, segna la multa come non nostra.',
      none: 'Nessun noleggio aveva l’auto in quel minuto: un errore nel verbale o nella targa, o l’auto era in sede.',
      several: 'Più noleggi avevano l’auto in quel minuto: scegli quello giusto.',
      pick: 'Abbina al noleggio {code}',
      noneNamed: 'Nessun noleggio abbinato.',
      period: 'Dal {from} al {to}',
      stillOut: 'ancora fuori',
      auto: 'Trovato da solo: era l’unico noleggio con l’auto in quel minuto.',
      manual: 'Scelto da {name} il {when}.',
      unmatch: 'Annulla l’abbinamento',
      unmatchHint: 'Solo finché nulla è stato inviato e il diritto non è addebitato.',
    },
    letter: {
      title: 'Lettera con i dati del conducente',
      intro:
        'In italiano, per l’ente: i dati del conducente registrati alla consegna. Non riporta mai il numero del documento d’identità.',
      prepare: 'Prepara la lettera',
      preparing: 'In preparazione…',
      failed:
        'La lettera non è stata preparata: riprova; se succede ancora, guarda la schermata Sistema.',
      retry: 'Riprova',
      open: 'Apri la lettera (PDF)',
      notInApp:
        'La lettera ora non si apre in quest’app: chiudila e riaprila, oppure apri la lettera da un computer.',
      ready: 'Lettera pronta dal {when}.',
      // S8: a renter anonymised, or a rental naming someone anonymised.
      renterAnonymised:
        'Il cliente di questo noleggio è stato anonimizzato: nessuna lettera può più indicarlo. Gestisci la multa a mano.',
      ownerOnly:
        'Questo noleggio nomina una persona anonimizzata: la lettera la apre solo il titolare.',
      viewLogged:
        'Questo noleggio nomina una persona anonimizzata: ogni apertura della lettera viene registrata.',
    },
    communication: {
      title: 'Invio all’ente',
      intro:
        'Quando i dati del conducente sono stati inviati all’ente, registra il giorno e il modo.',
      on: 'Inviata il',
      channel: 'Come',
      reference: 'Riferimento',
      referenceHint: 'Il numero della PEC, del modulo o della raccomandata.',
      submit: 'Registra l’invio',
      done: 'Inviata il {date} con {channel}; registrata da {name}.',
      doneReference: 'Inviata il {date} con {channel} ({reference}); registrata da {name}.',
      why: 'Scegli il giorno in cui sono stati inviati.',
    },
    fee: {
      title: 'Diritto di gestione',
      none: 'Il noleggio è partito senza un diritto di gestione multe: non c’è nulla da addebitare.',
      notYet: 'Si addebita quando la multa è abbinata al noleggio.',
      charge: 'Addebita {amount}',
      confirm:
        'Addebitare {amount} alla prenotazione {code}? Si incassa al banco o con un link di pagamento, mai sulla carta in automatico.',
      confirmYes: 'Sì, addebita',
      charged: 'Addebitato {amount} il {when}.',
      balance:
        'La prenotazione {code} deve ancora {amount}: incassalo dalla sua pagina, al banco o con un link di pagamento.',
      paid: 'La prenotazione {code} non deve più nulla.',
    },
    end: {
      title: 'Chiudere la multa',
      notOurs: 'Non è nostra',
      withdrawn: 'Ritirata dall’ente',
      close: 'Chiudi la multa',
      reopen: 'Riapri',
      reason: 'Motivo',
      submit: 'Conferma',
      cancel: 'Annulla',
      ended: '{status} il {when} da {name}.',
      endedReason: '{status} il {when} da {name}: {reason}',
      why: 'Scrivi il motivo, almeno {min} caratteri.',
    },
    done: {
      registered: 'Verbale registrato.',
      corrected: 'Verbale corretto.',
      matched: 'Multa abbinata al noleggio.',
      unmatched: 'Abbinamento annullato.',
      letter: 'La lettera è in preparazione.',
      communicated: 'Invio registrato.',
      fee: 'Diritto di gestione addebitato.',
      ended: 'Fatto.',
      reopened: 'Multa riaperta.',
    },
    mentions: {
      title: 'Multe',
      line: '{authority}, verbale {number}: {date} alle {time}, {plate}',
      fee: 'diritto {amount}',
    },
  },
  fiscal: {
    title: 'Fiscale',
    intro:
      'I documenti fiscali li stampa il registratore di ogni punto: qui si scrivono i loro numeri. Sotto, gli incassi e le restituzioni che non ne hanno ancora uno.',
    providerPending:
      'Fornitore dei documenti fiscali: ogni documento si scrive da quello stampato.',
    money: { payment: 'Incasso', refund: 'Restituzione' } satisfies Record<
      'payment' | 'refund',
      string
    >,
    methods: {
      cash: 'Contanti',
      card_terminal: 'POS',
      bank_transfer: 'Bonifico',
      online: 'Online, con Stripe',
      deposit: 'Dal deposito',
      old_site: 'Sul vecchio sito',
      stripe: 'Con Stripe',
    } satisfies Record<PaymentMethod | RefundMethod, string>,
    kinds: {
      receipt: 'Scontrino',
      return: 'Reso',
      invoice: 'Fattura',
      credit_note: 'Nota di credito',
    } satisfies Record<FiscalDocumentKind, string>,
    sources: {
      till: 'Il registratore del punto',
      provider: 'Il fornitore',
      other: 'Altro',
    } satisfies Record<FiscalSource, string>,
    document: {
      legend: 'Documento fiscale',
      hint: 'Il numero stampato. Senza numero il movimento resta nella schermata Fiscale, da completare.',
      kind: 'Tipo',
      source: 'Stampato da',
      point: 'Punto del registratore',
      pointFromDrawer: 'Il registratore dello stesso punto della cassa.',
      pointChoose: 'Scegli il punto del registratore.',
      /** The list's empty choice: short enough for a half-width pane on the iPad. */
      pointPrompt: 'Scegli il punto',
      number: 'Numero',
      numberHint: 'Come è stampato, per esempio 0042-0007.',
      printedLater: 'Stampato in un altro momento',
      printedDate: 'Stampato il',
      printedTime: 'Alle',
      printedHint: 'La data e l’ora decidono in quale chiusura (Z) entra.',
      printedInvalid: 'Scrivi una data e un’ora valide.',
      stripeLater:
        'Con Stripe i soldi tornano più tardi: il documento si scrive dalla schermata Fiscale quando il rimborso è riuscito.',
    },
    line: '{kind} n. {number}, {date}',
    linePoint: '{kind} n. {number}, {date}, {point}',
    none: 'Senza documento fiscale',
    // S9 (Q-120): what was paid on the old site, and what goes back from it, has none here.
    notHere: 'Nessun documento fiscale qui: denaro del vecchio sito.',
    record: 'Registra il documento',
    recordTitle: 'Documento fiscale per {amount}',
    recordSubmit: 'Registra',
    /** UI-2e: a row's Registra, named by its row. */
    recordFor: 'Registra il documento: {code}, {amount}',
    recorded: 'Documento registrato.',
    cancel: 'Annulla',
    correct: 'Correggi',
    correctTitle: 'Correggi il documento n. {number}',
    correctReason: 'Motivo',
    correctReasonHint: 'Obbligatorio: il documento sbagliato resta, con il motivo.',
    correctWithRight: 'Scrivi anche il documento giusto',
    correctSubmit: 'Correggi il documento',
    /** UI-1e: what still stops a document's button, beside it. */
    why: {
      number: 'Scrivi il numero del documento.',
      reason: 'Scrivi il motivo, almeno 3 caratteri.',
    },
    corrected: 'Documento corretto.',
    superseded: '{kind} n. {number}, corretto il {date}: {reason}',
    missing: {
      title: 'Senza documento fiscale',
      when: 'Quando',
      booking: 'Prenotazione',
      money: 'Movimento',
      method: 'Metodo',
      amount: 'Importo',
      totals: {
        one: '{count} movimento, {amount}.',
        other: '{count} movimenti, {amount}.',
      },
      shown: 'Ne sono elencati {shown} di {count}: restringi il periodo.',
      none: 'Ogni incasso e restituzione di questo periodo ha il suo documento.',
    },
    till: {
      title: 'Chiusura del registratore (Z)',
      closeHint:
        'Se il registratore di questo punto ha fatto la chiusura giornaliera, scrivi la Z: si confronta con i documenti registrati. Senza numero la cassa si chiude senza Z.',
      aloneTitle: 'Registra una Z',
      aloneHint: 'Una chiusura del registratore fatta senza chiudere la cassa, o dimenticata.',
      number: 'Numero della Z',
      date: 'Stampata il',
      time: 'Alle',
      total: 'Totale (€)',
      totalHint: 'Il totale stampato, già al netto dei resi.',
      incomplete: 'Per la Z servono numero, data e ora e totale.',
      difference:
        'I documenti del registratore registrati per questa Z sommano {documents}: differenza {difference}.',
      reason: 'Motivo della differenza della Z',
      reasonHint: 'Obbligatorio: il titolare lo legge.',
      /** UI-2g: what the Z's button waits for once the server has said the Z differs. */
      whyReason: 'Scrivi il motivo della differenza, almeno 3 caratteri.',
      submit: 'Registra la Z',
      recorded: 'Z registrata.',
      list: 'Ultime Z',
      /** The table's region, one per point: two regions of one name are one too many. */
      listOf: 'Ultime Z, {point}',
      listFailed: 'Le ultime Z non si sono caricate.',
      none: 'Nessuna Z registrata per questo punto.',
      columns: {
        number: 'Z n.',
        closedAt: 'Stampata',
        total: 'Totale',
        documents: 'Documenti',
        difference: 'Differenza',
        now: 'Ora',
        reason: 'Motivo',
      },
      liveHint: '«Ora»: la differenza con i documenti registrati oggi, anche dopo la Z.',
      superseded: 'Corretta il {date}: {reason}',
      correct: 'Correggi',
      correctTitle: 'Correggi la Z n. {number}',
      correctWithRight: 'Scrivi la Z giusta',
      correctSubmit: 'Correggi la Z',
      corrected: 'Z corretta.',
    },
  },

  paymentMethods: {
    cash: 'Contanti',
    card_terminal: 'Carta, con il POS del negozio',
    bank_transfer: 'Bonifico',
    online: 'Online, con Stripe',
    deposit: 'Trattenuto dal deposito',
    // Where the money came from: its date is the day it was carried over, not the day it was paid.
    old_site: 'Dal vecchio sito',
  } satisfies Record<PaymentMethod, string>,

  sources: {
    walk_in: 'Al banco',
    phone: 'Telefono',
    whatsapp: 'WhatsApp',
    email: 'E-mail',
    web: 'Sito',
    old_site: 'Dal vecchio sito',
  } satisfies Record<ReservationSource, string>,

  eligibility: {
    eligible: 'Requisiti in regola',
    ineligible: 'Requisiti non rispettati',
    unknown: 'Verifica al banco',
  } satisfies Record<EligibilityStatus, string>,

  eligibilityReasons: {
    rules_not_configured: 'Requisiti del conducente non configurati per questa categoria.',
    birth_date_missing: 'Data di nascita non indicata.',
    too_young: 'Età minima: {minAge} anni.',
    licence_rules_not_configured: 'Patente richiesta non configurata per questa categoria.',
    licence_missing: 'Patente non indicata.',
    licence_expired: 'Patente scaduta: {expiresOn}.',
    licence_category: 'Categoria di patente: si accetta {accepted}.',
    licence_first_issue_unknown: 'Data del primo rilascio della patente non indicata.',
    licence_too_recent: 'Patente da meno di {minYears} anni.',
    idp_required: 'Serve il permesso internazionale di guida (patente: {country}).',
    manual_check: 'Patente da controllare, come per ogni noleggio di questa categoria.',
  } satisfies Record<EligibilityReasonCode, string>,

  documentKinds: {
    id_card: 'Carta d’identità',
    passport: 'Passaporto',
    driving_licence: 'Patente',
    other: 'Altro documento',
  } satisfies Record<DocumentKind, string>,

  documentSides: {
    front: 'fronte',
    back: 'retro',
    single: 'pagina unica',
  } satisfies Record<DocumentSide, string>,

  mediaStatus: {
    pending: 'in caricamento',
    stored: 'ricevuto',
  } satisfies Record<MediaStatus, string>,

  vehicleDocuments: {
    insurance: 'Assicurazione',
    revisione: 'Revisione',
    bollo: 'Bollo',
    other: 'Altro documento',
  } satisfies Record<VehicleDocumentKind, string>,

  quoteLines: {
    rental: 'Noleggio',
    surcharge: 'Supplemento',
    discount: 'Sconto {code}',
    delivery: 'Consegna: {name}',
    collection: 'Ritiro a fine noleggio: {name}',
    one_way: 'Riconsegna in un altro luogo',
    extra: '{name}',
    coverage: '{name}',
  } satisfies Record<QuoteLineKind, string>,
  youngDriver: 'Supplemento giovane conducente',

  errors: {
    network: 'Il server non risponde. Controlla la connessione e riprova.',
    fallback: 'Qualcosa non ha funzionato. Riprova tra poco.',
    /** C1: what is missing, by the setting the answer names; the Owner completes it. */
    notConfigured: {
      'terms.current_version': 'Non ci sono condizioni in vigore: il titolare deve caricarle.',
      'company.legal':
        'Mancano i dati dell’azienda per il contratto (ragione sociale, sede, partita IVA): il titolare deve completarli.',
      'catalog.demo': 'Questo sistema contiene i dati di prova: in produzione non si vende nulla.',
      other: 'La configurazione è incompleta: il titolare deve completarla.',
    },
    /** The counter's own words for codes the public endpoints share (C1). */
    shared: {
      booking_closed: 'Questa prenotazione è chiusa: non si aggiungono più documenti.',
      booking_disputed:
        'Il cliente ha contestato un pagamento con la sua banca: quanto deve si incassa solo al banco, senza link.',
      terms_draft:
        'Le condizioni in vigore sono una bozza: il contratto non si può firmare. Il titolare deve caricare le condizioni definitive.',
    },
    /** A1: what the app finds itself, with no answer from the server behind it. */
    client: {
      device_store_unavailable:
        'Le credenziali salvate su questo dispositivo non si leggono in questo momento. Riprova tra poco; se continua, chiudi l’app e riaprila.',
      file_type_refused: 'Questo file non è una foto né un PDF: l’app non lo mostra.',
      file_not_opened: 'Il file non si è aperto: riprova.',
      // S2: anything that is not an answer from the API (a bug in the app).
      unexpected:
        'Qualcosa non ha funzionato in questa schermata. Riprova; se continua, ricarica l’app.',
    },
    codes: {
      credentials_invalid: 'E-mail o password non corretti.',
      totp_required: 'Inserisci il codice dell’app di autenticazione.',
      totp_invalid: 'Codice non corretto o già usato: aspetta il prossimo e riprova.',
      totp_not_configured:
        'Questo account non ha un’app di autenticazione configurata: rivolgiti al titolare.',
      device_not_enrolled: 'Questo dispositivo non è registrato.',
      device_revoked: 'Questo dispositivo è stato disconnesso.',
      session_required: 'Schermo bloccato: inserisci il PIN.',
      user_not_found: 'Questa persona non può più accedere.',
      pin_not_set: 'Nessun PIN ancora: accedi con la password per sceglierlo.',
      pin_invalid: 'PIN errato.',
      pin_locked: 'Troppi PIN errati: su questo dispositivo serve la password.',
      reauthentication_required: 'Per cambiare il PIN serve un accesso recente con la password.',
      pin_format: 'Il PIN è di 6 cifre.',
      pin_too_simple: 'PIN troppo facile: evita sequenze e ripetizioni.',
      // OPS-1, Regole del contratto.
      terms_already_current: 'Questa versione è già in vigore.',
      terms_already_stored: 'Questo testo è già salvato come versione {version}.',
      forbidden: 'Il tuo ruolo non permette questa operazione.',
      contact_required:
        'Per una prenotazione al telefono, su WhatsApp o per e-mail serve un telefono o un’e-mail del cliente.',
      customer_not_found: 'Cliente non trovato: cercalo di nuovo.',
      checkout_too_early:
        'Troppo presto: il veicolo può partire da due ore prima del ritiro prenotato.',
      checkout_too_late:
        'L’orario di riconsegna è già passato: questa prenotazione non può più partire.',
      checkout_blocked: 'La consegna è bloccata da quello che è elencato qui.',
      vehicle_required: 'Scegli il veicolo che parte.',
      not_reschedulable: 'Questa prenotazione non si può spostare: è già in corso o chiusa.',
      km_below_checkout: 'I chilometri sono meno di quelli alla consegna: controlla la lettura.',
      payment_exceeds_balance: 'L’importo supera quanto resta da pagare.',
      paid_online_meanwhile:
        'Il cliente ha appena pagato online: il pagamento è registrato. Controlla il conto prima di incassare.',
      customer_email_missing:
        'La prenotazione non ha un indirizzo e-mail: crea il link e copialo nel messaggio.',
      contract_already_signed:
        'Il contratto di questa prenotazione è già firmato per questo veicolo: non serve firmarlo di nuovo.',
      deposit_already_taken: 'Per questa prenotazione c’è già un deposito.',
      deposit_not_needed: 'Questa categoria non prevede un deposito.',
      deposit_not_pending: 'Il lettore non sta aspettando nessuna carta.',
      deposit_not_held: 'La carta non è ancora stata preautorizzata.',
      deposit_exceeds_hold: 'L’importo supera il deposito.',
      deposit_exceeds_balance:
        'L’importo supera quanto resta da pagare: dal deposito si trattiene solo il dovuto.',
      deposit_expired:
        'La preautorizzazione è scaduta prima di poterla incassare: non è stato trattenuto nulla.',
      no_deposit: 'Per questa prenotazione non c’è un deposito.',
      cash_not_accepted: 'Per questa categoria il deposito in contanti non è accettato.',
      reader_pending:
        'Il lettore di carte non è ancora attivo: registra il deposito preso sul POS o in contanti.',
      reader_unavailable:
        'Il lettore di carte non risponde o è occupato: riprova, oppure usa il POS del negozio.',
      simulation_refused: 'La carta di prova si usa solo in modalità di test.',
      inspection_closed:
        'Il veicolo è già partito o già rientrato: questa ispezione non si può più cambiare.',
      not_this_vehicle: 'Questa prenotazione ha un altro veicolo.',
      zone_not_on_vehicle: 'Questo veicolo non ha questa zona.',
      damage_conflict: 'Questo danno è già stato registrato con altri dati: ricarica la pagina.',
      damage_not_on_vehicle: 'Il danno non è aperto su questo veicolo, in questa zona.',
      intervention_voided: 'L’intervento è stato annullato.',
      photo_conflict:
        'Questa foto è già stata inviata con altri dati: scartala e scattala di nuovo.',
      photo_other_device:
        'Questa foto è stata iniziata su un altro dispositivo, o prima che questo fosse registrato di nuovo: scartala e scattala di nuovo.',
      photo_erased:
        'Questa foto è stata cancellata con la sua prenotazione, conclusa da tempo: scartala.',
      damage_not_chargeable:
        'Si addebita solo un danno segnato a questo rientro, una volta, come danno.',
      too_many_photos: 'Questa ispezione ha già il numero massimo di foto.',
      too_many_damages: 'Questa ispezione ha già il numero massimo di danni.',
      vehicle_still_out:
        'Il veicolo non è ancora rientrato dal noleggio {bookingCode}: registra prima il rientro, o scegli un altro veicolo.',
      origin_not_allowed: 'Il server non è configurato per questa app. Avvisa il titolare.',
      credentials_ambiguous:
        'Il dispositivo ha inviato le sue credenziali in due modi. Chiudi l’app e riaprila.',
      auth_mode_required: 'Questa versione dell’app non può accedere: aggiornala.',
      credentials_malformed:
        'Le credenziali di questo dispositivo non sono leggibili. Registra di nuovo il dispositivo.',
      cross_site_refused: 'Apri foto e documenti dall’app del banco.',
      not_dead:
        'Questo messaggio non è più tra i non inviati: forse qualcuno l’ha già rimesso in coda.',
      not_resendable: 'Da qui si possono inviare di nuovo solo le e-mail.',
      identity_required: 'Registra prima i dati del conducente.',
      identity_changed:
        'I dati del conducente sono stati registrati di nuovo nel frattempo: controlla e riprova.',
      cargos_details_required:
        'Per questo veicolo servono anche documento, patente, luogo di nascita e cittadinanza.',
      cargos_not_required:
        'Questa categoria non si comunica a CaRGOS: il numero del documento non si registra.',
      cargos_record_invalid: 'CaRGOS rifiuterebbe questi dati: correggi i campi indicati.',
      cargos_already_reported: 'CaRGOS ha già la ricevuta per questa consegna.',
      cargos_in_flight: 'Un invio a CaRGOS è già in corso: attendi la risposta.',
      cargos_outcome_unknown:
        'L’ultimo invio ha un esito sconosciuto: prima controlla sul portale CaRGOS che la comunicazione non ci sia.',
      cargos_pending:
        'L’invio a CaRGOS non è ancora attivo: usa il portale e registra la ricevuta.',
      contract_not_signed: 'Prima fai firmare il contratto per questo veicolo.',
      cancellation_fee_required:
        'Le condizioni di questa prenotazione non dicono nulla sull’annullamento: scrivi quanto si trattiene.',
      cancellation_fee_not_allowed:
        'Penale non ammessa: non supera mai il totale della prenotazione, e una riduzione è più bassa della penale prevista.',
      cancellation_fee_changed:
        'La penale è cambiata mentre la finestra era aperta: controlla il nuovo importo e conferma di nuovo.',
      no_show_too_early:
        'Il cliente si segna come non presentato solo dopo l’orario di ritiro prenotato.',
      draft_paid_meanwhile:
        'Il cliente ha appena pagato online la sua parte: la prenotazione è confermata. Ricarica la pagina; se va annullata, si annulla con la penale delle condizioni.',
      deposit_still_held:
        'C’è ancora un deposito in corso: prima rilascialo o trattienilo, dal riquadro Deposito.',
      account_not_settled:
        'Il conto non è saldato: prima incassa quanto manca o restituisci quanto dovuto.',
      refund_in_progress:
        'Un rimborso è ancora in corso: la prenotazione si chiude quando è concluso.',
      dispute_open:
        'La banca del cliente sta ancora decidendo una contestazione: la prenotazione si chiude quando è conclusa.',
      payment_page_open:
        'Stripe non ha risposto: la pagina di pagamento online del cliente potrebbe ancora incassare. Riprova tra qualche minuto.',
      cash_drawer_required:
        'I contanti passano solo da una cassa aperta: scegli la cassa, o aprila con una conta.',
      cash_drawer_closed:
        'Questa cassa è chiusa, o in chiusura: aprila di nuovo con una conta, o scegline un’altra.',
      cash_drawer_open: 'La cassa di questo punto è già aperta.',
      cash_difference:
        'La conta non corrisponde a quanto dovrebbe esserci: indica il motivo, o conta di nuovo.',
      fiscal_kind:
        'Tipo di documento sbagliato: per un incasso scontrino o fattura, per un rimborso reso o nota di credito; il registratore emette solo scontrini e resi.',
      fiscal_location: 'Indica il punto del registratore che ha emesso il documento.',
      fiscal_document_exists:
        'Questo importo ha già il suo documento fiscale: correggilo, se è sbagliato.',
      fiscal_document_not_yet:
        'Il rimborso non è ancora concluso: il documento si registra quando i soldi sono tornati.',
      fiscal_not_here:
        'Quanto è stato pagato sul vecchio sito, e quanto se ne restituisce, non ha un documento fiscale qui.',
      old_site_reference_taken:
        'Questa prenotazione del vecchio sito è già stata registrata: la trovi con il riferimento.',
      stripe_payment_taken: 'Questo pagamento Stripe è già registrato su una prenotazione.',
      old_site_payment_unchecked:
        'Stripe non è configurato: il pagamento non si può controllare. Registralo senza il codice Stripe (si restituirà solo con bonifico) o riprova quando Stripe è attivo.',
      old_site_payment_unknown:
        'Stripe non ha un pagamento con questo codice: controlla che sia quello giusto (inizia con pi_).',
      old_site_payment_ours:
        'Questo pagamento è stato incassato da questa app (pagina di pagamento o deposito), non dal vecchio sito.',
      old_site_payment_not_paid: 'Per Stripe questo pagamento non è stato incassato.',
      old_site_payment_currency: 'Questo pagamento non è in euro.',
      old_site_payment_amount:
        'Per questo pagamento Stripe ha incassato {stripeNet}, al netto dei rimborsi: correggi l’importo pagato, o il codice.',
      old_site_payment_disputed:
        'Una banca ha contestato questo pagamento: verifica in Stripe quanto il cliente ha davvero pagato, poi registralo senza il codice Stripe.',
      old_site_payment_refunds:
        'Stripe ha un rimborso di questo pagamento che qui non si può registrare: registralo senza il codice Stripe, con quanto il cliente ha davvero pagato.',
      fiscal_superseded: 'Questo documento è già stato corretto.',
      vehicle_document_superseded: 'Questo documento è già stato annullato.',
      till_closure_exists: 'Questa chiusura è già registrata (stesso numero o stessa ora).',
      fine_exists: 'Questo verbale di questo ente è già registrato.',
      fine_status: 'La multa non è più in questo stato: ricarica la pagina.',
      fine_plate: 'La targa è fatta di lettere e cifre.',
      fine_dates:
        'Controlla le date: il verbale arriva dopo la violazione e non oltre oggi; l’invio dei dati non è prima dell’arrivo né oltre oggi.',
      fine_not_candidate: 'Quel noleggio non aveva l’auto nel minuto della violazione.',
      fine_not_matched: 'La multa non è ancora abbinata a un noleggio.',
      fine_reason_required: 'Indica il motivo.',
      fine_fee_charged: 'Il diritto di gestione di questa multa è già addebitato.',
      fine_fee_not_configured:
        'Il noleggio è partito senza un diritto di gestione multe: non c’è nulla da addebitare.',
      fine_renter_anonymised:
        'I dati del cliente sono stati cancellati per la privacy: nessuna lettera può indicarlo. Gestisci la multa a mano.',
      customer_anonymised:
        'Questo cliente è stato anonimizzato per la privacy: la sua scheda non cambia più.',
      contact_erased:
        'I recapiti di questo cliente sono stati cancellati su sua richiesta: non si possono reinserire.',
      contract_being_signed:
        'Il cliente sta firmando un contratto: correggi i dati quando ha firmato.',
      second_driver_unavailable:
        'Ora non si può aggiungere un secondo conducente a questa prenotazione: ricarica la pagina.',
      second_driver_ineligible:
        'Il secondo conducente non ha i requisiti per questa categoria: solo il titolare può lasciarlo guidare, con un motivo.',
      second_driver_is_renter:
        'Chi ha prenotato non può essere anche il secondo conducente: scegli un’altra persona.',
      no_second_driver: 'Questa prenotazione non ha un secondo conducente: ricarica la pagina.',
      second_identity_required: 'Registra prima i dati del secondo conducente.',
      unknown_category: 'Questa categoria non esiste più: ricarica la pagina.',
      unknown_site_model: 'Il sito non ha questo modello: ricarica la pagina.',
      model_exists: 'Questo modello è già nell’elenco: cercalo lì.',
      site_model_fields: 'Un modello del sito tiene il nome e i dati del sito: cambiali sul sito.',
      name_required: 'Dai un nome al modello.',
      photo_required: 'Un modello aggiunto qui ha bisogno di una foto.',
      photo_not_ready:
        'La foto non ha finito di caricarsi: attendi un momento o scattala di nuovo.',
      lead_needs_category: 'Solo un modello collegato a una categoria può rappresentarla.',
      lead_changed:
        'Qualcun altro ha cambiato il modello che rappresenta questa categoria: ricarica la pagina.',
      retired_linked: 'Scollega il modello dalla sua categoria prima di toglierlo dall’uso.',
      customer_erasure_requested:
        'Questo cliente ha chiesto la cancellazione dei suoi dati: la sua scheda non si usa per nuove prenotazioni. Registralo come nuovo cliente.',
      erasure_already_requested:
        'Una richiesta di cancellazione dei dati di questo cliente è già registrata.',
      no_erasure_request: 'Non risulta nessuna richiesta di cancellazione: ricarica la pagina.',
      erasure_grace_over:
        'Le 24 ore per ritirare la richiesta sono passate: la cancellazione è avvenuta o avviene al prossimo passaggio.',
      legal_hold_exists: 'Su questo cliente c’è già un blocco legale.',
      no_legal_hold: 'Su questo cliente non c’è nessun blocco legale: ricarica la pagina.',
      export_ticket_invalid:
        'Il download è scaduto, o è stato chiesto da un’altra sessione: chiedi di nuovo l’esportazione con il codice.',
      fines_not_configured:
        'Manca l’impostazione dei giorni per comunicare i dati del conducente (fines.communication_days).',
      till_difference:
        'Il totale della chiusura non corrisponde ai documenti registrati: indica il motivo.',
      report_range:
        'Il periodo va dal primo giorno all’ultimo, in quest’ordine, e dura al massimo un anno e un giorno.',
      difference_acknowledged: 'Questa differenza è già stata segnata come letta.',
      nothing_to_refund: 'Per questa prenotazione non c’è nulla da restituire.',
      refund_exceeds_credit: 'L’importo supera quanto va restituito al cliente.',
      refund_exceeds_payment: 'L’importo supera quanto resta di questo pagamento.',
      refund_method_not_allowed:
        'I soldi tornano come sono arrivati: scegli uno dei modi proposti per questo pagamento.',
      refund_needs_stripe:
        'Stripe non è configurato su questo sistema: il rimborso con Stripe non si può fare. Restituisci in un altro modo proposto.',
      // S9 (Persone, Q-51)
      password_change_required:
        'Prima di continuare scegli la tua password: quella che ti è stata data vale solo per il primo accesso.',
      password_expired: 'Questa password è scaduta: chiedi al titolare di dartene una nuova.',
      password_is_email: 'La password non può essere la tua e-mail.',
      password_unchanged: 'Scegli una password diversa da quella attuale.',
      email_taken:
        'Esiste già un account con questa e-mail. Se la password appena creata non si è vista, dagliene una nuova dalla sua scheda.',
      role_not_found: 'Questo ruolo non esiste: ricarica la pagina e scegline un altro.',
      last_owner:
        'Deve restare almeno un titolare attivo con l’app di autenticazione: prima aggiungine un altro.',
      person_self:
        'Non sul proprio account: ruolo e accesso li cambia un altro titolare, la password la cambi da «Cambia password».',
      person_disabled: 'Questa persona è disattivata: riattivala prima.',
      person_active: 'Questa persona è già attiva.',
      no_authenticator:
        'Questo ruolo accede senza codice: non ha un’app di autenticazione da rifare.',
      device_current: 'Questo è il dispositivo che stai usando: disconnettilo da Dispositivi.',
      device_not_theirs: 'Questa persona non ha mai usato quel dispositivo: ricarica la pagina.',
      session_opened:
        'Proprio in quel momento questa persona ha sbloccato un dispositivo: riprova.',
      delivery_not_a_zone:
        'Questa parte della prenotazione è a un punto di ritiro, non in una zona di consegna.',
      delivery_closed: 'Questo viaggio è già fatto, o la prenotazione è chiusa.',
      delivery_person_unavailable:
        'Questa persona è disattivata, o il suo ruolo non consegna auto: scegline un’altra.',
      delivery_given_now:
        'Proprio in quel momento a questa persona è stata data una consegna: riprova.',
      delivery_zone:
        'La zona di consegna non accetta lo spostamento: consegne sospese, preavviso troppo breve o prenotazioni chiuse per quel giorno.',
      opening_hours: 'Il punto è chiuso a quell’ora: scegli un orario di apertura.',
      delivery_not_yours:
        'Questo viaggio non è affidato a te, o il suo tempo è finito: chiedi all’ufficio.',
      delivery_office_only: 'In consegna questo lo fa l’ufficio: chiedi all’ufficio.',
      changed_since_preview:
        'Qualcuno ha cambiato uno di questi giorni dopo l’anteprima: rifai l’anteprima.',
      day_past: 'Un giorno già passato non si cambia.',
      invalid_range: 'L’ultimo giorno viene prima del primo.',
      range_too_long: 'Una modifica copre al massimo 400 giorni.',
      invalid_weekdays: 'Scegli almeno un giorno della settimana.',
      price_not_positive: 'Il prezzo di un giorno resta sopra zero.',
      price_too_high: 'Il prezzo di un giorno è al massimo 5.000 €.',
      // S11: inserimento da carta.
      reentry_not_configured:
        'L’inserimento da carta non è attivo: mancano le impostazioni reentry.max_days e reentry.revenue_day.',
      reentry_empty: 'Niente da inserire: compila almeno una parte del foglio.',
      reentry_time:
        'Un orario non va: non può essere nel futuro né più indietro di quanto le impostazioni consentono.',
      reentry_order:
        'Gli orari non tornano: niente prima che la prenotazione esistesse, poi ricevuta, consegna e riconsegna in quest’ordine, e il veicolo mai con due clienti insieme.',
      paper_sheet: 'Il numero del foglio non va: ricontrollalo sul foglio stampato.',
    } satisfies Record<StaffErrorCode, string>,
  },

  /** S2: the error screen, inside the app, when a screen stops working. */
  crash: {
    title: 'Questa schermata si è interrotta',
    body: 'Riprova. Se succede di nuovo, ricarica l’app: i dati già salvati non vanno persi.',
    retry: 'Riprova',
    reload: 'Ricarica l’app',
    reference: 'Riferimento: {id}',
  },

  // Q-157: the models that stand for each category, set by the Owner and the Agents.
  models: {
    title: 'Categorie e modelli',
    intro:
      'I modelli che rappresentano ogni categoria, sul sito e qui. Il principale dà la foto e il nome ("Panda o simile"); gli altri compaiono tra i modelli possibili.',
    lead: 'Principale',
    appModel: 'Aggiunto qui',
    ownPhoto: 'Foto scattata qui',
    noModels: 'Nessun modello collegato',
    noModelsHint: 'Sul sito questa categoria compare senza foto, con il suo nome.',
    noLead: 'Nessun modello principale: sul sito vale il primo dell’elenco.',
    inactiveCategory: 'Categoria non attiva',
    link: 'Collega un modello',
    linkTitle: 'Collega un modello a {category}',
    linkHint: 'I modelli del sito non ancora collegati a una categoria.',
    linkNone: 'Tutti i modelli del sito sono già collegati.',
    linked: '{model} collegato a {category}.',
    add: 'Aggiungi un modello nuovo',
    addTitle: 'Un modello che il sito non ha',
    addHint:
      'Compare con il suo nome e la sua foto, senza una pagina sul sito. Per cambiarne nome e dati sul sito serve il sito stesso.',
    added: '{model} aggiunto.',
    edit: 'Modifica',
    editTitle: 'Modifica {model}',
    saved: '{model} salvato.',
    makeLead: 'Rendi principale',
    madeLead: '{model} è ora il modello principale di {category}.',
    name: 'Nome',
    category: 'Categoria',
    noCategory: 'Nessuna categoria (scollegato)',
    isLead: 'Modello principale della categoria',
    leadTaken: 'Prende il posto di {model}.',
    active: 'In uso',
    retireHint: 'Tolto dall’uso, non compare più sul sito né tra i modelli da collegare.',
    photo: 'Foto',
    newPhoto: 'Nuova foto',
    photoHint:
      'Comparirà sul sito: niente targhe leggibili, persone o interni dell’ufficio. I dati della foto (posizione, fotocamera) vengono tolti.',
    sitePhoto: 'Torna alla foto del sito',
    seats: 'Posti',
    seatsInvalid: 'Da 1 a 9, o lascia vuoto.',
    transmission: 'Cambio',
    fuel: 'Alimentazione',
    notGiven: 'Non indicato',
    specsHint:
      'Un dato lasciato vuoto non compare: la categoria mostra solo quello che hanno tutti i suoi modelli.',
    unlinked: 'Modelli non collegati',
    unlinkedHint: 'Non rappresentano nessuna categoria: sul sito non compaiono nei risultati.',
    retired: 'Non in uso',
    cancel: 'Annulla',
    save: 'Salva',
    saving: 'Salvataggio…',
    sending: 'Invio della foto…',
    reading: 'Lettura della foto…',
    unreadable: 'Questa foto non si legge: scattane o sceglierne un’altra.',
    photoRequired: 'Scatta o scegli una foto.',
    nameRequired: 'Scrivi il nome del modello.',
    photoOf: 'Foto di {model}',
    preview: 'Anteprima della nuova foto',
  },

  /** S2: the Sistema screen (permission system.view) and its banner. */
  /** OPS-1: the rental terms' wording, the Owner's (Regole del contratto). */
  terms: {
    title: 'Regole del contratto',
    intro:
      'Le condizioni di noleggio che ogni nuovo contratto riporta. Una modifica è una nuova versione: i contratti già preparati o firmati restano come sono.',
    none: 'Nessuna versione in vigore: non si può firmare un contratto né prenotare online finché il titolare non ne mette una in vigore.',
    editor: 'Testo',
    editing:
      'Parti dalla versione {version}. Salvando crei una nuova versione: nessuna viene mai modificata.',
    editingNone: 'Nessuna versione salvata: scrivi la prima.',
    language: 'Il testo è in italiano per tutti i clienti.',
    versionTitle: 'Titolo',
    clauses: 'Clausole',
    clause: 'Clausola {n}',
    clauseUnnumbered: 'Paragrafo senza numero',
    moveUp: 'Sposta su',
    moveDown: 'Sposta giù',
    remove: 'Rimuovi',
    add: 'Aggiungi clausola',
    approval: 'Approvazione specifica (artt. 1341 e 1342 c.c.)',
    approvalHint:
      'I numeri delle clausole citati qui non vengono mai riscritti dall’app: dopo uno spostamento o una rimozione controllali tu.',
    renumbered:
      'Hai spostato o rimosso delle clausole: i numeri sono cambiati. Controlla i rimandi nell’approvazione specifica.',
    cited: 'I numeri citati nell’approvazione, e la clausola che oggi hanno',
    /** Q-162: whether these words let the second driver on the contract drive. */
    secondDriverAllowed: 'Queste condizioni permettono di guidare al secondo conducente',
    secondDriverHint:
      'Spunta solo se il testo dice che può guidare anche il secondo conducente indicato nel contratto. Senza, guida solo chi firma.',
    secondDriverHintFrom:
      'La versione {version} lo permetteva: non si riporta da sola, va confermato per queste parole.',
    secondDriverYes: 'Secondo conducente ammesso.',
    secondDriverNo: 'Guida solo chi firma.',
    citedRow: 'n. {n}: {text}',
    citedMissing: 'n. {n}: nessuna clausola con questo numero',
    cancellation: 'Penali di annullamento',
    cancellationHint:
      'Si cambiano con la configurazione, non da qui: la nuova versione le copia dalla versione in vigore.',
    cancellationNone:
      'Queste condizioni non dicono nulla sull’annullamento: il banco digita la penale.',
    tier: 'Da {hours} ore prima del ritiro: {fee}',
    noShow: 'Mancata presentazione: {fee}',
    fee: '{percent} % del totale + {fixed}',
    preview: 'Anteprima',
    previewHint: 'Come lo legge e lo firma il cliente.',
    untitled: '(senza titolo)',
    save: 'Salva come nuova versione',
    saved: 'Versione {version} salvata. Non è ancora in vigore: mettila in vigore dall’elenco.',
    unchanged: 'Il testo è uguale alla versione {version}: niente da salvare.',
    empty: 'Servono un titolo, almeno una clausola e l’approvazione specifica.',
    discard: 'Annulla le modifiche',
    discardTitle: 'Scartare le modifiche non salvate?',
    discardBody: 'Quello che hai scritto andrà perso.',
    discardConfirm: 'Scarta',
    discardKeep: 'Continua a scrivere',
    history: 'Versioni',
    noVersions: 'Nessuna versione salvata.',
    versionLabel: 'Versione {version}',
    current: 'In vigore',
    draft: 'BOZZA',
    inEditor: 'Nell’editor',
    created: 'Creata il {date} da {by}',
    fromFiles: 'configurazione',
    unknownAuthor: 'app',
    inForceSince: 'In vigore dal {date}',
    open: 'Apri nell’editor',
    activate: 'Metti in vigore',
    activateTitle: 'Mettere in vigore la versione {version}?',
    activateBody:
      'Da ora ogni nuovo contratto riporta questo testo. I contratti già preparati o firmati non cambiano.',
    activateDraft:
      'È una BOZZA: i contratti su di essa non hanno valore legale, e in produzione viene rifiutata.',
    reason: 'Motivo',
    reasonHint: 'Va nel registro delle modifiche, con la versione prima e dopo.',
    code: 'Codice dell’app di autenticazione',
    confirm: 'Metti in vigore',
    cancel: 'Indietro',
    activated: 'La versione {version} è in vigore.',
    why: 'Serve un motivo di almeno {min} lettere e il codice di 6 cifre.',
  },

  system: {
    title: 'Sistema',
    /** S6: the compliance adapters. */
    compliance: {
      cargos: 'CaRGOS',
      off: 'CaRGOS è spento (cargos.enabled): nessuna comunicazione è richiesta.',
      port: {
        send: 'Comunicazioni inviate dall’app.',
        check: 'Solo la prova dall’app (CARGOS_MODE=check): per le auto si usa il portale.',
        pending:
          'Le credenziali non sono ancora arrivate: le comunicazioni si fanno dal portale CaRGOS o via PEC.',
      },
      codes: 'Tabelle dei codici',
      codesValue: 'del {date}, {places} luoghi',
      codesHash: 'Impronta delle tabelle dei codici: {hash}',
      codesStale: 'Le tabelle dei codici hanno {days} giorni: vanno aggiornate (RUNBOOK).',
      last24h: 'Ultime 24 ore',
      none: 'nessuna comunicazione',
      oldestQueued: 'In coda dal',
      stuck: 'Una comunicazione è ferma dal {when}: non ha raggiunto CaRGOS.',
      fiscal: 'Documenti fiscali',
      fiscalPending:
        'Nessun fornitore scelto: i numeri dei documenti si scrivono da quelli del registratore telematico.',
      fiscalFake:
        'Fornitore di prova (solo in locale e nei test): i documenti che emette non sono veri.',
      fiscalOnline: {
        none: 'Pagamenti online: al fornitore non si chiede nulla (fiscal.online_payment_document).',
        at_payment:
          'Pagamenti online: lo scontrino si chiede al fornitore al momento del pagamento.',
      },
      fiscalWaiting: 'Richieste ancora senza documento',
      fiscalNoneWaiting: 'nessuna',
      fiscalStatus: {
        queued: 'In coda',
        issued: 'Emesso',
        refused: 'Rifiutato',
        unknown: 'Esito incerto',
        cancelled: 'Annullato',
      },
    },
    intro:
      'Lo stato dei servizi dietro l’app: backup, invio dei messaggi, avvisi. Si aggiorna ogni minuto.',
    checkedAt: 'Controllato alle {time}',
    state: { ok: 'Funziona', attention: 'Da controllare' },
    never: 'mai',
    failingTitle: 'Non funziona',
    failing: {
      database: 'Il database',
      worker: 'Il servizio in background (e-mail, avvisi)',
      delivery: 'L’invio delle e-mail',
      backups: 'I backup',
    },
    backups: {
      title: 'Backup',
      pending: 'Nessuno spazio esterno per i backup è ancora configurato.',
      base: 'Ultimo backup completo',
      verify: 'Ultima verifica',
      verifyFailed: 'non riuscita',
      files: 'Ultima copia di foto e documenti',
      filesCounts: '{copied} copiati, {failed} non riusciti, {total} in tutto',
      archiver: 'Archiviazione continua',
      archiverOk: 'Funziona',
      archiverFailing: 'Non riesce: {count} errori, l’ultimo il {when}',
      heartbeat: 'Ultimo segno di vita',
      busy: {
        base: 'sta facendo il backup completo',
        files: 'sta copiando foto e documenti',
      },
    },
    worker: {
      title: 'Servizio in background',
      heartbeat: 'Ultimo segno di vita',
      jobs: 'Controlli periodici',
      lastSuccess: 'Ultimo riuscito: {when}',
      failures: '{count} errori di fila',
      lastError: 'Ultimo errore di «{job}»: {error}',
      job: {
        heartbeat: 'Segno di vita',
        'alerts.evaluate': 'Valutazione degli avvisi',
        'smtp.probe': 'Server di posta',
        'daily.schedule': 'Lavori giornalieri',
        'cargos.erase': 'Cancellazione dei numeri dei documenti',
        'notifications.schedule': 'Promemoria e avvisi',
      },
    },
    daily: {
      title: 'Lavori giornalieri',
      morning: 'E-mail del mattino al titolare',
      next: 'Prossimo invio: {when}',
      pending: {
        no_email: 'Manca il server di posta (SMTP).',
        no_opening_hours: 'Nessun punto di ritiro ha un orario di apertura.',
        no_stripe: 'Mancano le chiavi di Stripe.',
      },
      reconcile: 'Controllo notturno con Stripe',
      unknown: 'Il servizio in background non l’ha ancora calcolato.',
      last: 'Ultima: {date}, {status}',
      status: {
        pending: 'in uscita',
        delivered: 'fatta',
        dead: 'non riuscita',
      },
    },
    /** S7: the customer reminders and the staff alerts, as the background service last read them. */
    /** S8: "Conservazione dei dati": what the retention runs do and what keeps the rest. */
    privacy: {
      title: 'Conservazione dei dati',
      intro:
        'Cosa cancella da solo il servizio in background, ogni ora, e cosa trattiene il resto. Solo numeri, mai un nome.',
      allOff:
        'Nessun periodo è impostato: per ora si cancellano solo i recapiti di chi lo chiede, 24 ore dopo la richiesta.',
      periodsTitle: 'Periodi',
      periods: {
        identityDocuments: 'Foto dei documenti',
        vehiclePhotos: 'Foto del veicolo',
        fineWindow: 'Attesa delle multe',
        customer: 'Clienti',
      },
      days: { one: '{count} giorno', other: '{count} giorni' },
      after: {
        identityDocuments: '{days} dalla fine del noleggio',
        vehiclePhotos: '{days} dalla fine del noleggio',
        fineWindow: '{days} dalla riconsegna, prima di cancellare l’identità',
        customer: '{days} dall’ultima attività',
      },
      off: {
        identityDocuments: 'Spento: non si cancellano da sole',
        vehiclePhotos: 'Spento: non si cancellano da sole',
        fineWindow: 'Non impostata: l’identità dei clienti resta',
        customer: 'Spento: i clienti si cancellano solo su richiesta',
      },
      dueTitle: 'Scaduti',
      due: {
        data: 'Dati',
        erasable: 'Da cancellare',
        held: 'Trattenuti',
        why: 'Perché',
        identityDocuments: 'Foto dei documenti',
        vehiclePhotos: 'Foto del veicolo',
        contacts: 'Recapiti dei clienti',
        identities: 'Identità dei clienti',
        importRows: 'Righe importate senza cliente',
        off: 'spento',
        none: '—',
        hold: '{hold}: {count}',
      },
      lastRunTitle: 'Ultimo passaggio',
      lastRun: {
        never: 'Il servizio in background non ha ancora fatto un passaggio di conservazione.',
        when: 'Quando',
        files: 'File',
        filesValue: '{erased} cancellati, {held} trattenuti, {failed} non riusciti',
        customers: 'Clienti',
        customersValue:
          '{contacts} recapiti e {identities} identità cancellati, {held} trattenuti, {failed} non riusciti',
        bytes: 'Tolti dal disco',
        bytesValue: '{removed} file, {failed} non riusciti',
        importRows: 'Righe importate svuotate',
        replies: 'Risposte salvate svuotate',
        stripe: 'Clienti Stripe messi in coda',
      },
      unfinishedTitle: 'Non ancora finito',
      unfinished: {
        bytes: 'File cancellati ancora sul disco',
        copies: 'Copie di backup non ancora sovrascritte',
        since: '{count}, il più vecchio dal {when}',
        none: 'nessuno',
        backupsOff: 'non seguite: i backup sono spenti',
      },
      requestsTitle: 'Richieste',
      requests: {
        open: 'Richieste di cancellazione aperte',
        openValue: '{open}, di cui {inGrace} ancora nelle 24 ore',
        oldest: 'La più vecchia registrata',
        legalHolds: 'Clienti con un blocco legale',
        stripe: 'Clienti Stripe da cancellare',
        stripeValue: '{queued} in coda, {unqueued} da mettere in coda, {dead} non riusciti',
        stripeDead:
          'Su Stripe {count} cancellazioni non sono riuscite: il RUNBOOK dice come riprovarle.',
      },
      orphans: {
        never: 'Il controllo giornaliero dei file senza riga non è ancora stato fatto.',
        line: 'Ultimo controllo dei file senza riga il {when}: {unknown} sconosciuti, {failed} non riusciti. Non si tolgono da soli: il RUNBOOK dice come.',
      },
    },
    notifications: {
      title: 'Promemoria e avvisi',
      pickup: 'Promemoria del ritiro al cliente',
      return: 'Promemoria della riconsegna al cliente',
      late: 'Avviso di veicolo non riconsegnato',
      alerts: 'Avvisi su cauzioni e CaRGOS',
      hoursBefore: '{hours} ore prima',
      minutesAfter: '{minutes} minuti dopo l’ora prevista',
      on: 'Attivi',
      off: {
        not_configured: 'Spento: nessun valore ({key}).',
        no_email: 'Manca il server di posta (SMTP).',
      },
      unknown: 'Il servizio in background non l’ha ancora letto.',
      recipients: 'Gli avvisi al personale vanno a {it} in italiano e a {en} in inglese.',
      noRecipients:
        'Nessuno riceverebbe gli avvisi al personale: serve il permesso operations.alerts (RUNBOOK).',
    },
    outbox: {
      title: 'Messaggi in uscita',
      pending: 'In coda',
      oldest: 'Il più vecchio in attesa dal',
      retrying: 'In nuovo tentativo',
      dead: 'Non inviati',
      deadTitle: 'Ultimi messaggi non inviati',
      noneDead: 'Nessun messaggio non inviato.',
      topic: 'Tipo',
      attempts: 'Tentativi',
      when: 'Quando',
      error: 'Errore',
      /** S7: the dead letters that are e-mails, in words, each with its way back into the queue. */
      emails: {
        title: 'E-mail non inviate',
        intro:
          'Ognuna si può inviare di nuovo: prima di spedirla il server rilegge i dati, e se non valgono più non parte nulla.',
        line: 'Messa in coda il {queued}; non inviata dopo {attempts} tentativi.',
        others:
          'Gli altri messaggi non inviati non sono e-mail e non si inviano da qui: il RUNBOOK dice come recuperarli.',
      },
      kinds: {
        'booking.confirmed': 'Conferma della prenotazione, al cliente',
        'rental.returned': 'Riepilogo del noleggio, al cliente',
        'payment_link.requested': 'Link di pagamento, al cliente',
        'contract.email': 'Contratto firmato, al cliente',
        'reminder.pickup': 'Promemoria del ritiro, al cliente',
        'reminder.return': 'Promemoria della riconsegna, al cliente',
        'booking.cancelled': 'Annullamento della prenotazione, al cliente',
        'refund.succeeded': 'Rimborso, al cliente',
        'operations.alert': 'Avviso operativo, al personale',
        'money.morning': 'E-mail del mattino, al titolare',
        'dispute.notify': 'Contestazione della banca, al titolare',
        'contact.message': 'Messaggio dal modulo del sito, all’ufficio',
      },
      resend: {
        action: 'Invia di nuovo',
        label: 'Invia di nuovo: {what}',
        title: 'Inviare di nuovo questa e-mail?',
        queued: 'Messa in coda il {when}: chi la riceve la legge in ritardo.',
        recheck:
          'Prima di spedirla il server rilegge i dati: se nel frattempo non valgono più (il ritiro è passato, il link è scaduto, il rimborso non è andato a buon fine, il cliente è stato cancellato, un messaggio dal sito ha più di 30 giorni ed è stato cancellato), non parte nulla.',
        confirm: 'Invia di nuovo',
        keep: 'Non inviare',
        done: 'Rimessa in coda. Il servizio in background rilegge i dati e, se valgono ancora, la spedisce. Se l’invio fallisce ancora, ritenta per alcune ore e poi torna in questa lista.',
      },
    },
    alerts: {
      title: 'Avvisi',
      none: 'Nessun avviso in corso.',
      since: 'dal {when}',
      recipients: 'Le e-mail di avviso vanno a {it} in italiano e a {en} in inglese.',
      noRecipients: 'Nessuno riceverebbe le e-mail di avviso.',
    },
    reporting: {
      title: 'Segnalazione degli errori',
      on: 'Attiva: {sent} inviate, {dropped} scartate da quando il server è partito.',
      pending: 'Nessun servizio scelto: gli errori restano nei registri del server.',
      sessionsRefused:
        'Il servizio scelto non accetta le sessioni: il tasso senza crash non si misura.',
    },
    disk: {
      title: 'Spazio su disco',
      free: '{percent}% libero',
      unknown: 'Non disponibile',
    },
    releases: 'Versioni: API {api}, servizio in background {worker}',
    banner: 'Qualcosa nel sistema va controllato.',
    bannerLink: 'Apri Sistema',
  },

  /** S2: what each alert means, for its e-mail and the Sistema screen. */
  alerts: {
    'backups.base_stale': {
      title: 'Nessun backup completo da più di 26 ore',
      todo: 'Avvisate iRevenue: senza backup recenti, un guasto del server farebbe perdere dati.',
    },
    'backups.verify_failed': {
      title: 'La verifica dei backup è fallita',
      todo: 'Avvisate iRevenue: alcuni punti di ripristino potrebbero non essere utilizzabili.',
    },
    'backups.files_failed': {
      title: 'La copia di sicurezza di foto e documenti non riesce',
      todo: 'Avvisate iRevenue: le foto e i documenti nuovi non hanno ancora una copia.',
    },
    'backups.archiving_failing': {
      title: 'L’archiviazione continua del database non riesce',
      todo: 'Avvisate subito iRevenue: il disco del server si riempie finché non si risolve.',
    },
    'backups.silent': {
      title: 'Il servizio dei backup non dà segni di vita',
      todo: 'Avvisate iRevenue.',
    },
    'outbox.overdue': {
      title: 'Le e-mail ai clienti sono in ritardo',
      todo: 'Il servizio che le invia è fermo: avvisate iRevenue. Partiranno quando riparte.',
    },
    'outbox.dead': {
      title: 'Alcuni messaggi non sono stati inviati',
      todo: 'L’elenco è nella schermata Sistema: se serve, contattate i clienti interessati.',
    },
    'disk.low': {
      title: 'Lo spazio su disco del server sta finendo',
      todo: 'Avvisate iRevenue.',
    },
    'privacy.erasures_stuck': {
      title: 'File cancellati per la privacy ancora conservati',
      todo: 'Foto o documenti cancellati per la privacy sono ancora sul server o nei backup: avvisate iRevenue.',
    },
  } satisfies Record<AlertKey, { title: string; todo: string }>,

  /**
   * S5 (PAY-6, Fady 15 Oct): a drawer per pick-up point, opened with a count,
   * closed with a blind one; cash moves only through an open drawer.
   */
  cash: {
    title: 'Cassa',
    intro:
      'Una cassa per punto di ritiro. Si apre contando i contanti che ci sono, e si chiude con una conta alla cieca: quanto dovrebbe esserci compare solo dopo la conta.',
    state: { closed: 'Chiusa', open: 'Aperta', counting: 'In chiusura' },
    openedAt: 'Aperta il',
    openedLine: '{date}, {name}',
    openingCount: 'Contanti all’apertura',
    depositsHeldLabel: 'Depositi in contanti custoditi qui',
    // S9 (Q-62): cash recorded here from a trip while still in the pocket of whoever drove.
    outsideTill: {
      title: 'Contanti presi fuori cassa',
      intro:
        'Presi o dati durante i viaggi e registrati in questa cassa mentre erano ancora in tasca a chi viaggiava: vanno messi in cassa prima di contare.',
      taken: 'presi {amount}',
      given: 'dati ai clienti {amount}',
      handIn: 'da mettere in cassa {amount}',
      giveBack: 'da ridare dalla cassa {amount}',
      even: 'niente da mettere in cassa',
    },
    openCount: 'Contanti in cassa a {point}',
    openCountHint: 'Contali, poi scrivi il totale.',
    depositsHeld: 'Qui sono custoditi depositi in contanti per {amount}.',
    openBelowDeposits:
      'La conta è inferiore ai depositi in contanti custoditi qui ({amount}): ricontrolla prima di aprire.',
    openSubmit: 'Apri la cassa',
    closeTitle: 'Chiusura',
    closeCount: 'Contanti contati',
    blindHint: 'Quanto dovrebbe esserci compare solo dopo la conta.',
    countSubmit: 'Registra la conta',
    counted: 'Prima conta: {first}. Dovrebbero esserci: {expected}. Differenza: {difference}.',
    frozen: 'Da ora la cassa non prende e non restituisce contanti, finché non è chiusa.',
    finalCount: 'Conta finale',
    differenceNow: 'Differenza: {difference}',
    reason: 'Motivo della differenza',
    reasonHint: 'Lo legge il titolare.',
    closeSubmit: 'Chiudi la cassa',
    closed: 'Cassa di {point} chiusa.',
    opened: 'Cassa di {point} aperta.',
    openedElsewhere:
      'La cassa di {point} era già stata aperta su un altro dispositivo: vale la sua conta.',
    openTitle: 'Apertura',
    /** UI-2g: how Cassa's points arrive, as Veicoli says of its vehicles. */
    noPointFills: 'I punti di ritiro arrivano con la configurazione dell’azienda.',
    /** UI-2e: what a drawer's button waits for, beside it. */
    why: {
      count: 'Scrivi i contanti contati, per esempio 50 o 50,50.',
      reason: 'Scrivi il motivo della differenza, almeno 3 caratteri.',
    },
    history: {
      title: 'Ultime chiusure',
      none: 'Nessuna cassa chiusa finora.',
      point: 'Punto',
      closedAt: 'Chiusa',
      opening: 'All’apertura',
      firstCount: 'Prima conta',
      finalCount: 'Conta finale',
      expected: 'Attesi',
      difference: 'Differenza',
      reason: 'Motivo',
    },
    field: {
      legend: 'Cassa',
      point: 'Punto di ritiro della cassa',
      choose: 'Scegli la cassa: questo dispositivo la ricorderà.',
      isOpen: 'La cassa di {point} è aperta.',
      isClosed: 'La cassa di {point} è chiusa: aprila contando i contanti che ci sono.',
      isCounting:
        'La cassa di {point} è in chiusura: finché non è chiusa non prende né restituisce contanti.',
      toCash: 'Vai alla Cassa',
      noAccess: 'La cassa la apre chi registra i pagamenti: chiedi a chi può.',
      noPoint: 'Nessun punto di ritiro è in servizio.',
      // S9: cash taken on a trip, into a drawer the office keeps open.
      tripInto: 'I contanti vanno nella cassa di {point}: lasciali lì al rientro.',
      tripChoose: 'Scegli la cassa in cui vanno i contanti.',
      tripNone: 'Nessuna cassa è aperta: i contanti si registrano quando l’ufficio ne apre una.',
    },
  },

  /** S5: the Owner's morning e-mail (Fady, 15 Oct): codes and plates, no names. */
  morningEmail: {
    subject: '[Mazzella Rent] Il punto della mattina, {date}',
    intro: 'Da guardare oggi:',
    holds: 'Depositi con carta in scadenza o scaduti:',
    bookingVehicle: '{code}, veicolo {plate}',
    holdLapsing: '{booking}: il deposito scade il {when}.',
    holdLapsed: '{booking}: il deposito è scaduto il {when}.',
    holdLapsedOut: '{booking}: il deposito è scaduto il {when}, con il veicolo ancora fuori.',
    disputes: 'Contestazioni a cui rispondere nella Dashboard di Stripe:',
    outside: 'pagamento non registrato dall’app',
    disputeAnswer: '{booking}: {amount}, rispondi entro il {when} ({id}).',
    disputeUnknown: '{booking}: {amount}, stato di Stripe {status} ({id}).',
    drawers: 'Casse, da guardare nella schermata Cassa:',
    drawerOpen: '{point}: aperta il {when} e non ancora chiusa.',
    drawerCounting: '{point}: aperta il {when}, contata e non ancora chiusa.',
    drawerClose:
      '{point}, chiusa il {when}: prima conta {first}, conta finale {counted}, dovevano esserci {expected} (differenza {difference}).',
    stripe: 'Il controllo notturno con Stripe:',
    stripeMissing: 'Il controllo di {date} non è stato fatto: vedi la schermata Sistema.',
    stripeRepaired: '{booking}: il controllo ha registrato {what} ({id}).',
    stripeRepairedWhat: {
      payment: 'un pagamento',
      refund: 'un rimborso',
      dispute: 'una contestazione',
    },
    stripeDifference: '{booking}: {what}, {amount} ({id}), dal {date}.',
    stripeMore: '…e altre {count}, nella schermata Riconciliazione.',
    noBooking: 'nessuna prenotazione',
    cargos: 'CaRGOS, da guardare nella consegna della prenotazione:',
    cargosProblem: {
      refused: '{booking}: CaRGOS ha rifiutato la comunicazione ({when}).',
      unknown:
        '{booking}: esito sconosciuto della comunicazione a CaRGOS ({when}): controlla sul portale.',
      queued: '{booking}: la comunicazione a CaRGOS è ferma dal {when}.',
    },
    cargosEnded: {
      cancelled:
        '{booking}: prenotazione annullata ieri, e CaRGOS {status}: guarda sul portale se va ritirata.',
      no_show:
        '{booking}: cliente non presentato ieri, e CaRGOS {status}: guarda sul portale se va ritirata.',
    },
    cargosEndedStatus: {
      sent: 'ha la comunicazione inviata dall’app',
      recorded: 'ha la comunicazione registrata dal portale o dalla PEC',
      unknown: 'potrebbe avere la comunicazione (esito sconosciuto)',
    },
    cargosReceipts: 'Ricevute CaRGOS di ieri, da spuntare sul PDF giornaliero del portale:',
    cargosReceipt: '{booking}: ricevuta {receipt} ({channel}, {when}).',
    cargosChannel: { api: 'dall’app', portal: 'dal portale', pec: 'dalla PEC' },
    cargosMoreReceipts: '…e altre {count}, ciascuna nella sua prenotazione.',
    fiscal: 'Registratori e documenti fiscali, da guardare in Cassa e nella schermata Fiscale:',
    fiscalZ:
      '{point}, Z n. {number} del {when}: totale {total}, differenza con i documenti registrati {difference}.',
    fiscalZMoved:
      '{point}, Z n. {number} del {when}: totale {total}, differenza {difference} quando è stata scritta, {now} con i documenti registrati dopo.',
    fiscalUnclosed:
      '{point}: {count} documenti del registratore ({amount}) dopo la Z n. {number} del {when}: manca una chiusura?',
    fiscalUnclosedNoZ:
      '{point}: {count} documenti del registratore ({amount}) e nessuna Z registrata.',
    fiscalNumbering:
      '{point}: la Z n. {next} del {nextWhen} viene dopo la n. {previous} del {previousWhen}: numero saltato o fuori ordine.',
    fiscalMissing:
      'Senza documento fiscale: {count} movimenti, {amount} (di ieri: {yesterdayCount}, {yesterdayAmount}).',
    fines: 'Multe con i dati del conducente ancora da inviare, nella schermata Multe:',
    fineDueSoon: '{authority}, verbale {number} (targa {plate}, {code}): scade il {date}.',
    fineOverdue: '{authority}, verbale {number} (targa {plate}, {code}): scaduta il {date}.',
    fineNoBooking: 'nessun noleggio abbinato',
    where: 'I depositi sono anche nella schermata Oggi dell’app staff.',
    automatic: 'Messaggio automatico: non rispondete a questa e-mail.',
  },
  /** S5c: the money reports (RPT-1, RPT-3, RPT-4), for the holders of reports.view. */
  reports: {
    title: 'Report',
    intro:
      'I soldi per giorno o per mese e per categoria, e quello che è ancora aperto. Codici di prenotazione e targhe, mai nomi di clienti.',
    tabs: { revenue: 'Ricavi', open: 'Aperti' },
    from: 'Dal',
    to: 'Al',
    by: 'Per',
    byDay: 'giorno',
    byMonth: 'mese',
    show: 'Mostra',
    csv: 'Scarica il CSV',
    csvPending: 'CSV nell’app per iPad: si scarica dall’app web.',
    generatedAt: 'Aggiornato: {when}',
    /** UI-2g: said once a period's answer is in. */
    shown: 'Ricavi dal {from} al {to}, per {by}.',
    empty: 'Niente in questo periodo.',
    /** A category gone from the list since: said as such, never as its id. */
    categoryGone: 'Categoria non più in elenco',
    total: 'Totale',
    period: 'Periodo',
    category: 'Categoria',
    received: 'Incassato',
    rented: 'Noleggiato',
    channels: {
      cash: 'Contanti',
      pos: 'POS',
      transfer: 'Bonifico',
      stripe: 'Stripe',
    },
    handovers: 'Noleggi consegnati',
    handedOver: 'Valore consegnati',
    fees: 'Penali trattenute',
    waived: 'Penali condonate',
    fineFees: 'Diritti di gestione multe',
    note: 'Lordo: il regime IVA non è ancora deciso. I rimborsi contano il giorno in cui sono andati a buon fine, le contestazioni perse il giorno in cui sono state aperte, i diritti di gestione multe il giorno dell’addebito.',
    open: {
      deposits: 'Depositi trattenuti o in attesa',
      receivable: 'Da incassare',
      payable: 'Da restituire',
      disputes: 'Contestazioni aperte',
      fiscal: 'Senza documento fiscale',
      fines: 'Multe da gestire',
      finesCount: 'Scadute: {overdue}. In scadenza entro 7 giorni: {dueSoon}.',
      shown: '{shown} di {count}: le altre nel CSV.',
      none: 'Niente.',
      section: 'Sezione',
      booking: 'Prenotazione',
      plate: 'Targa',
      state: 'Stato',
      amount: 'Importo',
      due: 'Scadenza',
      reference: 'Riferimento',
      moved: 'Data del movimento',
      method: 'Metodo',
      depositStates: { pending: 'In attesa della carta', held: 'Trattenuto' },
      problems: {
        failed: 'Rimborso non riuscito',
        requires_action: 'Rimborso in attesa di un’azione',
        disputed: 'Trattenuto per una contestazione',
      },
    },
    files: { revenue: 'ricavi', open: 'aperti' },
  },
  /** S5c: the night's check against Stripe, and what it leaves for a person to read. */
  reconcile: {
    title: 'Riconciliazione',
    intro:
      'Ogni notte l’app confronta i movimenti di Stripe del giorno prima con i suoi: registra quello che Stripe non è riuscito a comunicarle ed elenca qui quello che va guardato.',
    pending: 'Stripe: senza le sue chiavi non c’è niente da controllare.',
    date: 'Giorno',
    dayBefore: 'Giorno prima',
    dayAfter: 'Giorno dopo',
    yesterday: 'Ieri',
    due: 'Il controllo di {date} è previsto per {when}.',
    notRun: 'Il controllo di {date} non è stato fatto: vedi la schermata Sistema.',
    run: 'Controllo di {date}, finito {when} ({trigger}).',
    triggers: { schedule: 'notturno', cli: 'avviato a mano' },
    runs: 'Controlli di questo giorno: {count}.',
    testMode: 'Modalità di test di Stripe.',
    totals: {
      transactions: 'Movimenti',
      charges: 'Pagamenti',
      refunds: 'Rimborsi',
      disputes: 'Contestazioni',
      fees: 'Commissioni di Stripe',
      payouts: 'Bonifici alla banca',
      other: 'Altro',
      net: 'Netto',
      outside: 'Fuori dall’app',
    },
    repairs: 'Registrato dal controllo',
    repairKinds: {
      payment: 'Pagamento',
      refund: 'Rimborso',
      dispute: 'Contestazione',
    },
    outcomes: {
      payment_recorded: 'registrato',
      refund_recorded: 'registrato',
      refund_updated: 'aggiornato',
      dispute_recorded: 'registrata',
      dispute_updated: 'aggiornata',
    } as Readonly<Record<string, string>>,
    categories: {
      charge: 'Pagamento',
      partial_capture_reversal: 'Parte di un deposito lasciata andare',
      refund: 'Rimborso',
      refund_failure: 'Rimborso non riuscito',
      dispute: 'Contestazione',
      dispute_reversal: 'Contestazione annullata',
    } as Readonly<Record<string, string>>,
    noRepairs: 'Niente da registrare.',
    /** UI-2e: what the app's words do not cover, said as such; its code is the Owner's. */
    outcomeUnknown: 'con un esito che l’app non conosce',
    categoryUnknown: 'Un tipo di movimento che l’app non conosce',
    outside: 'Soldi presi o restituiti fuori dall’app',
    outsideHint:
      'Movimenti di Stripe senza un riferimento dell’app (il vecchio sito, la Dashboard): elencati a parte, non sono differenze.',
    noOutside: 'Nessuno.',
    open: 'Differenze da guardare',
    openHint: 'Non lette e ancora presenti, dalla più vecchia.',
    noOpen: 'Nessuna differenza da guardare.',
    more: 'Altre {count}: le più vecchie sono in alto.',
    ofDate: 'Trovate in questo giorno',
    noneOfDate: 'Nessuna.',
    since: 'dal {date}',
    resolved: 'Non più trovata dal {when}.',
    acknowledged: 'Letta da {name} il {when}: {reason}',
    reason: 'Che cosa hai controllato o fatto',
    reasonHint: 'Almeno 3 caratteri: resta nel registro, con il tuo nome.',
    acknowledge: 'Segna come letta',
    why: 'Scrivi che cosa hai controllato o fatto, almeno 3 caratteri.',
    acknowledgedDone: 'Differenza segnata come letta.',
    /** UI-2e: a difference's details in words; the raw ones are under Dettagli tecnici. */
    detail: {
      category: 'Movimento di Stripe: {category}',
      appAmount: 'Registrato nell’app: {amount}',
      kept: 'Trattenuto da Stripe: {stripe}; nell’app: {app}',
      depositState: 'Deposito nell’app: {state}',
      refundStatus: 'Rimborso nell’app: {app}; da Stripe: {stripe}',
      stripeRefund: 'Rimborso da Stripe: {status}',
    },
    depositStates: {
      pending: 'in attesa della carta',
      held: 'trattenuto',
      released: 'rilasciato',
      captured: 'incassato',
      expired: 'scaduto',
      failed: 'non riuscito',
    } satisfies Record<DepositState, string>,
    minorUnits: '{amount} {currency}, nell’unità più piccola della valuta',
    /** UI-2g: how the movement is found in Stripe's Dashboard. */
    stripeReference: 'Riferimento Stripe {id}',
    kinds: {
      payment_unrecorded: 'un pagamento preso da Stripe che l’app non ha potuto registrare',
      amount_mismatch: 'un pagamento registrato per un altro importo',
      deposit_mismatch: 'un deposito trattenuto da Stripe per un altro importo',
      refund_unrecorded: 'denaro restituito da Stripe che l’app non ha registrato',
      refund_status: 'un rimborso in uno stato diverso da quello di Stripe',
      dispute_unrecorded: 'una contestazione che l’app non ha potuto registrare',
      foreign_reference: 'un movimento che nomina qualcosa che quest’app non ha fatto',
      unexpected: 'un movimento di un tipo che l’app non conosce',
      currency: 'denaro di un cliente in un’altra valuta',
    } satisfies Record<StripeDifferenceKind, string>,
  },
  /** S5: the Owner's e-mail about a dispute (Fady, 15 Oct): codes and plates, no names. */
  disputeEmail: {
    subject: {
      answer: '[Mazzella Rent] Contestazione {code}: rispondi entro il {date}',
      lost: '[Mazzella Rent] Contestazione persa: {code}',
      won: '[Mazzella Rent] Contestazione vinta: {code}',
      other: '[Mazzella Rent] Contestazione {code}: {status}',
    },
    intro: 'La banca di un cliente contesta un pagamento con carta preso con Stripe.',
    booking: 'Prenotazione: {code}',
    bookingVehicle: 'Prenotazione: {code}, veicolo {plate}',
    outside:
      'Pagamento non registrato dall’app: preso fuori dall’app, o non ancora registrato (addebito Stripe {charge}).',
    amount: 'Importo contestato: {amount}',
    reason: 'Motivo indicato dalla banca: {reason}',
    status: 'Stato: {status}',
    answerBy: 'Rispondi nella Dashboard di Stripe entro il {date}.',
    open: 'Finché la banca decide, di questo pagamento non si rimborsa nulla, in nessun modo, e la prenotazione non si chiude.',
    lost: 'La banca ha restituito {amount} al cliente: la prenotazione lo deve di nuovo, e si incassa solo al banco.',
    won: 'La banca ha deciso a favore dell’azienda: il denaro resta all’azienda.',
    ended: 'La contestazione è chiusa: il denaro resta all’azienda.',
    where: 'Su Stripe: https://dashboard.stripe.com/disputes, la contestazione {id}.',
    automatic: 'Messaggio automatico: non rispondete a questa e-mail.',
  },
  /** S2: the alert e-mails, to the holders of the Sistema permission. */
  alertEmail: {
    subject: {
      fired: '[Mazzella Rent] Da controllare: {title}',
      resolved: '[Mazzella Rent] Risolto: {title}',
      reminder: '[Mazzella Rent] Ancora da risolvere: {title}',
      notice: '[Mazzella Rent] {title}',
    },
    intro: {
      fired: 'Il sistema ha rilevato un problema.',
      resolved: 'Il problema segnalato è risolto.',
      reminder: 'Il problema segnalato non è ancora risolto.',
      notice: 'Il sistema ha rilevato un problema.',
    },
    since: 'Dal: {when}',
    todo: 'Che cosa fare: {todo}',
    screen: 'I dettagli sono nella schermata Sistema dell’app staff.',
    runbook: 'Manuale operativo: docs/RUNBOOK.md, Avvisi, {key}.',
    details: 'Dettagli tecnici',
    automatic: 'Messaggio automatico: non rispondete a questa e-mail.',
  },
  /**
   * Q-165: a message from the website's contact form, to the office mailbox.
   * Reply-To is the sender, so the office answers with Rispondi.
   */
  contactEmail: {
    subject: '[Mazzella Rent] Messaggio dal sito: {name}',
    intro: 'Un messaggio dal modulo di contatto del sito.',
    name: 'Nome: {name}',
    email: 'E-mail: {email}',
    language: 'Lingua del sito: {language}',
    message: 'Messaggio:',
    reply: 'Per rispondere usate Rispondi: la risposta va a chi ha scritto.',
    languages: { it: 'italiano', en: 'inglese', de: 'tedesco' },
  },
  /**
   * S7 (NTF-3): the operational alerts, to the holders of operations.alerts.
   * Codes and plates only: who the customer is, the app shows.
   */
  operationsEmail: {
    subject: {
      late_return: '[Mazzella Rent] Veicolo non riconsegnato: {code}',
      hold_lapsing: '[Mazzella Rent] Cauzione in scadenza: {code}',
      hold_lapsed: '[Mazzella Rent] Cauzione scaduta, veicolo fuori: {code}',
      cargos: '[Mazzella Rent] CaRGOS da controllare: {code}',
      overbooked: '[Mazzella Rent] Prenotazione web pagata in ritardo, categoria piena: {code}',
    },
    intro: {
      late_return: 'Un veicolo non è rientrato all’ora prevista.',
      hold_lapsing:
        'Il blocco sulla carta di una cauzione sta per scadere: dopo, la cauzione non si può più incassare.',
      hold_lapsed:
        'Il blocco sulla carta di una cauzione è scaduto mentre il veicolo è ancora fuori.',
      cargos_refused: 'La comunicazione CaRGOS di una consegna è stata rifiutata.',
      cargos_unknown: 'La comunicazione CaRGOS di una consegna non ha un esito chiaro.',
      cargos_queued:
        'La comunicazione CaRGOS di una consegna è in attesa da più di {minutes} minuti.',
      overbooked:
        'Una prenotazione dal sito è stata pagata dopo la scadenza del blocco: è confermata, ma nella sua categoria non c’è più un veicolo per tutte le prenotazioni di quei giorni.',
    },
    booking: 'Prenotazione: {code}',
    vehicle: 'Veicolo: {plate}',
    at: {
      late_return: 'Rientro previsto: {when}',
      hold_lapsing: 'Il blocco scade: {when}',
      hold_lapsed: 'Scaduto: {when}',
      cargos: 'Dal: {when}',
      overbooked: 'Pagata e confermata: {when}',
    },
    todo: {
      late_return: 'Che cosa fare: contatta il cliente; i recapiti sono nella prenotazione.',
      hold_lapsing:
        'Che cosa fare: prima della scadenza decidi se incassare o liberare la cauzione.',
      hold_lapsed:
        'Che cosa fare: la cauzione non garantisce più il noleggio; decidi come procedere al rientro.',
      cargos:
        'Che cosa fare: apri la prenotazione e risolvi la comunicazione prima della consegna.',
      overbooked:
        'Che cosa fare: trova un veicolo (anche di un’altra categoria) o contatta il cliente; il denaro resta incassato.',
    },
    open: 'Apri la prenotazione {code} nell’app staff.',
    automatic: 'Messaggio automatico: non rispondete a questa e-mail.',
  },
  /** DOC-SCAN: scanning a licence or ID to prefill the hand-over form. */
  scan: {
    button: 'Scansiona documento',
    title: 'Scansiona il documento',
    intro:
      'Inquadra il documento nel riquadro, su uno sfondo scuro e senza riflessi, oppure scegli una foto. Tutto resta su questo dispositivo: nulla viene inviato.',
    front: 'Fronte',
    back: 'Retro (patenti USA)',
    captured: 'Acquisito: {side}',
    capture: 'Scatta',
    choosePhoto: 'Scegli una foto',
    readAction: 'Leggi il documento',
    retake: 'Scatta di nuovo',
    cancel: 'Annulla',
    useManual: 'Inserisci a mano',
    reading: 'Lettura del documento in corso…',
    cameraOff:
      'La fotocamera non è disponibile: scegli una foto del documento, oppure inserisci i dati a mano.',
    failed: 'Non è stato possibile leggere il documento. Riprova, o inserisci i dati a mano.',
    timeout:
      'La lettura ha impiegato troppo. Riprova con una foto più nitida e più vicina, o inserisci i dati a mano.',
    invalidFile: 'Il file scelto non è un’immagine. Scegli una foto del documento.',
    reviewTitle: 'Controlla e conferma',
    reviewIntro:
      'I campi letti sono qui sotto. Quelli incerti sono evidenziati: spunta ciascuno dopo averlo controllato. Un campo già scritto nel modulo resta com’è, salvo che tu scelga di sostituirlo. Nulla è salvato finché non premi Conferma.',
    toCheck: 'Da controllare',
    readFrom: 'Letto dal documento',
    current: 'Ora nel modulo',
    replace: 'Sostituisci con il valore letto',
    verified: 'Verificato',
    mustVerify: 'Spunta ogni campo da controllare',
    confirm: 'Conferma i dati',
    nothingRead: 'Nessun dato leggibile. Inserisci i dati a mano.',
    partial: 'Letti solo alcuni campi: gli altri restano da scrivere a mano.',
    categoryWarning:
      'La patente letta potrebbe non coprire il veicolo prenotato: controlla la categoria.',
    /** Flags from validation; never block, only warn. */
    issue: {
      'licence-expired': 'La patente risulta scaduta prima della riconsegna.',
      'licence-dates-order':
        'Le date della patente non sono coerenti (scadenza prima del rilascio).',
      'mrz-check-failed': 'La zona a lettura ottica non torna: controlla i dati letti.',
      'licence-number-format': 'Il numero della patente non ha il formato atteso: controllalo.',
      'name-mismatch': 'Il nome sul documento e sulla patente non coincidono: controlla.',
    },
  },
  /**
   * S10: the printed fallback kit (Kit di emergenza) and the screen that
   * downloads it. The PDF is printed in Italian for every customer (Q-26). Its
   * contents are the developer's proposal, for the owner and the lawyer to
   * check (DECISIONS, S10); the PEC page waits for Q-112.
   */
  /**
   * S11: a rental kept on paper while the server was down, typed back in by
   * the Owner at the times written on the printed set (DECISIONS S11).
   */
  paper: {
    title: 'Da carta {code}',
    open: 'Inserisci da carta',
    intro:
      'Per un noleggio tenuto su carta mentre il sistema non rispondeva. Scrivi ciò che riporta il set stampato, con il giorno e l’ora scritti sul foglio: valgono quelli, non l’ora in cui li inserisci.',
    window: 'Si inseriscono fatti di al massimo {days} giorni fa.',
    revenueDay: {
      actual: 'Gli incassi contano nel giorno in cui sono stati presi, come lo scontrino.',
      entry: 'Gli incassi contano nel giorno in cui li inserisci.',
    } satisfies Record<ReentryRevenueDay, string>,
    ownerOnly: 'Solo il titolare inserisce un noleggio da carta.',
    notConfigured:
      'L’inserimento da carta non è attivo: mancano le impostazioni reentry.max_days e reentry.revenue_day.',
    nothing: 'Per una prenotazione in questo stato non c’è nulla da inserire da carta.',
    parts: 'Cosa riporta il set',
    partHandover: 'La consegna',
    partReturn: 'Il rientro',
    handover: 'Consegna',
    sheet: 'Numero del set',
    sheetHint: 'Come è stampato in alto sul foglio, con la lettera finale.',
    vehicle: 'Veicolo partito',
    date: 'Giorno',
    time: 'Ora',
    km: 'Chilometri',
    fuel: 'Carburante',
    documents: 'Documento d’identità e patente visti al banco e copiati sul foglio',
    noIdentity:
      'Prima registra chi ha preso il veicolo, dalla schermata della consegna: poi torna qui.',
    toIdentity: 'Registra chi ha preso il veicolo',
    noSecondIdentity:
      'La prenotazione ha un secondo conducente: prima registra i suoi dati, dalla schermata della consegna, poi torna qui.',
    toSecondIdentity: 'Registra il secondo conducente',
    receipt: 'Ricevuta CaRGOS',
    receiptIntro:
      'Il veicolo è partito con la comunicazione fatta sul portale o via PEC: scrivi la ricevuta e quando è arrivata.',
    receiptChannel: 'Comunicata tramite',
    receiptChannels: { portal: 'Portale CaRGOS', pec: 'PEC' },
    receiptNumber: 'Numero di ricevuta o di protocollo',
    receiptOnFile: 'La ricevuta CaRGOS per questo veicolo è già registrata.',
    blocked: 'Il veicolo è partito anche se {blocks}.',
    overrideReason: 'Perché il titolare l’ha lasciato partire',
    return: 'Rientro',
    kmHint: 'Alla consegna: {km} km.',
    notes: 'Note del foglio',
    payments: 'Incassi',
    noPayments: 'Nessun incasso su questo set.',
    addPayment: 'Aggiungi un incasso',
    removePayment: 'Togli incasso {number}',
    paymentNumber: 'Incasso {number}',
    paymentsFull: 'Al massimo {max} incassi per set.',
    amount: 'Importo (€)',
    method: 'Modo',
    reference: 'Riferimento (scontrino del POS, bonifico)',
    drawerHint:
      'I contanti entrano nel cassetto aperto ora, con il giorno e l’ora in cui sono stati presi.',
    owner: 'Il titolare',
    reason: 'Perché si è lavorato su carta',
    reasonHint: 'Resta con ogni parte del set nel registro delle modifiche.',
    code: 'Codice del titolare',
    submit: 'Inserisci il set',
    railTitle: 'Per inserire il set',
    railDetails: 'Il conto',
    moneyHint: 'Il conto prima del set: gli incassi inseriti lo riducono.',
    done: 'Set inserito da carta.',
    /** What the form still lacks, beside the button. */
    why: {
      empty: 'Scegli cosa riporta il set: la consegna, il rientro o un incasso.',
      sheet: 'Scrivi il numero del set come è stampato.',
      vehicle: 'Scegli il veicolo partito.',
      identity: 'Registra prima chi ha preso il veicolo.',
      secondIdentity: 'Registra prima i dati del secondo conducente.',
      documents: 'Conferma che i documenti sono stati visti al banco.',
      receiptChannel: 'Scegli come è stata fatta la comunicazione a CaRGOS.',
      receiptNumber: 'Scrivi il numero della ricevuta CaRGOS.',
      receiptTime: 'Scrivi giorno e ora della ricevuta CaRGOS.',
      handoverTime: 'Scrivi giorno e ora della consegna.',
      kmOut: 'Scrivi i chilometri alla consegna.',
      fuelOut: 'Scegli il carburante alla consegna.',
      overrideReason: 'Scrivi perché il veicolo è partito lo stesso, almeno 3 caratteri.',
      returnTime: 'Scrivi giorno e ora del rientro.',
      kmIn: 'Scrivi i chilometri al rientro.',
      kmBelow: 'Controlla i chilometri al rientro: sono meno di quelli alla consegna.',
      fuelIn: 'Scegli il carburante al rientro.',
      paymentTime: 'Scrivi giorno e ora di ogni incasso.',
      paymentAmount: 'Scrivi l’importo di ogni incasso.',
      paymentMethod: 'Scegli il modo di ogni incasso.',
      drawer: 'Apri il cassetto per i contanti.',
      reason: 'Scrivi perché si è lavorato su carta, almeno 3 caratteri.',
      code: 'Scrivi il codice di 6 cifre del titolare.',
    },
    /** On the booking page: what was typed in from paper, and when the server was told. */
    handedOver: 'Da carta, set {sheet}: inserito il {date}. {reason}',
    returned: 'Rientro da carta: inserito il {date}. {reason}',
    paidOnPaper: 'Da carta: inserito il {date}.',
  },

  fallbackKit: {
    title: 'Kit di emergenza',
    intro:
      'I moduli per continuare a noleggiare su carta quando il sistema non risponde. Si stampano prima, quando tutto funziona: tienine sempre una scorta al banco.',
    sets: 'Set da stampare',
    setsHint:
      'Un set per noleggio: contratto, consegna, riconsegna, cauzione e pagamenti, CaRGOS. Da 1 a {max}.',
    prepare: 'Prepara il kit',
    preparing: 'Il kit {kit} è in preparazione.',
    asked: 'Kit {kit} chiesto: {sets} set, da {first} a {last}.',
    ready: 'Kit {kit} pronto: {sets} set, condizioni versione {version}.',
    failed:
      'Il kit {kit} non è stato preparato. Chiedine uno nuovo; la schermata Sistema dice perché.',
    open: 'Apri il kit {kit}',
    notInApp: 'Il kit si apre dal browser del computer dell’ufficio, per stamparlo.',
    recent: 'Kit recenti',
    none: 'Nessun kit ancora: preparane uno ora, finché il sistema risponde.',
    range: 'Set da {first} a {last}',
    reprint:
      'Quando il titolare mette in vigore nuove condizioni di noleggio, stampa un kit nuovo e distruggi i set non usati del vecchio.',
    pdf: {
      title: 'Kit di emergenza: noleggio su carta',
      printed: 'Kit {kit} · stampato il {date}',
      terms: 'Condizioni di noleggio in vigore: versione {version}.',
      draft: 'BOZZA',
      draftNotice: 'Condizioni in BOZZA: questo kit è solo per prova e non ha alcun valore legale.',
      setRange: 'Set numerati da {first} a {last}.',
      whenTitle: 'Quando si usa',
      when: 'Solo quando il sistema non risponde dal banco. Finché risponde, ogni noleggio si fa nell’app come sempre.',
      stepsTitle: 'Come si procede',
      step1:
        'Un set per noleggio. Il suo numero è il numero del contratto: scrivilo sulla pratica e non usarlo per altro.',
      step2:
        'Compila il contratto con il cliente, che firma due volte: il contratto e l’approvazione specifica delle clausole.',
      step3:
        'Dove la categoria lo richiede, comunica il noleggio a CaRGOS (foglio CaRGOS del set). Senza ricevuta il veicolo non parte, come nell’app.',
      step4:
        'Alla consegna: ora, km, carburante in ottavi, danni zona per zona, firma del cliente. Lo stesso alla riconsegna.',
      step5:
        'Cauzione e pagamenti: una riga per ogni incasso, con ora, importo, modo, numero dello scontrino o della fattura e chi ha incassato. Il contante resta nel cassetto aperto.',
      step6:
        'Quando il sistema torna, il titolare inserisce ogni set con i suoi orari reali (Inserimento da carta). Fino ad allora i set compilati sono l’unico originale: conservali in ordine di numero.',
      set: 'Set n. {number}',
      footer: '{sheet} · set n. {number} · condizioni versione {version}',
      sheets: {
        contract: 'Contratto di noleggio',
        handover: 'Consegna del veicolo',
        return: 'Riconsegna del veicolo',
        payments: 'Cauzione e pagamenti',
        cargos: 'Comunicazione a CaRGOS',
      } satisfies Record<PaperSheet, string>,
      sections: {
        booking: 'Prenotazione',
        driver: 'Conducente',
        document: 'Documento d’identità',
        licence: 'Patente',
        vehicle: 'Veicolo',
        period: 'Periodo e luoghi',
        price: 'Prezzo',
        signatures: 'Firme',
        deposit: 'Cauzione',
        payments: 'Pagamenti',
      },
      fields: {
        bookingCode: 'Codice della prenotazione, se c’è',
        surname: 'Cognome',
        givenNames: 'Nome',
        birthDate: 'Data di nascita',
        birthPlace: 'Luogo di nascita',
        citizenship: 'Cittadinanza',
        residenceAddress: 'Residenza: via e numero',
        residencePostalCode: 'CAP',
        residenceCity: 'Comune o città',
        residenceCountry: 'Stato',
        phone: 'Telefono',
        email: 'E-mail',
        idDocumentKind: 'Tipo di documento',
        idDocumentNumber: 'Numero del documento',
        idDocumentIssuedBy: 'Rilasciato da (luogo)',
        licenceNumber: 'Numero della patente',
        licenceIssuedBy: 'Rilasciata da (luogo)',
        licenceIssuedOn: 'Data di rilascio',
        licenceExpiresOn: 'Data di scadenza',
        licenceCategories: 'Categorie',
        vehiclePlate: 'Targa',
        vehicleModel: 'Marca e modello',
        pickupAt: 'Ritiro: giorno e ora',
        pickupPlace: 'Luogo di ritiro',
        returnDueAt: 'Riconsegna prevista: giorno e ora',
        returnPlace: 'Luogo di riconsegna',
        cover: 'Copertura scelta',
        extras: 'Accessori',
        totalAgreed: 'Prezzo concordato (totale)',
        signedAt: 'Firmato il (giorno e ora)',
        customerSignature: 'Firma del cliente',
        specificApprovalSignature: 'Firma per l’approvazione specifica',
        staffName: 'Per il locatore (nome)',
        checkedOutAt: 'Consegnato il (giorno e ora)',
        kmOut: 'Km alla consegna',
        fuelOut: 'Carburante o batteria alla consegna',
        damagesOut: 'Danni già presenti',
        notesOut: 'Note',
        staffOut: 'Consegnato da',
        customerSignatureOut: 'Firma del cliente',
        returnedAt: 'Riconsegnato il (giorno e ora)',
        kmIn: 'Km alla riconsegna',
        fuelIn: 'Carburante o batteria alla riconsegna',
        damagesIn: 'Danni nuovi',
        notesIn: 'Note',
        staffIn: 'Ricevuto da',
        customerSignatureIn: 'Firma del cliente',
        depositAmount: 'Importo della cauzione',
        depositMethod: 'Come è stata presa',
        depositTakenAt: 'Presa il (giorno e ora)',
        depositReturnedAt: 'Restituita il (giorno e ora)',
        depositKept: 'Trattenuto (con il motivo nelle note)',
        paidAt: 'Giorno e ora',
        paymentAmount: 'Importo',
        paymentMethod: 'Modo',
        fiscalDocument: 'Scontrino o fattura n.',
        receivedBy: 'Incassato da',
        cargosChannel: 'Comunicato tramite',
        cargosSentAt: 'Comunicato il (giorno e ora)',
        cargosReceipt: 'Numero di ricevuta o di protocollo',
        cargosReceiptAt: 'Ricevuta del (giorno e ora)',
        cargosSentBy: 'Comunicato da',
      } satisfies Record<PaperFieldKey, string>,
      options: {
        idDocumentKind: {
          id_card: 'Carta d’identità',
          passport: 'Passaporto',
          driving_licence: 'Patente',
          other: 'Altro',
        } satisfies Record<DocumentKind, string>,
        depositMethod: {
          pos: 'Blocco sul POS del negozio',
          cash: 'Contanti',
          none: 'Nessuna (decide il titolare)',
        },
        paymentMethod: {
          cash: 'Contanti',
          card_terminal: 'Carta sul POS',
          bank_transfer: 'Bonifico',
        } satisfies Record<CounterPaymentMethod, string>,
        cargosChannel: {
          portal: 'Portale CaRGOS',
          pec: 'PEC',
        },
      },
      fuelScale: '0 = vuoto · 8 = pieno',
      zone: 'Zona',
      seen: 'Cosa si vede (graffi, ammaccature, rotture)',
      zonesNote:
        'Scrivi «nessuno» dove non c’è nulla: una riga vuota non dice se la zona è stata guardata. Per uno scooter, il tetto e gli interni non si compilano; per un’auto, la sella.',
      notesNote: 'Trattenuto, motivo, correzioni: tutto qui, con la sigla di chi scrive.',
      cargosIntro:
        'Dove la categoria del veicolo lo richiede, il noleggio si comunica a CaRGOS prima che il veicolo parta, con i dati del contratto di questo set. Se la categoria non lo richiede, barra questo foglio.',
      cargosPortal:
        'Dal portale cargos.poliziadistato.it, con le credenziali dell’azienda: annota il numero di ricevuta che il portale mostra.',
      cargosPec:
        'Se anche il portale non funziona, via PEC alla Questura. Il modello e l’indirizzo sono ancora da confermare (domanda aperta Q-112): finché non li hai, il veicolo non parte.',
      pecAddress: 'Indirizzo PEC della Questura',
      termsTitle: 'Condizioni di noleggio',
      approvalTitle: 'Approvazione specifica (artt. 1341 e 1342 c.c.)',
    },
  },
} as const
